import React, { useState, useMemo, useEffect, useRef } from 'react';
import { View, UserRole, Teacher, Student, Tugas, TugasQuestion, TugasSubmission, TugasSession, LearningObjective, TugasType, AssessmentGrade, ActivityType, SchoolSettings, CourseModule, CourseChapter, CourseQuiz, CourseSubmission, FormativeGradeTP } from '../types';
import { Plus, Edit2, Trash2, CheckCircle, XCircle, Play, ChevronRight, ChevronLeft, ArrowLeft, ArrowRight, ClipboardList, RefreshCw, Check, Star, X, Upload, Download, Eye, Loader2, Copy, Printer, Users, Activity, BookOpen, Image as ImageIcon, FileText, Type, Bold, Italic, List, ListOrdered, Maximize2, Minimize2, AlignCenter, Layout, Sparkles, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import imageCompression from 'browser-image-compression';
import { supabase } from '../services/supabase';
import ConfirmModal from '../components/ConfirmModal';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface Props {
  role: UserRole;
  activeUser: any;
  tugas: Tugas[];
  tugasSubmissions: TugasSubmission[];
  tugasSessions?: TugasSession[];
  onStartSession?: (session: TugasSession) => void;
  onEndSession?: (sessionId: string) => void;
  students: Student[];
  classes: string[];
  objectives: LearningObjective[];
  onSaveTugas: (tugas: Tugas) => void;
  onDeleteTugas: (id: string) => void;
  onDeleteSubmission: (id: string) => void;
  onUpdateSubmission?: (submission: TugasSubmission) => void;
  onSubmitTugas: (submission: TugasSubmission) => void;
  onRefreshSubmissions?: () => void;
  onUpdateGrades?: (grades: AssessmentGrade[]) => void;
  assessmentGrades: AssessmentGrade[];
  formativeGradesTP?: FormativeGradeTP[];
  selectedSubjectForStudent?: string;
  filterType?: TugasType | 'all';
  onStudentClick?: (student: Student) => void;
  schoolSettings: SchoolSettings;
  initialTugasId?: string | null;
  courseModules?: CourseModule[];
  courseSubmissions?: CourseSubmission[];
  onSaveCourseModule?: (module: CourseModule) => void;
  onDeleteCourseModule?: (id: string) => void;
  onSaveCourseSubmission?: (submission: CourseSubmission) => void;
  onSaveAssessmentGrade?: (grade: AssessmentGrade) => void;
}

const SessionCard = React.memo(({ session, onStudentClick }: { session: TugasSession, onStudentClick?: (studentId: string) => void }) => (
  <motion.div 
    layout
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.2 }}
    className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 group hover:border-indigo-500/30 transition-all cursor-pointer flex flex-col justify-between"
    onClick={() => onStudentClick?.(session.studentId)}
  >
    <div className="flex flex-col items-center text-center mb-3">
      <div className="w-12 h-12 mb-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-center text-indigo-600 font-black text-lg group-hover:bg-indigo-600 group-hover:text-white transition-colors">
        {session.studentName.charAt(0)}
      </div>
      <div>
        <p className="text-sm font-black text-slate-800 dark:text-white line-clamp-1 group-hover:text-indigo-600 transition-colors">{session.studentName}</p>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">Kelas {session.studentClass}</p>
      </div>
    </div>
    
    <div className="flex flex-col items-center text-center mb-4 bg-white dark:bg-slate-900/50 rounded-xl p-2.5 border border-slate-100 dark:border-slate-700/50 flex-1 justify-center">
      <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest leading-none mb-1.5">Mengerjakan:</p>
      <p className="text-xs font-bold text-slate-700 dark:text-slate-300 line-clamp-3 leading-snug">{session.tugasTitle}</p>
    </div>
    
    <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700/80 w-full mt-auto">
      <span className="text-[9px] font-bold text-slate-400">{new Date(session.startTime).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</span>
      <span className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${
        session.status === 'working' 
          ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 animate-pulse' 
          : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400'
      }`}>
        <Activity size={10} />
        {session.status === 'working' ? 'Aktif' : 'Diam'}
      </span>
    </div>
  </motion.div>
));

const ImageDropzone = ({ onImageSelect, currentImage }: { onImageSelect: (base64: string) => void, currentImage?: string }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      onImageSelect(result);
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
      onClick={() => fileInputRef.current?.click()}
      className={`relative h-44 rounded-2xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center gap-2 p-4 overflow-hidden group ${
        isDragging 
          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
          : 'border-slate-200 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-indigo-600 bg-white dark:bg-slate-900 shadow-sm'
      }`}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
        accept="image/*"
        className="hidden"
      />
      
      {currentImage ? (
        <>
          <img src={currentImage} className="absolute inset-0 w-full h-full object-cover opacity-20 transition-opacity group-hover:opacity-10" referrerPolicy="no-referrer" />
          <div className="relative z-10 flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-2xl bg-indigo-600 shadow-lg shadow-indigo-500/30 flex items-center justify-center text-white">
              <Upload size={24} />
            </div>
            <div className="text-center">
              <p className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-tight">Ganti Gambar</p>
              <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Seret gambar baru ke sini</p>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-indigo-600 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/20 transition-all">
            <Upload size={24} />
          </div>
          <div className="text-center">
            <p className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-tight">Tambah Gambar</p>
            <p className="text-[10px] text-slate-400 font-bold mt-1 tracking-wide">Tarik & Lepas atau Klik</p>
          </div>
        </>
      )}
    </div>
  );
};

const RealtimeActivityBoard = React.memo(({ tugasSessions, onStudentClick }: { tugasSessions: TugasSession[], onStudentClick?: (studentId: string) => void }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(interval);
  }, []);

  const activeSessions = useMemo(() => {
    return (tugasSessions || []).filter(session => {
      const lastActive = new Date(session.lastActive).getTime();
      const isFresh = (now - lastActive) < 90000; // 90 seconds threshold (3x heartbeat)
      return isFresh && session.status === 'working';
    });
  }, [tugasSessions, now]);

  return (
    <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-rose-100 dark:bg-rose-500/20 text-rose-600 rounded-xl">
            <Activity size={20} />
          </div>
          <div>
            <h4 className="text-lg font-black text-slate-800 dark:text-white leading-none mb-1">Aktivitas Realtime</h4>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Siswa yang sedang mengerjakan</p>
          </div>
        </div>
        <div className="px-3 py-1 bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 rounded-full flex items-center gap-2">
          <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></div>
          <span className="text-[10px] font-black text-rose-600 dark:text-rose-400 uppercase tracking-widest">{activeSessions.length} Online</span>
        </div>
      </div>

      {activeSessions.length > 0 ? (
        <motion.div 
          layout
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4"
        >
          <AnimatePresence mode="popLayout">
            {activeSessions.map((session) => (
              <SessionCard key={session.id} session={session} onStudentClick={onStudentClick} />
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <div className="py-12 flex flex-col items-center justify-center text-center bg-slate-50/50 dark:bg-slate-800/20 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
          <Users className="text-slate-300 dark:text-slate-700 mb-4" size={40} />
          <p className="text-sm font-bold text-slate-400 italic">Tidak ada siswa yang sedang mengerjakan tugas saat ini.</p>
        </div>
      )}
    </div>
  );
});

const TugasView: React.FC<Props> = ({ 
  role, 
  activeUser, 
  tugas, 
  tugasSubmissions, 
  tugasSessions = [],
  onStartSession,
  onEndSession,
  students, 
  classes, 
  objectives,
  onSaveTugas, 
  onDeleteTugas, 
  onDeleteSubmission,
  onUpdateSubmission,
  onSubmitTugas,
  onRefreshSubmissions,
  onUpdateGrades,
  selectedSubjectForStudent,
  filterType = 'all',
  onStudentClick,
  schoolSettings,
  initialTugasId,
  courseModules = [],
  courseSubmissions = [],
  assessmentGrades = [],
  formativeGradesTP = [],
  onSaveCourseModule,
  onDeleteCourseModule,
  onSaveCourseSubmission,
  onSaveAssessmentGrade
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const teacherNip = activeUser?.nip || '';
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTugas, setActiveTugas] = useState<Tugas | null>(null);
  const [takingTugas, setTakingTugas] = useState<Tugas | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [studentAnswers, setStudentAnswers] = useState<Record<string, number | string>>({});
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number | null>(null);
  const [viewingResults, setViewingResults] = useState<Tugas | null>(null);
  const [viewingAnswers, setViewingAnswers] = useState<TugasSubmission | null>(null);
  const [viewingAllStudents, setViewingAllStudents] = useState<Tugas | null>(null);
  const [expandedEssayQuestions, setExpandedEssayQuestions] = useState<Record<string, boolean>>({});
  const [viewingAllReports, setViewingAllReports] = useState(false);
  const [reportSelectedClass, setReportSelectedClass] = useState<string>('all');
  const [reportSelectedSubject, setReportSelectedSubject] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [editingTpGrade, setEditingTpGrade] = useState<{ studentId: string, value: string } | null>(null);
  const [editingPraktikumTpGrade, setEditingPraktikumTpGrade] = useState<{ studentId: string, value: string } | null>(null);
  const [essayTpChoice, setEssayTpChoice] = useState<{ submissionId: string, student: Student } | null>(null);
  const [pgTpChoice, setPgTpChoice] = useState<{ submissionId: string, student: Student } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cheatWarnings, setCheatWarnings] = useState(0);
  const [activeTab, setActiveTab] = useState<'beranda' | 'list' | 'course'>('beranda');
  const fullscreenRef = useRef<HTMLDivElement>(null);

  // Course states
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<CourseModule | null>(null);
  const [viewingCourseResults, setViewingCourseResults] = useState<CourseModule | null>(null);
  const [selectedResultClass, setSelectedResultClass] = useState<string>('all');
  const [searchStudentResults, setSearchStudentResults] = useState<string>('');
  const [transferringScore, setTransferringScore] = useState<{
    submission: CourseSubmission;
    student: Student;
    chapterTitle: string;
  } | null>(null);
  const [newModule, setNewModule] = useState<Partial<CourseModule>>({
    title: '',
    description: '',
    chapters: [],
    targetClasses: [],
    quizCooldownMinutes: 0
  });
  const [learningModule, setLearningModule] = useState<CourseModule | null>(null);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<number, number>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [uploadingImageIndex, setUploadingImageIndex] = useState<number | null>(null);
  const [showFullCoursePreview, setShowFullCoursePreview] = useState(false);
  const [previewChapterIndex, setPreviewChapterIndex] = useState(0);
  
  // Enhanced Quiz States
  const [currentQuizQuestionIndex, setCurrentQuizQuestionIndex] = useState(0);
  const [quizMistakes, setQuizMistakes] = useState(0);
  const [shuffledOptions, setShuffledOptions] = useState<{ text: string, originalIndex: number }[]>([]);
  const [quizFeedback, setQuizFeedback] = useState<{ isWrong: boolean, showCorrect: boolean } | null>(null);
  const [cooldownCountdown, setCooldownCountdown] = useState<number | null>(null);

  const [viewingStudentGrades, setViewingStudentGrades] = useState<Student | null>(null);

  // Helper to get student's grades summary for popup
  const studentGradesSummary = useMemo(() => {
    if (!viewingStudentGrades) return null;
    const student = viewingStudentGrades;
    
    // Get current subject context
    const currentSubject = viewingResults?.subject || activeUser?.subject;
    const normalizedCurrentSubject = currentSubject?.trim().toLowerCase();
    
    // Group all data by TP first
    const tpGradesMap: Record<string, { 
      tpId: string, 
      tpCode: string, 
      tpTitle: string,
      sumatif?: number, 
      praktikum?: number,
      date: string
    }> = {};

    // 1. Process standard assessmentGrades
    assessmentGrades.forEach(g => {
      if (g.class !== student.class) return;
      if (g.scores[student.id] === undefined) return;
      
      const tp = objectives.find(obj => obj.id === g.tpId);
      if (!tp) return;
      
      const tpSubject = tp.subject?.trim().toLowerCase();
      if (normalizedCurrentSubject && tpSubject && tpSubject !== normalizedCurrentSubject) return;

      if (!tpGradesMap[g.tpId]) {
        tpGradesMap[g.tpId] = {
          tpId: g.tpId,
          tpCode: tp.code || 'TP',
          tpTitle: tp.description || '',
          date: g.date
        };
      }

      if (g.activityType === ActivityType.SUMATIF) {
        tpGradesMap[g.tpId].sumatif = g.scores[student.id];
      } else if (g.activityType === ActivityType.PRAKTIKUM) {
        tpGradesMap[g.tpId].praktikum = g.scores[student.id];
      }
    });

    // 2. Process formativeGradesTP
    if (formativeGradesTP) {
      formativeGradesTP.forEach(fg => {
        if (fg.class !== student.class) return;
        const tp = objectives.find(obj => obj.id === fg.tpId);
        if (!tp) return;
        
        const tpSubject = tp.subject?.trim().toLowerCase();
        if (normalizedCurrentSubject && tpSubject && tpSubject !== normalizedCurrentSubject) return;

        if (!tpGradesMap[fg.tpId]) {
          tpGradesMap[fg.tpId] = {
            tpId: fg.tpId,
            tpCode: tp.code || 'TP',
            tpTitle: tp.description || '',
            date: new Date().toISOString()
          };
        }

        const sumatifScore = fg.summativeScores?.[student.id];
        if (sumatifScore !== undefined && tpGradesMap[fg.tpId].sumatif === undefined) {
          tpGradesMap[fg.tpId].sumatif = sumatifScore;
        }

        const praktikumScore = fg.praktikumScores?.[student.id];
        if (praktikumScore !== undefined && tpGradesMap[fg.tpId].praktikum === undefined) {
          tpGradesMap[fg.tpId].praktikum = praktikumScore;
        }
      });
    }

    return Object.values(tpGradesMap).sort((a, b) => a.tpCode.localeCompare(b.tpCode, undefined, { numeric: true }));
  }, [viewingStudentGrades, assessmentGrades, objectives, viewingResults, activeUser, formativeGradesTP]);

  const getCooldownRemaining = (module: CourseModule, chapterId: string) => {
    if (!module.quizCooldownMinutes || module.quizCooldownMinutes <= 0) return 0;
    
    const latestSub = courseSubmissions
      .filter(s => s.moduleId === module.id && s.studentId === activeUser.id && s.chapterId === chapterId)
      .sort((a, b) => b.submittedAt - a.submittedAt)[0];
      
    if (!latestSub) return 0;
    
    const cooldownMs = module.quizCooldownMinutes * 60 * 1000;
    const elapsedMs = Date.now() - latestSub.submittedAt;
    const remainingMs = cooldownMs - elapsedMs;
    
    return remainingMs > 0 ? remainingMs : 0;
  };

  useEffect(() => {
    let interval: any;
    if (learningModule && !showQuiz) {
      interval = setInterval(() => {
        const remaining = getCooldownRemaining(learningModule, learningModule.chapters[currentChapterIndex].id);
        setCooldownCountdown(remaining > 0 ? remaining : null);
      }, 1000);
    } else {
      setCooldownCountdown(null);
    }
    return () => clearInterval(interval);
  }, [learningModule, currentChapterIndex, showQuiz, courseSubmissions]);

  const handleOpenModule = (module: CourseModule) => {
    setLearningModule(module);
    setCurrentChapterIndex(0);
    setShowQuiz(false);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setCurrentQuizQuestionIndex(0);
    setQuizMistakes(0);
    setQuizFeedback(null);
  };

  const prepareQuizQuestion = (qIdx: number, module: CourseModule, chapterIdx: number) => {
    const chapter = module.chapters[chapterIdx];
    const question = chapter.quiz[qIdx];
    if (!question) return;
    
    // Shuffle options
    const optionsWithIndex = question.options.map((text, index) => ({ text, originalIndex: index }));
    const shuffled = [...optionsWithIndex].sort(() => Math.random() - 0.5);
    
    setShuffledOptions(shuffled);
    setCurrentQuizQuestionIndex(qIdx);
    setQuizFeedback(null);
  };

  const handleAnswerQuiz = (selectedOriginalIndex: number) => {
    if (!learningModule) return;
    const chapter = learningModule.chapters[currentChapterIndex];
    const question = chapter.quiz[currentQuizQuestionIndex];
    
    const isCorrect = selectedOriginalIndex === question.correctAnswerIndex;
    
    if (isCorrect) {
      setQuizAnswers(prev => ({ ...prev, [currentQuizQuestionIndex]: selectedOriginalIndex }));
      setQuizFeedback({ isWrong: false, showCorrect: true });
    } else {
      setQuizMistakes(prev => prev + 1);
      setQuizFeedback({ isWrong: true, showCorrect: true });
    }
  };

  const handleNextQuizQuestion = () => {
    if (!learningModule) return;
    const chapter = learningModule.chapters[currentChapterIndex];
    if (currentQuizQuestionIndex < chapter.quiz.length - 1) {
      prepareQuizQuestion(currentQuizQuestionIndex + 1, learningModule, currentChapterIndex);
    } else {
      setQuizSubmitted(true);
      // Save results
      if (role === UserRole.Siswa && activeUser) {
        const score = Math.max(0, 100 - quizMistakes);
        const submission: CourseSubmission = {
          id: `${learningModule.id}-${chapter.id}-${activeUser.id}`,
          moduleId: learningModule.id,
          chapterId: chapter.id,
          studentId: activeUser.id,
          score,
          mistakes: quizMistakes,
          submittedAt: Date.now()
        };
        onSaveCourseSubmission?.(submission);
      }
    }
  };

  const handleTransferScore = (type: 'sumatif' | 'praktikum') => {
    if (!transferringScore || !viewingCourseResults) return;
    const { student, submission, chapterTitle } = transferringScore;
    
    // According to AssessmentGrade schema:
    // id: Combined ID: class-tpId-activityType
    // tpId is required. Since we don't have a specific TP linked to a course yet,
    // we use a generic ID or allow teacher to pick one. 
    // For now, we'll use a specific identifier for Course Quizzes.
    
    const activityType = type === 'sumatif' ? ActivityType.SUMATIF : ActivityType.PRAKTIKUM;
    const tpId = `course-${viewingCourseResults.id}`;
    const gradeId = `${student.class}-${tpId}-${activityType}`;

    // Find existing assessment grade for this class/TP/type
    const existingGrade = assessmentGrades.find(g => g.id === gradeId);

    const newGrade: AssessmentGrade = existingGrade 
      ? {
          ...existingGrade,
          scores: {
            ...existingGrade.scores,
            [student.id]: submission.score
          }
        }
      : {
          id: gradeId,
          class: student.class,
          tpId: tpId,
          activityType: activityType,
          assessmentType: type === 'sumatif' ? 'Sumatif per TP' : 'KBM dan quiz (jika ada)',
          date: new Date().toISOString().split('T')[0],
          scores: {
            [student.id]: submission.score
          },
          teacherId: activeUser?.id || ''
        };

    onSaveAssessmentGrade?.(newGrade);
    setTransferringScore(null);
  };

  const handleOpenCreateModule = () => {
    setEditingModule(null);
    setNewModule({
      title: '',
      description: '',
      chapters: [{
        id: crypto.randomUUID(),
        title: '',
        content: '',
        imageUrl: '',
        quiz: []
      }],
      targetClasses: [],
      quizCooldownMinutes: 0
    });
    setIsModuleModalOpen(true);
  };

  const handleEditModule = (module: CourseModule) => {
    setEditingModule(module);
    setNewModule({ ...module });
    setIsModuleModalOpen(true);
  };

  const handleSaveModule = () => {
    if (!newModule.title || !newModule.chapters || newModule.chapters.length === 0) {
      alert('Judul dan setidaknya satu bab wajib diisi');
      return;
    }
    
    const moduleToSave: CourseModule = {
      id: editingModule?.id || crypto.randomUUID(),
      title: newModule.title || '',
      description: newModule.description || '',
      subject: activeUser.subject || '',
      teacherId: activeUser.id,
      chapters: (newModule.chapters || []).map(ch => ({
        ...ch,
        id: ch.id || crypto.randomUUID()
      })),
      targetClasses: newModule.targetClasses || [],
      quizCooldownMinutes: newModule.quizCooldownMinutes || 0,
      createdAt: editingModule?.createdAt || Date.now(),
      updatedAt: Date.now()
    };

    if (onSaveCourseModule) {
      onSaveCourseModule(moduleToSave);
      setIsModuleModalOpen(false);
    }
  };

  const addChapter = () => {
    setNewModule(prev => ({
      ...prev,
      chapters: [
        ...(prev.chapters || []),
        { id: crypto.randomUUID(), title: '', content: '', imageUrl: '', quiz: [] }
      ]
    }));
  };

  const removeChapter = (index: number) => {
    setNewModule(prev => {
      const newChapters = [...(prev.chapters || [])];
      newChapters.splice(index, 1);
      return { ...prev, chapters: newChapters };
    });
  };

  const updateChapter = (index: number, updates: Partial<CourseChapter>) => {
    setNewModule(prev => {
      const newChapters = [...(prev.chapters || [])];
      newChapters[index] = { ...newChapters[index], ...updates };
      return { ...prev, chapters: newChapters };
    });
  };

  const addQuizQuestion = (chapterIndex: number) => {
    setNewModule(prev => {
      const newChapters = [...(prev.chapters || [])];
      newChapters[chapterIndex].quiz = [
        ...newChapters[chapterIndex].quiz,
        { question: '', options: ['', '', '', ''], correctAnswerIndex: 0 }
      ];
      return { ...prev, chapters: newChapters };
    });
  };

  const updateQuizQuestion = (chapterIndex: number, quizIndex: number, updates: Partial<CourseQuiz>) => {
    setNewModule(prev => {
      const newChapters = [...(prev.chapters || [])];
      const newQuiz = [...newChapters[chapterIndex].quiz];
      newQuiz[quizIndex] = { ...newQuiz[quizIndex], ...updates };
      newChapters[chapterIndex].quiz = newQuiz;
      return { ...prev, chapters: newChapters };
    });
  };

  const removeQuizQuestion = (chapterIndex: number, quizIndex: number) => {
    setNewModule(prev => {
      const newChapters = [...(prev.chapters || [])];
      const newQuiz = [...newChapters[chapterIndex].quiz];
      newQuiz.splice(quizIndex, 1);
      newChapters[chapterIndex].quiz = newQuiz;
      return { ...prev, chapters: newChapters };
    });
  };


  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // For results filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClass, setFilterClass] = useState('Semua');

  // For creating/editing
  const [title, setTitle] = useState('');
  const [tugasType, setTugasType] = useState<TugasType>(TugasType.TUGAS);
  const [targetType, setTargetType] = useState<'all' | 'class' | 'student'>('all');
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [studentFilterClass, setStudentFilterClass] = useState('all');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isSummative, setIsSummative] = useState(false);
  const [tpId, setTpId] = useState('');
  const [isExtraCredit, setIsExtraCredit] = useState(false);
  const [extraCreditTargetTpId, setExtraCreditTargetTpId] = useState('');
  const [forceSequential, setForceSequential] = useState(false);
  const [fullscreenRequired, setFullscreenRequired] = useState(false);
  const [questions, setQuestions] = useState<TugasQuestion[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialTugasId && tugas.length > 0) {
      const found = tugas.find((t: Tugas) => t.id === initialTugasId);
      if (found) {
        setViewingResults(found);
      }
    }
  }, [initialTugasId, tugas]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const worksheet = workbook.worksheets[0];
      
      const newQuestions: TugasQuestion[] = [];
      
      // Assuming row 1 is header
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Skip header
        
        const text = row.getCell(1).text || '';
        const optA = row.getCell(2).text || '';
        const optB = row.getCell(3).text || '';
        const optC = row.getCell(4).text || '';
        const optD = row.getCell(5).text || '';
        const answerLetter = (row.getCell(6).text || '').toUpperCase().trim();
        const timeLimit = parseInt(row.getCell(7).text) || undefined;
        
        if (!text) return; // Skip empty rows
        
        let correctAnswerIndex = 0;
        if (answerLetter === 'B') correctAnswerIndex = 1;
        else if (answerLetter === 'C') correctAnswerIndex = 2;
        else if (answerLetter === 'D') correctAnswerIndex = 3;
        
        newQuestions.push({
          id: crypto.randomUUID(),
          text,
          type: 'multiple-choice',
          options: [optA, optB, optC, optD],
          correctAnswerIndex,
          points: 10,
          timeLimit
        });
      });
      
      if (newQuestions.length > 0) {
        setQuestions(prev => {
          // If there's only one empty question, replace it
          if (prev.length === 1 && prev[0].text === '' && prev[0].options.every(o => o === '')) {
            return newQuestions;
          }
          return [...prev, ...newQuestions];
        });
        alert(`Berhasil mengimpor ${newQuestions.length} soal dari Excel.`);
      } else {
        alert('Tidak ada soal yang ditemukan dalam file Excel. Pastikan format sesuai.');
      }
    } catch (error) {
      console.error('Error reading Excel file:', error);
      alert('Gagal membaca file Excel. Pastikan file berformat .xlsx');
    }
    
    // Reset file input
    if (e.target) e.target.value = '';
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Template Soal');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };
    
    worksheet.columns = [
      { header: 'Pertanyaan', key: 'pertanyaan', width: 40 },
      { header: 'Opsi A', key: 'opsiA', width: 20 },
      { header: 'Opsi B', key: 'opsiB', width: 20 },
      { header: 'Opsi C', key: 'opsiC', width: 20 },
      { header: 'Opsi D', key: 'opsiD', width: 20 },
      { header: 'Jawaban Benar (A/B/C/D)', key: 'jawaban', width: 25 },
      { header: 'Waktu (Detik)', key: 'timeLimit', width: 15 },
    ];
    
    worksheet.addRow({
      pertanyaan: 'Siapakah penemu bola lampu?',
      opsiA: 'Thomas Edison',
      opsiB: 'Albert Einstein',
      opsiC: 'Nikola Tesla',
      opsiD: 'Isaac Newton',
      jawaban: 'A',
      timeLimit: 60
    });
    
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Template_Soal_Tugas.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Randomization states for student
  const [randomizedQuestions, setRandomizedQuestions] = useState<TugasQuestion[]>([]);
  const [randomizedOptionIndices, setRandomizedOptionIndices] = useState<Record<string, number[]>>({});
  const [showStartWarning, setShowStartWarning] = useState(false);
  const [pendingTugas, setPendingTugas] = useState<Tugas | null>(null);
  const [tugasResult, setTugasResult] = useState<{ 
    score: number; 
    correct: number; 
    total: number;
    essayTotal: number;
    tugasTitle: string;
    tugasType: string;
    hasEssay: boolean;
  } | null>(null);

  const teacherTugas = useMemo(() => {
    if (role !== UserRole.Guru) return [];
    return tugas.filter(q => 
      q.teacherId === activeUser.id && 
      (filterType === 'all' || q.type === filterType)
    ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [tugas, role, activeUser, filterType]);

  const studentTugas = useMemo(() => {
    if (role !== UserRole.Siswa) return [];
    const student = activeUser as Student;
    return tugas.filter(q => {
      let isTargeted = false;
      if (q.targetStudents && q.targetStudents.length > 0) {
        isTargeted = q.targetStudents.includes(student.id);
      } else if (q.targetClasses && q.targetClasses.length > 0) {
        isTargeted = q.targetClasses.includes(student.class);
      } else {
        isTargeted = q.class === student.class || q.class === 'Semua Kelas';
      }

      return isTargeted && 
        q.isActive && 
        (!selectedSubjectForStudent || q.subject?.trim().toLowerCase() === selectedSubjectForStudent.trim().toLowerCase()) &&
        (filterType === 'all' || q.type === filterType);
    });
  }, [tugas, role, activeUser, selectedSubjectForStudent, filterType]);

  const teacherSubjects = useMemo(() => {
    const subs = new Set<string>();
    if (activeUser?.subject) subs.add(activeUser.subject);
    tugas.forEach(t => {
      if (t.teacherId === activeUser.id && t.subject) {
        subs.add(t.subject);
      }
    });
    return Array.from(subs);
  }, [tugas, activeUser]);

  const reportData = useMemo(() => {
    // 1. Filter assignments for this teacher
    let teacherTugasList = tugas.filter(t => t.teacherId === activeUser.id);
    
    // Filter by subject if chosen
    if (reportSelectedSubject !== 'all') {
      teacherTugasList = teacherTugasList.filter(t => t.subject === reportSelectedSubject);
    }
    
    // 2. Filter students
    let filteredStudents = students || [];
    if (reportSelectedClass !== 'all') {
      filteredStudents = filteredStudents.filter(s => s.class === reportSelectedClass);
    }
    
    // 3. Score matrix
    const studentsWithGrades = filteredStudents.map(student => {
      const taskScores: Record<string, number | null> = {};
      let totalScore = 0;
      let gradedTaskCount = 0;
      
      teacherTugasList.forEach(task => {
        const sub = tugasSubmissions.find(s => s.tugasId === task.id && s.studentId === student.id);
        if (sub) {
          taskScores[task.id] = sub.score;
          totalScore += sub.score;
          gradedTaskCount++;
        } else {
          taskScores[task.id] = null;
        }
      });
      
      const average = gradedTaskCount > 0 ? Math.round(totalScore / gradedTaskCount) : 0;
      
      return {
        student,
        scores: taskScores,
        average,
        gradedCount: gradedTaskCount
      };
    }).filter(item => item.gradedCount > 0);
    
    return {
      tasks: teacherTugasList,
      students: studentsWithGrades
    };
  }, [tugas, tugasSubmissions, students, activeUser, reportSelectedClass, reportSelectedSubject]);

  const handlePrintResults = (taskParam?: Tugas) => {
    const task = taskParam || viewingResults;
    if (!task) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const submissions = (tugasSubmissions || []).filter(s => s.tugasId === task.id);
    // Only include students who are detected as having worked on/submitted the task
    const validSubmissions = submissions.filter(sub => {
      const student = students.find(s => s.id === sub.studentId);
      return !!student;
    });

    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

    // Generate table rows dynamically
    const rowTemplates = validSubmissions.map((sub, idx) => {
      const student = students.find(s => s.id === sub.studentId)!;

      let pgEarned = 0;
      let pgTotal = 0;
      let essayEarned = 0;
      let essayTotal = 0;

      task.questions.forEach(q => {
        const pts = q.points || 10;
        if (!q.type || q.type === 'multiple-choice') {
          pgTotal += pts;
          if (sub.answers[q.id] === q.correctAnswerIndex) pgEarned += pts;
        } else if (q.type === 'essay') {
          essayTotal += pts;
          essayEarned += (sub.essayScores?.[q.id] || 0);
        }
      });

      const sumatifGrade = task.tpId ? (assessmentGrades.find(g => g.id === `${student.class}::${task.tpId}::${ActivityType.SUMATIF}`)?.scores[student.id]) : null;
      const praktikGrade = task.tpId ? (assessmentGrades.find(g => g.id === `${student.class}::${task.tpId}::${ActivityType.PRAKTIKUM}`)?.scores[student.id]) : null;

      const isPerempuan = student.gender === 'Perempuan';

      return `
        <tr>
          <td style="font-weight: bold; font-size: 1rem;">${idx + 1}</td>
          <td style="font-weight: bold; font-size: 1rem;">${student.nisn || student.nis || '-'}</td>
          <td class="name" style="font-weight: bold; font-size: 1rem; ${isPerempuan ? 'color: #e11d48;' : ''}">${student.name.toUpperCase()}</td>
          <td style="font-size: 1rem;">${student.class}</td>
          <td style="font-size: 1rem;">${pgTotal > 0 ? pgEarned : '-'}</td>
          <td style="font-size: 1rem;">${essayTotal > 0 ? essayEarned : '-'}</td>
          <td style="font-weight: bold; font-size: 1.1rem; color: #1e1b4b;">${sub.score}</td>
          ${task.tpId ? `
            <td style="font-weight: bold; font-size: 1rem;">${sumatifGrade !== undefined && sumatifGrade !== null ? sumatifGrade : '-'}</td>
            <td style="font-weight: bold; font-size: 1rem;">${praktikGrade !== undefined && praktikGrade !== null ? praktikGrade : '-'}</td>
          ` : ''}
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Laporan Hasil Tugas - ${task.title}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
            .kop-logo { width: 80px; height: 80px; object-fit: contain; }
            .kop-text { flex: 1; text-align: center; }
            .kop-gov { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-dept { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-school { font-size: 18px; font-weight: bold; margin: 5px 0; }
            .kop-address { font-size: 10px; font-style: italic; margin: 0; }
            .kop-additional { font-size: 10px; margin: 0; }
            .kop-line-1 { border-bottom: 3px solid black; margin-top: 10px; }
            .kop-line-2 { border-bottom: 1px solid black; margin-top: 2px; margin-bottom: 20px; }
            
            .report-title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; }
            
            .meta-info { width: 100%; margin-bottom: 20px; font-size: 12px; }
            .meta-info td { padding: 3px 0; }
            
            table.data-table { width: 100%; border-collapse: collapse; font-size: 12px; }
            table.data-table th, table.data-table td { border: 1px solid black; padding: 10px 8px; text-align: center; }
            table.data-table th { background-color: #f0f0f0; font-weight: bold; }
            table.data-table td.name { text-align: left; padding-left: 15px; }
            
            .signature-area { margin-top: 40px; width: 100%; font-size: 12px; }
            .signature-area td { width: 48%; text-align: center; vertical-align: bottom; height: 100px; }
            .signature-name { font-weight: bold; text-decoration: underline; }
            
            @media print {
              @page { size: portrait; margin: 1cm; }
            }
          </style>
        </head>
        <body>
          <div class="kop-container">
            <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
            <div class="kop-text">
              <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
              <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
              <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
              <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
              ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
            </div>
            <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
          </div>
          <div class="kop-line-1"></div>
          <div class="kop-line-2"></div>
 
          <div class="report-title">LAPORAN HASIL TUGAS / ASSIGNMENT REPORT</div>
 
          <table class="meta-info">
            <tr>
              <td style="width: 20%; font-weight: bold;">Judul Tugas</td><td style="width: 2%">:</td><td style="font-weight: bold;">${task.title.toUpperCase()}</td>
            </tr>
            <tr>
              <td style="font-weight: bold;">Mata Pelajaran</td><td>:</td><td>${task.subject || activeUser?.subject || '-'}</td>
            </tr>
            <tr>
              <td style="font-weight: bold;">Kelas / Rombel</td><td>:</td><td>Kelas ${task.class || 'Semua Kelas'}</td>
            </tr>
            <tr>
              <td style="font-weight: bold;">Nama Guru</td><td>:</td><td>${activeUser?.name || '-'}</td>
            </tr>
          </table>
 
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 60px;">NO</th>
                <th style="width: 120px;">NISN</th>
                <th>NAMA SISWA</th>
                <th style="width: 100px;">KELAS</th>
                <th style="width: 100px;">SKOR PG</th>
                <th style="width: 100px;">SKOR ESSAY</th>
                <th style="width: 110px;">NILAI TUGAS</th>
                ${task.tpId ? `
                  <th style="width: 110px;">SUMATIF TP</th>
                  <th style="width: 110px;">PRAKTIK TP</th>
                ` : ''}
              </tr>
            </thead>
            <tbody>
              ${rowTemplates.length > 0 ? rowTemplates : '<tr><td colspan="10" style="padding: 20px; font-weight: bold; color: #666;">Belum ada siswa yang mengerjakan tugas ini.</td></tr>'}
            </tbody>
          </table>

          <table class="signature-area">
            <tr>
              <td>
                Mengetahui,<br/>
                Kepala Sekolah<br/><br/><br/><br/>
                <span class="signature-name">${schoolSettings.principalName || '________________'}</span><br/>
                <span>NIP. ${schoolSettings.principalNip || '________________'}</span>
              </td>
              <td>
                ${schoolSettings.address.split(',')[0] || 'Ciamis'}, ${today}<br/>
                Guru Mata Pelajaran,<br/><br/><br/><br/>
                <span class="signature-name">${activeUser?.name || '________________'}</span><br/>
                ${teacherNip ? `<span>NIP. ${teacherNip}</span>` : ''}
              </td>
            </tr>
          </table>
          
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const filteredSubmissions = useMemo(() => {
    if (!viewingResults) return [];
    const submissions = (tugasSubmissions || []).filter(s => s.tugasId === viewingResults.id);
    return submissions.filter(sub => {
      const student = students.find(s => s.id === sub.studentId);
      if (!student) return false;
      
      const name = (student.name || '').toLowerCase();
      const nis = (student.nis || '').toLowerCase();
      const nisn = (student.nisn || '').toLowerCase();
      const query = (searchQuery || '').toLowerCase();
      
      const matchesSearch = name.includes(query) || 
                            nis.includes(query) ||
                            (student.nisn && nisn.includes(query));
      const matchesClass = filterClass === 'Semua' || student.class === filterClass;
      
      return matchesSearch && matchesClass;
    }).sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
  }, [viewingResults, tugasSubmissions, students, searchQuery, filterClass]);

  const handleCreateNew = () => {
    setTitle('');
    setTugasType(filterType !== 'all' ? filterType as TugasType : TugasType.TUGAS);
    setTargetType('all');
    setSelectedClasses([]);
    setSelectedStudents([]);
    setStartTime('');
    setEndTime('');
    setIsSummative(false);
    setTpId('');
    setIsExtraCredit(false);
    setExtraCreditTargetTpId('');
    setForceSequential(false);
    setFullscreenRequired(false);
    setQuestions([{ id: Date.now().toString(), text: '', type: 'multiple-choice', options: ['', '', '', ''], correctAnswerIndex: 0, points: 10 }]);
    setActiveTugas(null);
    setIsCreating(true);
  };

  const handleEditTugas = (tugasItem: Tugas) => {
    setTitle(tugasItem.title);
    setTugasType(tugasItem.type || TugasType.TUGAS);
    
    if (tugasItem.targetStudents && tugasItem.targetStudents.length > 0) {
      setTargetType('student');
      setSelectedStudents(tugasItem.targetStudents);
      setSelectedClasses([]);
    } else if (tugasItem.targetClasses && tugasItem.targetClasses.length > 0) {
      setTargetType('class');
      setSelectedClasses(tugasItem.targetClasses);
      setSelectedStudents([]);
    } else {
      if (tugasItem.class === 'Semua Kelas') {
        setTargetType('all');
        setSelectedClasses([]);
      } else {
        setTargetType('class');
        setSelectedClasses([tugasItem.class]);
      }
      setSelectedStudents([]);
    }

    const formatForInput = (val: any) => {
      if (!val) return '';
      try {
        if (typeof val === 'string') return val.slice(0, 16);
        if (val instanceof Date) return val.toISOString().slice(0, 16);
        if (typeof val.toISOString === 'function') return val.toISOString().slice(0, 16);
        if (val && typeof val.toDate === 'function') return val.toDate().toISOString().slice(0, 16);
        return String(val).slice(0, 16);
      } catch (e) {
        console.error("Error formatting date for input:", e);
        return '';
      }
    };

    setStartTime(formatForInput(tugasItem.startTime));
    setEndTime(formatForInput(tugasItem.endTime));
    setIsSummative(tugasItem.type === TugasType.TUGAS_NILAI_TAMBAH ? false : (tugasItem.isSummative || false));
    setTpId(tugasItem.type === TugasType.TUGAS_NILAI_TAMBAH ? '' : (tugasItem.tpId || ''));
    setIsExtraCredit(tugasItem.type === TugasType.TUGAS_NILAI_TAMBAH);
    setExtraCreditTargetTpId(tugasItem.type === TugasType.TUGAS_NILAI_TAMBAH ? (tugasItem.tpId || '') : '');
    setForceSequential(tugasItem.forceSequential || false);
    setFullscreenRequired(tugasItem.fullscreenRequired || false);
    setQuestions([...tugasItem.questions]);
    setActiveTugas(tugasItem);
    setIsCreating(true);
  };

  const handleDuplicateTugas = (tugasItem: Tugas) => {
    const duplicatedTugas: Tugas = {
      ...tugasItem,
      id: `tugas-${Date.now()}`,
      title: `${tugasItem.title} (Salinan)`,
      questions: tugasItem.questions.map(q => ({ ...q })),
      createdAt: new Date().toISOString(),
      isActive: false // Default duplicated to inactive
    };
    
    onSaveTugas(duplicatedTugas);
  };

  const handleAddQuestion = () => {
    setQuestions([...questions, { id: Date.now().toString(), text: '', type: 'multiple-choice', options: ['', '', '', ''], correctAnswerIndex: 0, points: 10 }]);
  };

  const handleQuestionChange = (index: number, field: string, value: any) => {
    const newQuestions = [...questions];
    if (field === 'text') {
      newQuestions[index].text = value;
    } else if (field === 'correctAnswerIndex') {
      newQuestions[index].correctAnswerIndex = value;
    } else if (field === 'type') {
      newQuestions[index].type = value;
    } else if (field === 'points') {
      newQuestions[index].points = parseInt(value) || 0;
    } else if (field === 'timeLimit') {
      newQuestions[index].timeLimit = value !== '' ? parseInt(value) : undefined;
    }
    setQuestions(newQuestions);
  };

  const handleOptionChange = (qIndex: number, optIndex: number, value: string) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options[optIndex] = value;
    setQuestions(newQuestions);
  };

  const handleRemoveQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleSaveTugas = () => {
    console.log("TugasView: handleSaveTugas", title);
    if (!title || questions.length === 0) {
      alert('Mohon lengkapi judul dan minimal 1 pertanyaan.');
      return;
    }
    
    if (targetType === 'class' && selectedClasses.length === 0) {
      alert('Mohon pilih minimal 1 kelas.');
      return;
    }
    
    if (targetType === 'student' && selectedStudents.length === 0) {
      alert('Mohon pilih minimal 1 siswa.');
      return;
    }

    const teacher = activeUser as Teacher;
    const newTugas: Tugas = {
      id: activeTugas ? activeTugas.id : crypto.randomUUID(),
      title,
      type: tugasType,
      teacherId: teacher.id,
      subject: teacher.subject,
      class: targetType === 'all' ? 'Semua Kelas' : (targetType === 'class' ? selectedClasses[0] : 'Semua Kelas'),
      targetClasses: targetType === 'class' ? selectedClasses : [],
      targetStudents: targetType === 'student' ? selectedStudents : [],
      questions,
      createdAt: activeTugas ? activeTugas.createdAt : new Date().toISOString(),
      isActive: true,
      startTime: startTime ? new Date(startTime).toISOString() : undefined,
      endTime: endTime ? new Date(endTime).toISOString() : undefined,
      isSummative: tugasType === TugasType.TUGAS_NILAI_TAMBAH ? false : isSummative,
      tpId: tugasType === TugasType.TUGAS_NILAI_TAMBAH ? extraCreditTargetTpId : (isSummative ? tpId : undefined),
      forceSequential,
      fullscreenRequired,
    };

    console.log("TugasView: Calling onSaveTugas", newTugas.id);
    onSaveTugas(newTugas);
    setIsCreating(false);
    setActiveTugas(null);
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const handleStartTugas = (tugasItem: Tugas) => {
    const now = new Date();
    if (tugasItem.startTime && new Date(tugasItem.startTime) > now) {
      alert('Tugas belum dimulai.');
      return;
    }
    if (tugasItem.endTime && new Date(tugasItem.endTime) < now) {
      alert('Waktu pengerjaan tugas sudah berakhir.');
      return;
    }

    if (tugasItem.forceSequential) {
      setPendingTugas(tugasItem);
      setShowStartWarning(true);
    } else {
      startTugasExecution(tugasItem);
    }
  };

  useEffect(() => {
    if (!takingTugas) {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      setIsFullscreen(false);
      setCheatWarnings(0);
      return;
    }

    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      if (!isFull && takingTugas && takingTugas.fullscreenRequired) {
        setCheatWarnings(prev => prev + 1);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && takingTugas && takingTugas.fullscreenRequired) {
        setCheatWarnings(prev => prev + 1);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [takingTugas]);

  const handleEnterFullscreen = () => {
    if (fullscreenRef.current) {
      const elem = fullscreenRef.current as any;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    }
  };

  const startTugasExecution = (tugasItem: Tugas) => {
    // Separate MCQ and Essay questions
    const mcqQuestions = tugasItem.questions.filter(q => !q.type || q.type === 'multiple-choice');
    const essayQuestions = tugasItem.questions.filter(q => q.type === 'essay');

    // Randomize questions (MCQ first, then Essay)
    const shuffledQuestions = [
      ...shuffleArray(mcqQuestions),
      ...shuffleArray(essayQuestions)
    ];
    
    // Randomize options for each question
    const optionIndicesMap: Record<string, number[]> = {};
    shuffledQuestions.forEach(q => {
      const indices = [0, 1, 2, 3];
      optionIndicesMap[q.id] = shuffleArray(indices);
    });

    setRandomizedQuestions(shuffledQuestions);
    setRandomizedOptionIndices(optionIndicesMap);
    setTakingTugas(tugasItem);
    setStudentAnswers({});
    setCurrentQuestionIndex(0);
    setQuestionStartTime(Date.now());
    setTimeRemaining(shuffledQuestions[0].timeLimit || null);
    setShowStartWarning(false);
    setPendingTugas(null);

    // Auto-enter fullscreen if required
    if (tugasItem.fullscreenRequired && role === UserRole.Siswa) {
      setTimeout(() => {
        handleEnterFullscreen();
      }, 100);
    }

    // Create Realtime Session
    if (role === UserRole.Siswa && onStartSession) {
      const student = activeUser as Student;
      const sessionId = `session-${tugasItem.id}-${student.id}`;
      onStartSession({
        id: sessionId,
        tugasId: tugasItem.id,
        studentId: student.id,
        studentName: student.name,
        studentClass: student.class,
        tugasTitle: tugasItem.title,
        startTime: new Date().toISOString(),
        lastActive: new Date().toISOString(),
        status: 'working'
      });
    }
  };

  const handleAnswerSelect = (questionId: string, answer: number | string) => {
    setStudentAnswers({
      ...studentAnswers,
      [questionId]: answer
    });
  };

  useEffect(() => {
    if (takingTugas && questionStartTime !== null) {
      const currentQ = randomizedQuestions[currentQuestionIndex];
      if (!currentQ || !currentQ.timeLimit) {
        setTimeRemaining(null);
        return;
      }

      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - questionStartTime) / 1000);
        const remaining = currentQ.timeLimit! - elapsed;
        
        if (remaining <= 0) {
          clearInterval(interval);
          setTimeRemaining(0);
          // Time is up for this question
          if (currentQuestionIndex < randomizedQuestions.length - 1) {
            // Move to next question
            const nextIdx = currentQuestionIndex + 1;
            setCurrentQuestionIndex(nextIdx);
            setQuestionStartTime(Date.now());
            setTimeRemaining(randomizedQuestions[nextIdx].timeLimit || null);
          } else {
            // Last question, submit automatically
            handleSubmitTugas();
          }
        } else {
          setTimeRemaining(remaining);
        }
      }, 500); // Check more frequently
      return () => clearInterval(interval);
    }
  }, [takingTugas, questionStartTime, currentQuestionIndex, randomizedQuestions]);

  const handleSubmitTugas = () => {
    console.log("TugasView: handleSubmitTugas CALLED", takingTugas);
    if (!takingTugas) return;
    
    setQuestionStartTime(null);
    setTimeRemaining(null);
    
    let earnedPoints = 0;
    let totalMcqPoints = 0;
    let totalEssayPoints = 0;
    takingTugas.questions.forEach(q => {
      const pts = q.points || 10;
      if (!q.type || q.type === 'multiple-choice') {
        totalMcqPoints += pts;
        if (studentAnswers[q.id] === q.correctAnswerIndex) {
          earnedPoints += pts;
        }
      } else if (q.type === 'essay') {
        totalEssayPoints += pts;
      }
    });

    let score = earnedPoints;
    // Score is just the sum of points earned from PG questions initially.
    // Essay points will be added later by the teacher.

    const submission: TugasSubmission = {
      id: crypto.randomUUID(),
      tugasId: takingTugas.id,
      studentId: activeUser.id,
      score,
      answers: studentAnswers,
      submittedAt: new Date().toISOString()
    };

    console.log("TugasView: Calling onSubmitTugas", submission);
    onSubmitTugas(submission);

    // End Realtime Session
    if (role === UserRole.Siswa && onEndSession) {
      const sessionId = `session-${takingTugas.id}-${activeUser.id}`;
      onEndSession(sessionId);
    }

    setTugasResult({ 
      score, 
      correct: earnedPoints, 
      total: totalMcqPoints,
      essayTotal: totalEssayPoints,
      tugasTitle: takingTugas.title,
      tugasType: takingTugas.type,
      hasEssay: takingTugas.questions.some(q => q.type === 'essay')
    });
    setTakingTugas(null);
    setCurrentQuestionIndex(0);
    setStudentAnswers({});
  };

  const sessionStartTimeRef = useRef<string>(new Date().toISOString());
  useEffect(() => {
    if (takingTugas) {
      sessionStartTimeRef.current = new Date().toISOString();
    }
  }, [takingTugas]);

  useEffect(() => {
    if (takingTugas && role === UserRole.Siswa && onStartSession) {
      const student = activeUser as Student;
      const sessionId = `session-${takingTugas.id}-${student.id}`;
      
      const updateSession = () => {
        const isIdle = document.hidden;
        onStartSession({
          id: sessionId,
          tugasId: takingTugas.id,
          studentId: student.id,
          studentName: student.name,
          studentClass: student.class,
          tugasTitle: takingTugas.title,
          startTime: sessionStartTimeRef.current,
          lastActive: new Date().toISOString(),
          status: isIdle ? 'idle' : 'working'
        });
      };

      updateSession();
      const interval = setInterval(updateSession, 30000);

      const handleVisibility = () => {
        updateSession();
      };
      window.addEventListener('visibilitychange', handleVisibility);

      return () => {
        clearInterval(interval);
        window.removeEventListener('visibilitychange', handleVisibility);
      };
    }
  }, [takingTugas, role, activeUser, onStartSession]);

  // Clean up session if component unmounts, student leaves task, or closes tab
  useEffect(() => {
    const handleUnload = () => {
      if (takingTugas && role === UserRole.Siswa && onEndSession) {
        const sessionId = `session-${takingTugas.id}-${activeUser.id}`;
        onEndSession(sessionId);
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (takingTugas && role === UserRole.Siswa && onEndSession) {
        const sessionId = `session-${takingTugas.id}-${activeUser.id}`;
        onEndSession(sessionId);
      }
    };
  }, [takingTugas, role, activeUser, onEndSession]);


  const renderContent = () => {
    if (takingTugas) {
    return (
      <div ref={fullscreenRef} className="animate-fade-in space-y-6 max-w-3xl mx-auto bg-slate-50 dark:bg-slate-950 p-4 sm:p-8 min-h-screen relative overflow-y-auto">
        {/* Anti-Cheat Overlay */}
        {takingTugas.fullscreenRequired && !isFullscreen && role === UserRole.Siswa && (
          <div className="fixed inset-0 z-[9999] bg-slate-900/95 backdrop-blur-md flex items-center justify-center p-6 text-center">
            <div className="max-w-md w-full bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-300">
              <div className="w-20 h-20 bg-rose-100 dark:bg-rose-500/20 rounded-3xl flex items-center justify-center mx-auto mb-6 text-rose-600 dark:text-rose-400">
                <XCircle size={40} />
              </div>
              <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-4">Mode Ujian Terganggu!</h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-8">
                Anda terdeteksi keluar dari mode layar penuh atau berpindah tab. 
                Untuk melanjutkan pengerjaan tugas, Anda <strong>WAJIB</strong> kembali ke mode layar penuh.
                <br /><br />
                <span className="text-rose-500 font-bold">Peringatan: {cheatWarnings} kali terdeteksi.</span>
              </p>
              <button
                onClick={handleEnterFullscreen}
                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <Play size={20} />
                Kembali ke Layar Penuh
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => setTakingTugas(null)}
            className="p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:text-indigo-600 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-2xl font-black text-slate-800 dark:text-white">{takingTugas.title}</h2>
            <p className="text-sm text-slate-500">{takingTugas.subject} - {takingTugas.questions.length} Pertanyaan</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-3xl sm:rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex justify-between items-center w-full sm:w-auto">
              <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">
                Pertanyaan {currentQuestionIndex + 1} dari {takingTugas.questions.length}
              </span>
              
              {timeRemaining !== null && (
                <span className={`sm:hidden text-xs font-black uppercase tracking-widest ${timeRemaining <= 10 ? 'text-rose-600 animate-pulse' : 'text-slate-500'}`}>
                  {timeRemaining}s
                </span>
              )}
            </div>

            {timeRemaining !== null && (
              <span className={`hidden sm:block text-xs font-black uppercase tracking-widest ${timeRemaining <= 10 ? 'text-rose-600 animate-pulse' : 'text-slate-500'}`}>
                Waktu: {timeRemaining} detik
              </span>
            )}

            <div className="flex flex-wrap gap-1 w-full sm:w-auto">
              {randomizedQuestions.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1.5 rounded-full transition-all ${
                    idx === currentQuestionIndex ? 'w-6 bg-indigo-600' : 
                    studentAnswers[randomizedQuestions[idx].id] !== undefined ? 'w-2 bg-emerald-500' : 'w-2 bg-slate-200 dark:bg-slate-800'
                  }`}
                />
              ))}
            </div>
          </div>

          {(() => {
            const q = randomizedQuestions[currentQuestionIndex];
            const optionIndices = randomizedOptionIndices[q.id] || [0, 1, 2, 3];
            return (
              <div key={q.id} className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-8 leading-tight whitespace-pre-wrap">{q.text}</h3>
                
                {q.type === 'essay' ? (
                  <textarea
                    value={(studentAnswers[q.id] as string) || ''}
                    onChange={(e) => handleAnswerSelect(q.id, e.target.value)}
                    className="w-full p-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-3xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-slate-700 dark:text-slate-300 min-h-[200px] resize-y"
                    placeholder="Tulis jawaban Anda di sini..."
                  />
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {optionIndices.map((optIndex) => {
                      const opt = q.options[optIndex];
                      return (
                        <button
                          key={optIndex}
                          onClick={() => handleAnswerSelect(q.id, optIndex)}
                          className={`w-full text-left p-5 rounded-3xl border-2 transition-all flex items-center gap-4 group ${
                            studentAnswers[q.id] === optIndex
                              ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                              : 'border-slate-100 dark:border-slate-800 hover:border-indigo-300 bg-slate-50/50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black transition-colors ${
                            studentAnswers[q.id] === optIndex ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-slate-900 text-slate-400 group-hover:text-indigo-600'
                          }`}>
                            {String.fromCharCode(65 + optionIndices.indexOf(optIndex))}
                          </div>
                          <span className="font-bold text-sm">{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-between items-center pt-6 gap-4">
          <button
            onClick={() => {
              setCurrentQuestionIndex(prev => Math.max(0, prev - 1));
              setQuestionStartTime(Date.now());
              const prevIdx = Math.max(0, currentQuestionIndex - 1);
              setTimeRemaining(randomizedQuestions[prevIdx].timeLimit || null);
            }}
            disabled={currentQuestionIndex === 0 || takingTugas.forceSequential}
            className="w-full sm:w-auto px-6 py-3 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl font-bold border border-slate-200 dark:border-slate-800 hover:bg-slate-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <ChevronLeft size={18} />
            Sebelumnya
          </button>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {currentQuestionIndex < takingTugas.questions.length - 1 ? (
              <button
                onClick={() => {
                  const nextIdx = currentQuestionIndex + 1;
                  setCurrentQuestionIndex(nextIdx);
                  setQuestionStartTime(Date.now());
                  setTimeRemaining(randomizedQuestions[nextIdx].timeLimit || null);
                }}
                disabled={takingTugas.forceSequential && studentAnswers[randomizedQuestions[currentQuestionIndex].id] === undefined}
                className="w-full sm:w-auto px-8 py-3 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Selanjutnya
                <ChevronRight size={18} />
              </button>
            ) : (
              <button
                onClick={handleSubmitTugas}
                className="w-full sm:w-auto px-8 py-3 bg-emerald-600 text-white rounded-2xl font-bold shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 transition-all flex items-center justify-center gap-2"
              >
                Kumpulkan Jawaban
                <CheckCircle size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isCreating) {
    return (
      <div className="animate-fade-in space-y-6 max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsCreating(false)}
              className="p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:text-indigo-600 transition-colors shrink-0"
            >
              <ArrowLeft size={20} />
            </button>
            <h2 className="text-xl sm:text-2xl font-black text-slate-800 dark:text-white">{activeTugas ? 'Edit Tugas' : 'Buat Tugas Baru'}</h2>
          </div>
          <button
            onClick={handleSaveTugas}
            className="w-full sm:w-auto px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold shadow-md hover:bg-indigo-700 transition-colors"
          >
            Simpan Tugas
          </button>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Judul {tugasType}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                placeholder={`Misal: ${tugasType} Harian Bab 1`}
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Jenis</label>
              <select
                value={tugasType}
                onChange={(e) => setTugasType(e.target.value as TugasType)}
                className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value={TugasType.TUGAS}>Tugas</option>
                <option value={TugasType.TUGAS_NILAI_TAMBAH}>Tugas Nilai Tambah</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Target Tugas</label>
              <select
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as 'all' | 'class' | 'student')}
                className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="all">Semua Kelas</option>
                <option value="class">Kelas Tertentu</option>
                <option value="student">Siswa Tertentu</option>
              </select>
            </div>
          </div>

          {targetType === 'class' && (
            <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700">
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">Pilih Kelas</label>
              <div className="flex flex-wrap gap-2">
                {classes.map(c => (
                  <button
                    key={c}
                    onClick={() => {
                      if (selectedClasses.includes(c)) {
                        setSelectedClasses(selectedClasses.filter(cls => cls !== c));
                      } else {
                        setSelectedClasses([...selectedClasses, c]);
                      }
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      selectedClasses.includes(c) 
                        ? 'bg-indigo-600 text-white shadow-md' 
                        : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-indigo-300'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {targetType === 'student' && (
            <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Pilih Siswa</label>
                <select 
                  value={studentFilterClass}
                  onChange={(e) => setStudentFilterClass(e.target.value)}
                  className="p-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">Semua Kelas</option>
                  {classes.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="max-h-60 overflow-y-auto pr-2 space-y-2 no-scrollbar">
                {students.filter(s => studentFilterClass === 'all' || s.class === studentFilterClass).map(s => (
                  <div 
                    key={s.id}
                    onClick={() => {
                      if (selectedStudents.includes(s.id)) {
                        setSelectedStudents(selectedStudents.filter(id => id !== s.id));
                      } else {
                        setSelectedStudents([...selectedStudents, s.id]);
                      }
                    }}
                    className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                      selectedStudents.includes(s.id)
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800'
                        : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-indigo-300'
                    }`}
                  >
                    <div>
                      <p className={`font-bold ${selectedStudents.includes(s.id) ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-300'}`}>{s.name}</p>
                      <p className="text-xs text-slate-500">{s.class} • {s.nis} {s.nisn ? `| ${s.nisn}` : ''}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                      selectedStudents.includes(s.id)
                        ? 'bg-indigo-600 border-indigo-600'
                        : 'border-slate-300 dark:border-slate-600'
                    }`}>
                      {selectedStudents.includes(s.id) && <Check size={12} strokeWidth={3} className="text-white" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tugasType === TugasType.TUGAS_NILAI_TAMBAH && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 p-6 rounded-3xl border border-emerald-100 dark:border-emerald-900/30 space-y-4">
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                Pilih Tujuan Pembelajaran (TP) untuk Tambahan Nilai
              </label>
              <select
                value={extraCreditTargetTpId}
                onChange={(e) => setExtraCreditTargetTpId(e.target.value)}
                className="w-full p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                required={tugasType === TugasType.TUGAS_NILAI_TAMBAH}
              >
                <option value="">Pilih TP...</option>
                {(objectives || []).filter(obj => obj.isSummative || obj.isPracticum).map(obj => (
                  <option key={obj.id} value={obj.id}>
                    {obj.code} - {obj.description} {obj.isSummative ? '(Sumatif)' : '(Praktikum)'}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                Tugas ini terdiri dari {questions.length} soal. Setiap jawaban benar bernilai {questions[0]?.points || 10} poin (jumlah benar semua = {questions.reduce((acc, q) => acc + (q.points || 10), 0)}). Hasil akan ditambahkan ke nilai sumatif TP yang dipilih.
              </p>
            </div>
          )}

          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-6 rounded-3xl border border-indigo-100 dark:border-indigo-900/30 space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isSummative"
                checked={isSummative}
                onChange={(e) => setIsSummative(e.target.checked)}
                className="w-5 h-5 text-indigo-600 border-slate-300 rounded-lg focus:ring-indigo-500"
              />
              <label htmlFor="isSummative" className="text-sm font-bold text-slate-700 dark:text-slate-300">
                Tugas ini berkontribusi ke Nilai Sumatif TP
              </label>
            </div>

            {isSummative && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                  Pilih Tujuan Pembelajaran (TP)
                </label>
                <select
                  value={tpId}
                  onChange={(e) => setTpId(e.target.value)}
                  className="w-full p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  required={isSummative}
                >
                  <option value="">Pilih TP...</option>
                  {(objectives || []).filter(obj => obj.isSummative || obj.isPracticum).map(obj => (
                    <option key={obj.id} value={obj.id}>
                      {obj.code} - {obj.description} {obj.isSummative ? '(Sumatif)' : '(Praktikum)'}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                  Nilai dari tugas ini akan otomatis masuk ke rekapitulasi nilai sumatif untuk TP yang dipilih.
                </p>
              </div>
            )}
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="forceSequential"
                checked={forceSequential}
                onChange={(e) => setForceSequential(e.target.checked)}
                className="w-5 h-5 text-indigo-600 border-slate-300 rounded-lg focus:ring-indigo-500"
              />
              <label htmlFor="forceSequential" className="text-sm font-bold text-slate-700 dark:text-slate-300">
                Wajib Jawab Berurutan (Siswa tidak bisa melewati soal tanpa menjawab)
              </label>
            </div>
            <p className="text-xs text-slate-500 font-medium ml-8">
              Jika diaktifkan, tombol "Selanjutnya" akan dinonaktifkan sampai siswa memilih jawaban. Soal dan opsi juga akan diacak secara otomatis untuk setiap siswa.
            </p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="fullscreenRequired"
                checked={fullscreenRequired}
                onChange={(e) => setFullscreenRequired(e.target.checked)}
                className="w-5 h-5 text-indigo-600 border-slate-300 rounded-lg focus:ring-indigo-500"
              />
              <label htmlFor="fullscreenRequired" className="text-sm font-bold text-slate-700 dark:text-slate-300">
                Wajib Fullscreen (Siswa wajib masuk mode layar penuh untuk mengerjakan)
              </label>
            </div>
            <p className="text-xs text-slate-500 font-medium ml-8">
              Jika diaktifkan, siswa akan diminta untuk masuk ke mode layar penuh sebelum memulai. Jika siswa keluar dari mode layar penuh atau pindah tab, sistem akan memberikan peringatan kecurangan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Waktu Mulai</label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Waktu Selesai</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {questions.map((q, qIndex) => (
            <div key={q.id} className="bg-white dark:bg-slate-900 p-6 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 relative">
              <button 
                onClick={() => handleRemoveQuestion(qIndex)}
                className="absolute top-4 right-4 p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
              >
                <Trash2 size={18} />
              </button>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                <h3 className="font-bold text-slate-700 dark:text-slate-300">Pertanyaan {qIndex + 1}</h3>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 sm:mr-8 w-full sm:w-auto">
                  <div className="flex items-center gap-2 flex-1 sm:flex-none">
                    <label className="text-xs sm:text-sm font-bold text-slate-500">Waktu (detik):</label>
                    <input
                      type="number"
                      min="0"
                      value={q.timeLimit || ''}
                      onChange={(e) => handleQuestionChange(qIndex, 'timeLimit', e.target.value)}
                      className="w-16 px-2 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-center"
                      placeholder="∞"
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-1 sm:flex-none">
                    <label className="text-xs sm:text-sm font-bold text-slate-500">Poin:</label>
                    <input
                      type="number"
                      min="1"
                      value={q.points || 10}
                      onChange={(e) => handleQuestionChange(qIndex, 'points', e.target.value)}
                      className="w-16 px-2 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-center"
                    />
                  </div>
                  <select
                    value={q.type || 'multiple-choice'}
                    onChange={(e) => handleQuestionChange(qIndex, 'type', e.target.value)}
                    className="w-full sm:w-auto px-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="multiple-choice">Pilihan Ganda</option>
                    <option value="essay">Essai</option>
                  </select>
                </div>
              </div>
              
              <textarea
                value={q.text}
                onChange={(e) => handleQuestionChange(qIndex, 'text', e.target.value)}
                className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none mb-4 min-h-[100px]"
                placeholder="Tulis pertanyaan di sini..."
              />

              {(!q.type || q.type === 'multiple-choice') && (
                <div className="space-y-3">
                  {q.options.map((opt, optIndex) => (
                    <div key={optIndex} className="flex items-center gap-3">
                      <input
                        type="radio"
                        name={`correct-${q.id}`}
                        checked={q.correctAnswerIndex === optIndex}
                        onChange={() => handleQuestionChange(qIndex, 'correctAnswerIndex', optIndex)}
                        className="w-5 h-5 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="font-bold text-slate-500 w-6">{String.fromCharCode(65 + optIndex)}.</span>
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => handleOptionChange(qIndex, optIndex, e.target.value)}
                        className="flex-1 p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder={`Opsi ${String.fromCharCode(65 + optIndex)}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={handleAddQuestion}
            className="w-full p-4 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-3xl text-slate-500 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all flex items-center justify-center gap-2 font-bold"
          >
            <Plus size={20} />
            Tambah Manual
          </button>
          
          <div className="relative w-full">
            <input 
              type="file" 
              accept=".xlsx, .xls" 
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
              onChange={handleFileUpload}
              ref={fileInputRef}
            />
            <button
              className="w-full h-full p-4 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-3xl text-slate-500 hover:text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all flex items-center justify-center gap-2 font-bold"
            >
              <Upload size={20} />
              Upload Excel
            </button>
          </div>

          <button
            onClick={handleDownloadTemplate}
            className="w-full p-4 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-3xl text-slate-500 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all flex items-center justify-center gap-2 font-bold"
          >
            <Download size={20} />
            Download Template
          </button>
        </div>
      </div>
    );
  }

  if (viewingResults) {
    const submissions = tugasSubmissions.filter(s => s.tugasId === viewingResults.id);
    
    const availableClasses = Array.from(new Set(submissions.map(sub => {
      const student = students.find(s => s.id === sub.studentId);
      return student?.class;
    }).filter(Boolean))) as string[];

    const handleUpdateTpGrade = (student: Student, newValue: string) => {
      if (!viewingResults.tpId || !onUpdateGrades) return;
      
      const gradeId = `${student.class}::${viewingResults.tpId}::${ActivityType.SUMATIF}`;
      const existingGrade = assessmentGrades.find(g => g.id === gradeId);
      
      const numValue = parseInt(newValue);
      if (isNaN(numValue)) return;
      const finalValue = Math.min(100, Math.max(0, numValue));

      let updatedGrades: AssessmentGrade[];
      if (existingGrade) {
        const updatedGrade = {
          ...existingGrade,
          scores: { ...existingGrade.scores, [student.id]: finalValue }
        };
        updatedGrades = assessmentGrades.map(g => g.id === gradeId ? updatedGrade : g);
      } else {
        const newGrade: AssessmentGrade = {
          id: gradeId,
          class: student.class,
          tpId: viewingResults.tpId,
          activityType: ActivityType.SUMATIF,
          date: new Date().toISOString().split('T')[0],
          scores: { [student.id]: finalValue }
        };
        updatedGrades = [...assessmentGrades, newGrade];
      }
      
      onUpdateGrades(updatedGrades);
      setEditingTpGrade(null);
    };

    const handleUpdatePraktikumTpGrade = (student: Student, newValue: string) => {
      if (!viewingResults.tpId || !onUpdateGrades) return;
      
      const gradeId = `${student.class}::${viewingResults.tpId}::${ActivityType.PRAKTIKUM}`;
      const existingGrade = assessmentGrades.find(g => g.id === gradeId);
      
      const numValue = parseInt(newValue);
      if (isNaN(numValue)) return;
      const finalValue = Math.min(100, Math.max(0, numValue));

      let updatedGrades: AssessmentGrade[];
      if (existingGrade) {
        const updatedGrade = {
          ...existingGrade,
          scores: { ...existingGrade.scores, [student.id]: finalValue }
        };
        updatedGrades = assessmentGrades.map(g => g.id === gradeId ? updatedGrade : g);
      } else {
        const newGrade: AssessmentGrade = {
          id: gradeId,
          class: student.class,
          tpId: viewingResults.tpId,
          activityType: ActivityType.PRAKTIKUM,
          date: new Date().toISOString().split('T')[0],
          scores: { [student.id]: finalValue }
        };
        updatedGrades = [...assessmentGrades, newGrade];
      }
      
      onUpdateGrades(updatedGrades);
      setEditingPraktikumTpGrade(null);
    };

    const handleAddTpGrade = async (student: Student, submission: TugasSubmission, scoreType: 'pg' | 'essay', activityType: ActivityType = ActivityType.SUMATIF) => {
      if (!viewingResults.tpId || !onUpdateGrades || !onUpdateSubmission) return;
      
      const processingKey = `${submission.id}-${scoreType}`;
      if (isProcessing === processingKey) return;
      setIsProcessing(processingKey);

      try {
        // Calculate specific score
        let scoreToAdd = 0;
        if (scoreType === 'pg') {
          viewingResults.questions.forEach(q => {
            if (!q.type || q.type === 'multiple-choice') {
              if (submission.answers[q.id] === q.correctAnswerIndex) {
                scoreToAdd += (q.points || 10);
              }
            }
          });
        } else {
          viewingResults.questions.forEach(q => {
            if (q.type === 'essay') {
              scoreToAdd += (submission.essayScores?.[q.id] || 0);
            }
          });
        }

        const gradeId = `${student.class}::${viewingResults.tpId}::${activityType}`;
        const existingGrade = assessmentGrades.find(g => g.id === gradeId);
        
        const currentScore = (existingGrade && existingGrade.scores[student.id]) || 0;
        const finalValue = Math.min(100, currentScore + scoreToAdd);

        let updatedGrades: AssessmentGrade[];
        if (existingGrade) {
          const updatedGrade = {
            ...existingGrade,
            scores: { ...existingGrade.scores, [student.id]: finalValue }
          };
          updatedGrades = assessmentGrades.map(g => g.id === gradeId ? updatedGrade : g);
        } else {
          const newGrade: AssessmentGrade = {
            id: gradeId,
            class: student.class,
            tpId: viewingResults.tpId,
            activityType: activityType,
            date: new Date().toISOString().split('T')[0],
            scores: { [student.id]: finalValue }
          };
          updatedGrades = [...assessmentGrades, newGrade];
        }
        
        await onUpdateGrades(updatedGrades);
        
        // Mark submission as added
        const updatedSubmission = { ...submission };
        if (scoreType === 'pg') {
          updatedSubmission.isPgAddedToTp = true;
        } else {
          updatedSubmission.isEssayAddedToTp = true;
        }
        
        // If both are added, mark isAddedToTp as true
        const hasPg = viewingResults.questions.some(q => !q.type || q.type === 'multiple-choice');
        const hasEssay = viewingResults.questions.some(q => q.type === 'essay');
        
        if ((!hasPg || updatedSubmission.isPgAddedToTp) && (!hasEssay || updatedSubmission.isEssayAddedToTp)) {
          updatedSubmission.isAddedToTp = true;
        }
        
        await onUpdateSubmission(updatedSubmission);
      } finally {
        setIsProcessing(null);
      }
    };



    const handleExportResultsExcel = async () => {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Hasil Tugas');

      // Title & Info
      worksheet.mergeCells('A1:G1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = schoolSettings.schoolName;
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('A2:G2');
      const subTitleCell = worksheet.getCell('A2');
      subTitleCell.value = `HASIL TUGAS: ${viewingResults.title}`;
      subTitleCell.font = { bold: true, size: 12 };
      subTitleCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('A3:G3');
      const infoCell = worksheet.getCell('A3');
      infoCell.value = `Kelas: ${viewingResults.class} | Tanggal: ${new Date().toLocaleDateString('id-ID')}`;
      infoCell.alignment = { horizontal: 'center' };

      worksheet.addRow([]); // Gap

      // Headers
      const headers = ['No', 'NISN', 'Nama Siswa', 'Kelas', 'Skor PG', 'Skor Essai', 'Skor Akhir'];
      if (viewingResults.tpId) {
        headers.push('Nilai Sumatif TP');
        headers.push('Nilai Praktik TP');
      }
      
      const headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0E0E0' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Data
      filteredSubmissions.forEach((sub, idx) => {
        const student = students.find(s => s.id === sub.studentId);
        
        let pgEarned = 0;
        let pgTotal = 0;
        let essayEarned = 0;
        let essayTotal = 0;

        viewingResults.questions.forEach(q => {
          const pts = q.points || 10;
          if (!q.type || q.type === 'multiple-choice') {
            pgTotal += pts;
            if (sub.answers[q.id] === q.correctAnswerIndex) pgEarned += pts;
          } else if (q.type === 'essay') {
            essayTotal += pts;
            essayEarned += (sub.essayScores?.[q.id] || 0);
          }
        });

        const rowData = [
          idx + 1,
          student?.nisn || student?.nis || '-',
          student?.name || 'Unknown',
          student?.class || '-',
          pgTotal > 0 ? pgEarned : '-',
          essayTotal > 0 ? essayEarned : '-',
          sub.score
        ];

        if (viewingResults.tpId && student) {
          const sumatifGradeId = `${student.class}::${viewingResults.tpId}::${ActivityType.SUMATIF}`;
          const sumatifGrade = assessmentGrades.find(g => g.id === sumatifGradeId);
          rowData.push(sumatifGrade && sumatifGrade.scores[student.id] !== undefined ? sumatifGrade.scores[student.id] : '-');

          const praktikGradeId = `${student.class}::${viewingResults.tpId}::${ActivityType.PRAKTIKUM}`;
          const praktikGrade = assessmentGrades.find(g => g.id === praktikGradeId);
          rowData.push(praktikGrade && praktikGrade.scores[student.id] !== undefined ? praktikGrade.scores[student.id] : '-');
        }

        const dataRow = worksheet.addRow(rowData);
        dataRow.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });

      // Adjust column widths
      worksheet.columns.forEach(column => {
        column.width = 15;
      });
      worksheet.getColumn(3).width = 30; // Name column

      // Generate & Save
      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Hasil_Tugas_${viewingResults.title}_${viewingResults.class}.xlsx`.replace(/\s+/g, '_');
      saveAs(new Blob([buffer]), fileName);
    };

    return (
      <div className="animate-fade-in space-y-6 print:space-y-4">
        {/* Print Header */}
        <div className="print-only text-center mb-8 border-b-2 border-slate-900 pb-4">
          <h1 className="text-2xl font-black">{schoolSettings.schoolName}</h1>
          <p className="text-sm font-bold">{schoolSettings.address}</p>
          <div className="mt-6">
            <h2 className="text-xl font-black uppercase underline">Laporan Hasil Tugas</h2>
            <p className="text-lg font-bold mt-2">{viewingResults.title}</p>
            <p className="text-sm font-medium mt-1">Kelas: {viewingResults.class} | Tanggal: {new Date().toLocaleDateString('id-ID')}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 no-print">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => {
                setViewingResults(null);
                setSearchQuery('');
                setFilterClass('Semua');
              }}
              className="p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:text-indigo-600 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h2 className="text-2xl font-black text-slate-800 dark:text-white">Hasil: {viewingResults.title}</h2>
              <p className="text-sm text-slate-500">Kelas {viewingResults.class} • {submissions.length} Siswa Mengerjakan</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {onRefreshSubmissions && (
              <button
                onClick={async () => {
                  setIsRefreshing(true);
                  await onRefreshSubmissions();
                  setTimeout(() => setIsRefreshing(false), 500);
                }}
                disabled={isRefreshing}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                title="Refresh Data"
              >
                <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
                <span className="hidden sm:inline">{isRefreshing ? 'Memuat...' : 'Refresh'}</span>
              </button>
            )}
            <button
               onClick={() => handlePrintResults()}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              title="Cetak Hasil"
            >
              <Printer size={16} />
              <span className="hidden sm:inline">Cetak</span>
            </button>
            
            <button 
              onClick={handleExportResultsExcel}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-500/20"
              title="Export ke Excel"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Excel</span>
            </button>

            <input
              type="text"
              placeholder="Cari Nama / NISN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            {availableClasses.length > 0 && (
              <select
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
                className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="Semua">Semua Kelas</option>
                {availableClasses.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Print Only Table */}
        <div className="print-only">
          <table className="w-full border-collapse border border-slate-900 text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-slate-900 p-2 text-left">No</th>
                <th className="border border-slate-900 p-2 text-left">NISN</th>
                <th className="border border-slate-900 p-2 text-left">Nama Siswa</th>
                <th className="border border-slate-900 p-2 text-center">PG</th>
                <th className="border border-slate-900 p-2 text-center">Essai</th>
                <th className="border border-slate-900 p-2 text-center">Total</th>
                {viewingResults.tpId && (
                  <>
                    <th className="border border-slate-900 p-2 text-center">Sumatif TP</th>
                    <th className="border border-slate-900 p-2 text-center">Praktik TP</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((sub, idx) => {
                const student = students.find(s => s.id === sub.studentId);
                
                let pgEarned = 0;
                let pgTotal = 0;
                let essayEarned = 0;
                let essayTotal = 0;

                viewingResults.questions.forEach(q => {
                  const pts = q.points || 10;
                  if (!q.type || q.type === 'multiple-choice') {
                    pgTotal += pts;
                    if (sub.answers[q.id] === q.correctAnswerIndex) pgEarned += pts;
                  } else if (q.type === 'essay') {
                    essayTotal += pts;
                    essayEarned += (sub.essayScores?.[q.id] || 0);
                  }
                });

                const sumatifGrade = viewingResults.tpId && student ? (assessmentGrades.find(g => g.id === `${student.class}::${viewingResults.tpId}::${ActivityType.SUMATIF}`)?.scores[student.id]) : null;
                const praktikGrade = viewingResults.tpId && student ? (assessmentGrades.find(g => g.id === `${student.class}::${viewingResults.tpId}::${ActivityType.PRAKTIKUM}`)?.scores[student.id]) : null;

                return (
                  <tr key={sub.id}>
                    <td className="border border-slate-900 p-2">{idx + 1}</td>
                    <td className="border border-slate-900 p-2">{student?.nisn || student?.nis || '-'}</td>
                    <td className="border border-slate-900 p-2 font-bold">{student?.name || 'Unknown'}</td>
                    <td className="border border-slate-900 p-2 text-center">{pgTotal > 0 ? pgEarned : '-'}</td>
                    <td className="border border-slate-900 p-2 text-center">{essayTotal > 0 ? essayEarned : '-'}</td>
                    <td className="border border-slate-900 p-2 text-center font-bold">{sub.score}</td>
                    {viewingResults.tpId && (
                      <>
                        <td className="border border-slate-900 p-2 text-center font-bold">{sumatifGrade !== undefined && sumatifGrade !== null ? sumatifGrade : '-'}</td>
                        <td className="border border-slate-900 p-2 text-center font-bold">{praktikGrade !== undefined && praktikGrade !== null ? praktikGrade : '-'}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-8 flex justify-between">
            <div className="text-center w-48">
              <p className="text-sm">Mengetahui,</p>
              <p className="text-sm">Kepala Sekolah</p>
              <div className="h-20"></div>
              <p className="text-sm font-bold underline">{schoolSettings.principalName}</p>
              <p className="text-xs text-slate-600">NIP. {schoolSettings.principalNip}</p>
            </div>
            <div className="text-center w-48">
              <p className="text-sm">{schoolSettings.address.split(',')[0] || 'Kabupaten'}, {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              <p className="text-sm">Guru Mata Pelajaran</p>
              <div className="h-20"></div>
              <p className="text-sm font-bold underline">{activeUser?.name || '________________'}</p>
              {teacherNip ? <p className="text-xs text-slate-600">NIP. {teacherNip}</p> : null}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden no-print">
          {/* Mobile Card View */}
          <div className="block sm:hidden p-4 space-y-4">
            {filteredSubmissions.length > 0 ? (
              filteredSubmissions.map(sub => {
                const student = students.find(s => s.id === sub.studentId);
                
                // Calculate scores (logic duplicated from table)
                let pgEarned = 0;
                let pgTotal = 0;
                let essayEarned = 0;
                let essayTotal = 0;
                let hasEssay = false;
                let hasPg = false;
                viewingResults.questions.forEach(q => {
                  const pts = q.points || 10;
                  if (!q.type || q.type === 'multiple-choice') {
                    hasPg = true;
                    pgTotal += pts;
                    if (sub.answers[q.id] === q.correctAnswerIndex) pgEarned += pts;
                  } else if (q.type === 'essay') {
                    hasEssay = true;
                    essayTotal += pts;
                    essayEarned += (sub.essayScores?.[q.id] || 0);
                  }
                });
                const allEssaysGraded = viewingResults.questions.filter(q => q.type === 'essay' && sub.essayScores?.[q.id] !== undefined).length === viewingResults.questions.filter(q => q.type === 'essay').length;
                const pgScoreDisplay = pgTotal > 0 ? pgEarned.toString() : '-';
                const essayScoreDisplay = essayTotal > 0 ? (allEssaysGraded ? `${essayEarned} / ${essayTotal}` : `${essayEarned} / ${essayTotal} (Pending)`) : '-';

                return (
                  <div key={sub.id} className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        {student ? (
                          <button 
                            onClick={() => setViewingStudentGrades(student)}
                            className="font-bold text-indigo-600 dark:text-indigo-400 hover:underline text-left"
                          >
                            {student.name}
                          </button>
                        ) : (
                          <h4 className="font-bold text-slate-800 dark:text-white">{student?.name || 'Unknown'}</h4>
                        )}
                        <p className="text-xs text-slate-500">{student?.nisn || '-'} • {student?.class || '-'}</p>
                      </div>
                      <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        !allEssaysGraded ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400'
                      }`}>
                        {allEssaysGraded ? sub.score : 'Pending'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-center text-[10px] mt-2">
                       <div className="bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                         <div className="text-slate-500">PG</div>
                         <div className="font-bold">{pgScoreDisplay}</div>
                       </div>
                       <div className="bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                         <div className="text-slate-500">Essai</div>
                         <div className="font-bold">{essayScoreDisplay}</div>
                       </div>
                       {viewingResults.tpId && (
                         <>
                           <div className="bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                             <div className="text-slate-500">Sumatif</div>
                             <div className="font-bold">{student ? (assessmentGrades.find(g => g.id === `${student.class}::${viewingResults.tpId}::${ActivityType.SUMATIF}`)?.scores[student.id] || '-') : '-'}</div>
                           </div>
                           <div className="bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                             <div className="text-slate-500">Praktik</div>
                             <div className="font-bold text-teal-600 dark:text-teal-400">{student ? (assessmentGrades.find(g => g.id === `${student.class}::${viewingResults.tpId}::${ActivityType.PRAKTIKUM}`)?.scores[student.id] || '-') : '-'}</div>
                           </div>
                         </>
                       )}
                    </div>
                    <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                      {hasEssay && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingAnswers(sub);
                          }}
                          className="px-2 py-1 bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400 rounded-lg text-[10px] font-bold hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors"
                        >
                          Nilai Essai
                        </button>
                      )}
                      {viewingResults.tpId && student && (
                        <>
                          {hasPg && (
                            <div className="flex items-center">
                              {pgTpChoice?.submissionId === sub.id ? (
                                <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200 bg-slate-50 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                                  <span className="text-[9px] font-bold text-slate-500 uppercase mx-1">PG:</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddTpGrade(student!, sub, 'pg', ActivityType.SUMATIF);
                                      setPgTpChoice(null);
                                    }}
                                    className="px-2 py-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/50 rounded flex-1 text-[10px] font-bold transition-colors"
                                  >
                                    Sumatif
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddTpGrade(student!, sub, 'pg', ActivityType.PRAKTIKUM);
                                      setPgTpChoice(null);
                                    }}
                                    className="px-2 py-1 bg-teal-100 text-teal-700 dark:bg-teal-500/30 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-500/50 rounded flex-1 text-[10px] font-bold transition-colors"
                                  >
                                    Praktikum
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPgTpChoice(null);
                                    }}
                                    className="px-1.5 py-1 bg-rose-50 text-rose-500 dark:bg-rose-500/20 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-500/40 rounded text-[10px] font-bold transition-colors flex items-center justify-center"
                                    title="Batal"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  disabled={sub.isPgAddedToTp || sub.isAddedToTp || isProcessing === `${sub.id}-pg`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPgTpChoice({ submissionId: sub.id, student: student! });
                                  }}
                                  className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all ${
                                    sub.isPgAddedToTp || sub.isAddedToTp || isProcessing === `${sub.id}-pg`
                                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:text-slate-500'
                                      : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-400 dark:hover:bg-indigo-500/20'
                                  }`}
                                  title={sub.isPgAddedToTp || sub.isAddedToTp ? 'Skor PG sudah ditambahkan' : 'Tambahkan Skor PG ke TP'}
                                >
                                  {isProcessing === `${sub.id}-pg` ? (
                                    <Loader2 size={10} className="animate-spin" />
                                  ) : (
                                    sub.isPgAddedToTp || sub.isAddedToTp ? 'PG OK' : 'PG'
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                          {hasEssay && (
                            <div className="flex items-center">
                              {essayTpChoice?.submissionId === sub.id ? (
                                <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200 bg-slate-50 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                                  <span className="text-[9px] font-bold text-slate-500 uppercase mx-1">Jenis:</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddTpGrade(student!, sub, 'essay', ActivityType.SUMATIF);
                                      setEssayTpChoice(null);
                                    }}
                                    className="px-2 py-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/50 rounded flex-1 text-[10px] font-bold transition-colors"
                                  >
                                    Sumatif
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddTpGrade(student!, sub, 'essay', ActivityType.PRAKTIKUM);
                                      setEssayTpChoice(null);
                                    }}
                                    className="px-2 py-1 bg-teal-100 text-teal-700 dark:bg-teal-500/30 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-500/50 rounded flex-1 text-[10px] font-bold transition-colors"
                                  >
                                    Praktikum
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEssayTpChoice(null);
                                    }}
                                    className="px-1.5 py-1 bg-rose-50 text-rose-500 dark:bg-rose-500/20 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-500/40 rounded text-[10px] font-bold transition-colors flex items-center justify-center"
                                    title="Batal"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  disabled={sub.isEssayAddedToTp || sub.isAddedToTp || !allEssaysGraded || isProcessing === `${sub.id}-essay`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEssayTpChoice({ submissionId: sub.id, student: student! });
                                  }}
                                  className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all ${
                                    sub.isEssayAddedToTp || sub.isAddedToTp
                                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:text-slate-500'
                                      : !allEssaysGraded
                                        ? 'bg-slate-50 text-slate-300 cursor-not-allowed dark:bg-slate-800/50 dark:text-slate-600'
                                        : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-400 dark:hover:bg-indigo-500/20'
                                  }`}
                                  title={sub.isEssayAddedToTp || sub.isAddedToTp ? 'Skor Essai sudah ditambahkan' : (!allEssaysGraded ? 'Nilai essai belum lengkap' : 'Pilih TP untuk Skor Essai')}
                                >
                                  {sub.isEssayAddedToTp || sub.isAddedToTp ? 'Essai OK' : 'Tambah Essai'}
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmModal({
                            isOpen: true,
                            title: 'Hapus Hasil Tugas',
                            message: `Apakah Anda yakin ingin menghapus hasil tugas dari ${student?.name || 'siswa ini'}? Tindakan ini tidak dapat dibatalkan.`,
                            onConfirm: async () => {
                              setIsDeleting(true);
                              await onDeleteSubmission(sub.id);
                              setIsDeleting(false);
                              setConfirmModal(prev => ({ ...prev, isOpen: false }));
                            }
                          });
                        }}
                        className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
                        title="Hapus Hasil"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-slate-500 text-sm">Belum ada data.</p>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                  <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 hidden sm:table-cell">NISN</th>
                  <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300">Nama Siswa</th>
                  <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 hidden md:table-cell">Kelas</th>
                  <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 hidden lg:table-cell">Waktu</th>
                  <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 text-right">Skor PG</th>
                  <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 text-right">Skor Essai</th>
                  <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 text-right">Skor Akhir</th>
                  {viewingResults.tpId && (
                    <>
                      <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 text-right">Nilai Sumatif TP</th>
                      <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 text-right">Nilai Praktik TP</th>
                    </>
                  )}
                  <th className="p-2 sm:p-4 font-bold text-slate-600 dark:text-slate-300 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.length > 0 ? (
                  filteredSubmissions.map(sub => {
                    const student = students.find(s => s.id === sub.studentId);
                    
                    // Get TP Grade if applicable
                    let tpGrade = '-';
                    let tpPraktikGrade = '-';
                    if (viewingResults.tpId && student) {
                      const gradeId = `${student.class}::${viewingResults.tpId}::${ActivityType.SUMATIF}`;
                      const grade = assessmentGrades.find(g => g.id === gradeId);
                      if (grade && grade.scores[student.id] !== undefined) {
                        tpGrade = grade.scores[student.id].toString();
                      }
                      const praktikGradeId = `${student.class}::${viewingResults.tpId}::${ActivityType.PRAKTIKUM}`;
                      const praktikGrade = assessmentGrades.find(g => g.id === praktikGradeId);
                      if (praktikGrade && praktikGrade.scores[student.id] !== undefined) {
                        tpPraktikGrade = praktikGrade.scores[student.id].toString();
                      }
                    }

                    // Calculate PG and Essay scores
                    let pgEarned = 0;
                    let pgTotal = 0;
                    let essayEarned = 0;
                    let essayTotal = 0;
                    let hasEssay = false;
                    let hasPg = false;

                    viewingResults.questions.forEach(q => {
                      const pts = q.points || 10;
                      if (!q.type || q.type === 'multiple-choice') {
                        hasPg = true;
                        pgTotal += pts;
                        if (sub.answers[q.id] === q.correctAnswerIndex) {
                          pgEarned += pts;
                        }
                      } else if (q.type === 'essay') {
                        hasEssay = true;
                        essayTotal += pts;
                        essayEarned += (sub.essayScores?.[q.id] || 0);
                      }
                    });

                    let pgScoreDisplay = '-';
                    if (pgTotal > 0) {
                      pgScoreDisplay = pgEarned.toString();
                    }

                    let essayScoreDisplay = '-';
                    let allEssaysGraded = true;
                    if (essayTotal > 0) {
                      const gradedCount = viewingResults.questions.filter(q => q.type === 'essay' && sub.essayScores?.[q.id] !== undefined).length;
                      const essayCount = viewingResults.questions.filter(q => q.type === 'essay').length;
                      allEssaysGraded = gradedCount === essayCount;
                      
                      essayScoreDisplay = allEssaysGraded 
                        ? `${essayEarned} / ${essayTotal}`
                        : `${essayEarned} / ${essayTotal} (Pending)`;
                    }

                    return (
                      <tr key={sub.id} className="border-b border-slate-100 dark:border-slate-800/50 last:border-0">
                        <td className="p-2 sm:p-4 font-medium text-slate-800 dark:text-slate-200 hidden sm:table-cell">{student?.nisn || student?.nis || '-'}</td>
                        <td 
                          className={`p-2 sm:p-4 font-medium text-slate-800 dark:text-slate-200 ${student ? 'cursor-pointer hover:text-indigo-600 hover:underline' : ''}`}
                          onClick={() => {
                            if (student) {
                              setViewingStudentGrades(student);
                            }
                          }}
                        >{student?.name || 'Unknown'}</td>
                        <td className="p-2 sm:p-4 text-slate-500 hidden md:table-cell">{student?.class || '-'}</td>
                        <td className="p-2 sm:p-4 text-slate-500 hidden lg:table-cell">{new Date(sub.submittedAt).toLocaleDateString('id-ID')}</td>
                        <td className="p-2 sm:p-4 text-right font-bold text-slate-700 dark:text-slate-300">
                          {pgScoreDisplay}
                        </td>
                        <td className={`p-2 sm:p-4 text-right font-bold ${allEssaysGraded ? 'text-slate-700 dark:text-slate-300' : 'text-amber-500'}`}>
                          {essayScoreDisplay}
                        </td>
                        <td className="p-2 sm:p-4 text-right">
                          <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold ${
                            !allEssaysGraded 
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                              : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400'
                          }`}>
                            {allEssaysGraded ? sub.score : 'Pending'}
                          </span>
                        </td>
                        {viewingResults.tpId && (
                          <>
                            <td className="p-2 sm:p-4 text-right">
                              {editingTpGrade?.studentId === student?.id ? (
                                <div className="flex items-center justify-end gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    autoFocus
                                    value={editingTpGrade.value}
                                    onChange={(e) => setEditingTpGrade({ ...editingTpGrade, value: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleUpdateTpGrade(student!, editingTpGrade.value);
                                      if (e.key === 'Escape') setEditingTpGrade(null);
                                    }}
                                    className="w-16 p-1 text-right bg-white dark:bg-slate-800 border border-indigo-500 rounded-lg text-sm font-bold outline-none"
                                  />
                                  <button
                                    onClick={() => handleUpdateTpGrade(student!, editingTpGrade.value)}
                                    className="p-1 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded-lg"
                                  >
                                    <Check size={16} />
                                  </button>
                                  <button
                                    onClick={() => setEditingTpGrade(null)}
                                    className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div 
                                  onClick={() => student && setEditingTpGrade({ studentId: student.id, value: tpGrade === '-' ? '' : tpGrade })}
                                  className="font-bold text-slate-700 dark:text-slate-300 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex items-center justify-end gap-1 group"
                                >
                                  {tpGrade}
                                  <Edit2 size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              )}
                            </td>
                            <td className="p-2 sm:p-4 text-right">
                              {editingPraktikumTpGrade?.studentId === student?.id ? (
                                <div className="flex items-center justify-end gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    autoFocus
                                    value={editingPraktikumTpGrade.value}
                                    onChange={(e) => setEditingPraktikumTpGrade({ ...editingPraktikumTpGrade, value: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleUpdatePraktikumTpGrade(student!, editingPraktikumTpGrade.value);
                                      if (e.key === 'Escape') setEditingPraktikumTpGrade(null);
                                    }}
                                    className="w-16 p-1 text-right bg-white dark:bg-slate-800 border border-teal-500 rounded-lg text-sm font-bold outline-none"
                                  />
                                  <button
                                    onClick={() => handleUpdatePraktikumTpGrade(student!, editingPraktikumTpGrade.value)}
                                    className="p-1 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded-lg"
                                  >
                                    <Check size={16} />
                                  </button>
                                  <button
                                    onClick={() => setEditingPraktikumTpGrade(null)}
                                    className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div 
                                  onClick={() => student && setEditingPraktikumTpGrade({ studentId: student.id, value: tpPraktikGrade === '-' ? '' : tpPraktikGrade })}
                                  className="font-bold text-teal-600 dark:text-teal-400 cursor-pointer hover:text-teal-700 dark:hover:text-teal-300 transition-colors flex items-center justify-end gap-1 group"
                                >
                                  {tpPraktikGrade}
                                  <Edit2 size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              )}
                            </td>
                          </>
                        )}
                        <td className="p-4 text-center">
                          <div className="flex flex-wrap items-center justify-center gap-2">
                            {hasEssay && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingAnswers(sub);
                                }}
                                className="px-3 py-1.5 bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400 rounded-xl text-xs font-bold hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors"
                              >
                                Nilai Essai
                              </button>
                            )}
                            {viewingResults.tpId && student && (
                              <div className="flex items-center gap-1">
                                 {hasPg && (
                                  <div className="flex items-center">
                                    {pgTpChoice?.submissionId === sub.id ? (
                                      <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200 bg-slate-50 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase mx-1">PG:</span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddTpGrade(student!, sub, 'pg', ActivityType.SUMATIF);
                                            setPgTpChoice(null);
                                          }}
                                          className="px-3 py-1.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/50 rounded-lg text-xs font-bold transition-colors"
                                        >
                                          Sumatif
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddTpGrade(student!, sub, 'pg', ActivityType.PRAKTIKUM);
                                            setPgTpChoice(null);
                                          }}
                                          className="px-3 py-1.5 bg-teal-100 text-teal-700 dark:bg-teal-500/30 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-500/50 rounded-lg text-xs font-bold transition-colors"
                                        >
                                          Praktikum
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPgTpChoice(null);
                                          }}
                                          className="px-1.5 py-1.5 bg-rose-50 text-rose-500 dark:bg-rose-500/20 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-500/40 rounded-lg text-xs font-bold transition-colors flex items-center justify-center"
                                          title="Batal"
                                        >
                                          <X size={14} />
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        disabled={sub.isPgAddedToTp || sub.isAddedToTp || isProcessing === `${sub.id}-pg`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPgTpChoice({ submissionId: sub.id, student: student! });
                                        }}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                          sub.isPgAddedToTp || sub.isAddedToTp || isProcessing === `${sub.id}-pg`
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:text-slate-500'
                                            : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-400 dark:hover:bg-indigo-500/20'
                                        }`}
                                        title={sub.isPgAddedToTp || sub.isAddedToTp ? 'Skor PG sudah ditambahkan' : 'Tambahkan Skor PG ke TP'}
                                      >
                                        {isProcessing === `${sub.id}-pg` ? (
                                          <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                          <Plus size={14} />
                                        )}
                                        {sub.isPgAddedToTp || sub.isAddedToTp ? 'PG OK' : 'Tambah PG'}
                                      </button>
                                    )}
                                  </div>
                                )}
                                {hasEssay && (
                                  <div className="flex items-center">
                                    {essayTpChoice?.submissionId === sub.id ? (
                                      <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200 bg-slate-50 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase mx-1">Sbg:</span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddTpGrade(student!, sub, 'essay', ActivityType.SUMATIF);
                                            setEssayTpChoice(null);
                                          }}
                                          className="px-3 py-1.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/50 rounded-lg text-xs font-bold transition-colors"
                                        >
                                          Sumatif
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddTpGrade(student!, sub, 'essay', ActivityType.PRAKTIKUM);
                                            setEssayTpChoice(null);
                                          }}
                                          className="px-3 py-1.5 bg-teal-100 text-teal-700 dark:bg-teal-500/30 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-500/50 rounded-lg text-xs font-bold transition-colors"
                                        >
                                          Praktikum
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEssayTpChoice(null);
                                          }}
                                          className="px-2 py-1.5 bg-rose-50 text-rose-500 dark:bg-rose-500/20 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-500/40 rounded-lg transition-colors flex items-center justify-center"
                                          title="Batal"
                                        >
                                          <X size={14} />
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        disabled={sub.isEssayAddedToTp || sub.isAddedToTp || !allEssaysGraded || isProcessing === `${sub.id}-essay`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEssayTpChoice({ submissionId: sub.id, student: student! });
                                        }}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                          sub.isEssayAddedToTp || sub.isAddedToTp || isProcessing === `${sub.id}-essay`
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:text-slate-500'
                                            : !allEssaysGraded
                                              ? 'bg-slate-50 text-slate-300 cursor-not-allowed dark:bg-slate-800/50 dark:text-slate-600'
                                              : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-400 dark:hover:bg-indigo-500/20'
                                        }`}
                                        title={sub.isEssayAddedToTp || sub.isAddedToTp ? 'Skor Essai sudah ditambahkan' : (!allEssaysGraded ? 'Nilai essai belum lengkap' : 'Pilih TP untuk Skor Essai')}
                                      >
                                        {isProcessing === `${sub.id}-essay` ? (
                                          <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                          <Plus size={14} />
                                        )}
                                        {sub.isEssayAddedToTp || sub.isAddedToTp ? 'Essai OK' : 'Tambah Essai'}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmModal({
                                  isOpen: true,
                                  title: 'Hapus Hasil Tugas',
                                  message: `Apakah Anda yakin ingin menghapus hasil tugas dari ${student?.name || 'siswa ini'}? Tindakan ini tidak dapat dibatalkan.`,
                                  onConfirm: async () => {
                                    setIsDeleting(true);
                                    await onDeleteSubmission(sub.id);
                                    setIsDeleting(false);
                                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                                  }
                                });
                              }}
                              className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl transition-colors"
                              title="Hapus Hasil"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500">
                      {submissions.length === 0 ? 'Belum ada siswa yang mengerjakan tugas ini.' : 'Tidak ada hasil yang cocok dengan pencarian.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (viewingAllReports) {
    const classStr = reportSelectedClass === 'all' ? 'Semua Kelas' : `Kelas ${reportSelectedClass}`;
    const subjectStr = reportSelectedSubject === 'all' ? 'Semua Mapel' : reportSelectedSubject;

    const handlePrintAllReports = () => {
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

      // Generate headers for table dynamically starting with No, NIS, Nama Siswa, Kelas
      const headerCols = reportData.tasks.map(task => {
        return `<th>${task.title.toUpperCase()}</th>`;
      }).join('');

      // Generate rows
      const rowTemplates = reportData.students.map((item, idx) => {
        const isPerempuan = item.student.gender === 'Perempuan';
        
        const scoresCols = reportData.tasks.map(task => {
          const score = item.scores[task.id];
          return `<td style="font-weight: bold; font-size: 1rem;">${score !== null ? score : '-'}</td>`;
        }).join('');

        return `
          <tr>
            <td style="font-weight: bold; font-size: 1rem;">${idx + 1}</td>
            <td style="font-weight: bold; font-size: 1rem;">${item.student.nisn || item.student.nis || '-'}</td>
            <td class="name" style="font-weight: bold; font-size: 1rem; ${isPerempuan ? 'color: #e11d48;' : ''}">${item.student.name.toUpperCase()}</td>
            <td style="font-size: 1rem;">${item.student.class}</td>
            ${scoresCols}
          </tr>
        `;
      }).join('');

      // Calculate generic class stats for custom printing
      const averageClass = reportData.students.length > 0 ? Math.round(reportData.students.reduce((acc, s) => acc + s.average, 0) / reportData.students.length) : 0;
      const ketuntasanKlasikal = reportData.students.length > 0 ? Math.round((reportData.students.filter(s => s.average >= 75).length / reportData.students.length) * 100) : 0;

      printWindow.document.write(`
        <html>
          <head>
            <title>Rekap Hasil Tugas - ${classStr} - ${subjectStr}</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
              body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
              .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
              .kop-logo { width: 80px; height: 80px; object-fit: contain; }
              .kop-text { flex: 1; text-align: center; }
              .kop-gov { font-size: 14px; font-weight: bold; margin: 0; }
              .kop-dept { font-size: 14px; font-weight: bold; margin: 0; }
              .kop-school { font-size: 18px; font-weight: bold; margin: 5px 0; }
              .kop-address { font-size: 10px; font-style: italic; margin: 0; }
              .kop-additional { font-size: 10px; margin: 0; }
              .kop-line-1 { border-bottom: 3px solid black; margin-top: 10px; }
              .kop-line-2 { border-bottom: 1px solid black; margin-top: 2px; margin-bottom: 20px; }
              
              .report-title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; }
              
              .meta-info { width: 100%; margin-bottom: 20px; font-size: 12px; }
              .meta-info td { padding: 3px 0; }
              
              table.data-table { width: 100%; border-collapse: collapse; font-size: 12px; }
              table.data-table th, table.data-table td { border: 1px solid black; padding: 10px 8px; text-align: center; }
              table.data-table th { background-color: #f0f0f0; font-weight: bold; }
              table.data-table td.name { text-align: left; padding-left: 15px; }
              
              .summary-card-container { display: flex; gap: 15px; margin-top: 25px; margin-bottom: 25px; }
              .summary-card { flex: 1; border: 1px solid #ccc; border-radius: 8px; padding: 10px; text-align: center; }
              .summary-title { font-size: 10px; text-transform: uppercase; font-weight: bold; color: #666; margin-bottom: 5px; }
              .summary-val { font-size: 18px; font-weight: bold; color: #1e1b4b; }

              .signature-area { margin-top: 40px; width: 100%; font-size: 12px; }
              .signature-right { margin-left: auto; text-align: center; width: 250px; }
              .signature-name { font-weight: bold; text-decoration: underline; }
              
              @media print {
                @page { size: portrait; margin: 1cm; }
              }
            </style>
          </head>
          <body>
            <div class="kop-container">
              <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
              <div class="kop-text">
                <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
                <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
                <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
                <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
                ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
              </div>
              <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
            </div>
            <div class="kop-line-1"></div>
            <div class="kop-line-2"></div>
   
            <div class="report-title">LAPORAN REKAPITULASI HASIL TUGAS / ASSIGNMENT RECAP REPORT</div>
   
            <table class="meta-info">
              <tr>
                <td style="width: 25%; font-weight: bold;">Mata Pelajaran</td><td style="width: 2%">:</td><td>${subjectStr.toUpperCase()}</td>
              </tr>
              <tr>
                <td style="font-weight: bold;">Kelas / Rombel</td><td>:</td><td>${classStr}</td>
              </tr>
              <tr>
                <td style="font-weight: bold;">Nama Guru</td><td>:</td><td>${activeUser?.name || '-'}</td>
              </tr>
              <tr>
                <td style="font-weight: bold;">Tanggal Cetak</td><td>:</td><td>${today}</td>
              </tr>
            </table>
   
            <table class="data-table">
              <thead>
                <tr>
                  <th style="width: 60px;">NO</th>
                  <th style="width: 120px;">NISN</th>
                  <th>NAMA SISWA</th>
                  <th style="width: 100px;">KELAS</th>
                  ${headerCols}
                </tr>
              </thead>
              <tbody>
                ${rowTemplates.length > 0 ? rowTemplates : `<tr><td colspan="${4 + reportData.tasks.length}" style="padding: 20px; font-weight: bold; color: #666;">Belum ada siswa yang mengerjakan tugas.</td></tr>`}
              </tbody>
            </table>

            <div class="summary-card-container">
              <div class="summary-card">
                <div class="summary-title">RATA-RATA KELAS</div>
                <div class="summary-val">${averageClass}</div>
              </div>
              <div class="summary-card">
                <div class="summary-title">KETUNTASAN KLASIKAL</div>
                <div class="summary-val">${ketuntasanKlasikal}%</div>
              </div>
              <div class="summary-card">
                <div class="summary-title">TOTAL EVALUASI</div>
                <div class="summary-val">${reportData.tasks.length} Tugas</div>
              </div>
            </div>

            <div class="signature-area">
              <div class="signature-right">
                ${schoolSettings.address.split(',')[0] || 'Ciamis'}, ${today}<br/>
                Guru Mata Pelajaran,<br/><br/><br/><br/><br/>
                <span class="signature-name">${activeUser?.name || '________________'}</span><br/>
                ${teacherNip ? `<span>NIP. ${teacherNip}</span>` : ''}
              </div>
            </div>
            
            <script>
              window.onload = () => {
                window.print();
                setTimeout(() => window.close(), 500);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    };

    const handleExportAllResultsExcel = async () => {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Rekap Tugas');

      // Title & Info
      const maxColumns = 4 + reportData.tasks.length;
      
      const colLetter = (colIndex: number) => {
        let temp, letter = '';
        while (colIndex > 0) {
          temp = (colIndex - 1) % 26;
          letter = String.fromCharCode(65 + temp) + letter;
          colIndex = Math.floor((colIndex - temp) / 26);
        }
        return letter;
      };

      const lastColLetter = colLetter(maxColumns);

      worksheet.mergeCells(`A1:${lastColLetter}1`);
      const titleCell = worksheet.getCell('A1');
      titleCell.value = schoolSettings.schoolName;
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells(`A2:${lastColLetter}2`);
      const subTitleCell = worksheet.getCell('A2');
      subTitleCell.value = `REKAPITULASI HASIL TUGAS MATA PELAJARAN`;
      subTitleCell.font = { bold: true, size: 12 };
      subTitleCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells(`A3:${lastColLetter}3`);
      const infoCell = worksheet.getCell('A3');
      infoCell.value = `Filter: ${classStr} | Mapel: ${subjectStr} | Tanggal Cetak: ${new Date().toLocaleDateString('id-ID')}`;
      infoCell.alignment = { horizontal: 'center' };

      worksheet.addRow([]); // Blank line

      // Headers
      const headers = ['No', 'NISN', 'Nama Siswa', 'Kelas'];
      reportData.tasks.forEach(task => {
        headers.push(task.title);
      });

      const headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0E0E0' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      });

      // Data Rows
      reportData.students.forEach((item, idx) => {
        const rowData = [
          idx + 1,
          item.student.nisn || item.student.nis || '-',
          item.student.name,
          item.student.class,
        ];

        reportData.tasks.forEach(task => {
          const score = item.scores[task.id];
          rowData.push(score !== null ? score : '-');
        });

        const dataRow = worksheet.addRow(rowData);
        dataRow.eachCell((cell, cIdx) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          if (cIdx > 4) {
            cell.alignment = { horizontal: 'center' };
          }
        });
      });

      // Autosize column widths
      worksheet.columns.forEach((column, index) => {
        if (index === 2) {
          column.width = 32; // Nama Siswa
        } else if (index >= 4 && index < 4 + reportData.tasks.length) {
          column.width = 18; // Tugas columns
        } else {
          column.width = 12;
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Rekap_Semua_Hasil_Tugas_${classStr}_${subjectStr}.xlsx`.replace(/\s+/g, '_');
      saveAs(new Blob([buffer]), fileName);
    };

    return (
      <div className="animate-fade-in space-y-6 print:space-y-4">
        {/* Print Header */}
        <div className="print-only text-center mb-8 border-b-2 border-slate-900 pb-4">
          <h1 className="text-2xl font-black">{schoolSettings.schoolName}</h1>
          <p className="text-sm font-bold">{schoolSettings.address}</p>
          <div className="mt-6">
            <h2 className="text-xl font-black uppercase underline">Laporan Hasil Tugas (Semua Tugas)</h2>
            <p className="text-sm font-medium mt-1">Kelas: {classStr} | Mata Pelajaran: {subjectStr} | Tanggal Cetak: {new Date().toLocaleDateString('id-ID')}</p>
          </div>
        </div>

        {/* Filter & Action Panel */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 no-print p-6 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => {
                setViewingAllReports(false);
                setReportSelectedClass('all');
                setReportSelectedSubject('all');
              }}
              className="p-2 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700/80 rounded-xl text-slate-600 dark:text-slate-400 hover:text-indigo-600 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h2 className="text-xl font-black text-slate-800 dark:text-white">Laporan Rekap Semua Tugas</h2>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-0.5">Analisis hasil belajar kolektif siswa</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Class filter selection */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-black uppercase tracking-wider text-slate-400">Kelas:</span>
              <select
                value={reportSelectedClass}
                onChange={(e) => setReportSelectedClass(e.target.value)}
                className="px-3 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold border border-slate-200 dark:border-slate-700 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">Semua Kelas</option>
                {classes.map(c => (
                  <option key={c} value={c}>Kelas {c}</option>
                ))}
              </select>
            </div>

            {/* Subject filter selection */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-black uppercase tracking-wider text-slate-400">Mapel:</span>
              <select
                value={reportSelectedSubject}
                onChange={(e) => setReportSelectedSubject(e.target.value)}
                className="px-3 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold border border-slate-200 dark:border-slate-700 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">Semua Mapel</option>
                {teacherSubjects.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <button
              onClick={handlePrintAllReports}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-colors shadow-sm"
              title="Cetak"
            >
              <Printer size={14} />
              <span>Cetak</span>
            </button>
            
            <button 
              onClick={handleExportAllResultsExcel}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-emerald-700 transition-colors shadow-sm"
              title="Export ke Excel"
            >
              <Download size={14} />
              <span>Excel</span>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm p-6 sm:p-8 print:p-0 print:border-none print:shadow-none print:bg-white">
          
          {/* Header display inside main area for print preview correctness */}
          <div className="hidden sm:block print:hidden mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
            <h3 className="text-lg font-black text-slate-800 dark:text-white flex items-center gap-2">
              <ClipboardList className="text-indigo-500" size={20} />
              {schoolSettings.schoolName}
            </h3>
            <p className="text-xs text-slate-500 font-medium mt-1">Rekap Laporan Nilai - {classStr} • Mapel: {subjectStr}</p>
          </div>

          <div className="overflow-x-auto lg:overflow-x-visible rounded-3xl border border-slate-200 dark:border-slate-800 print:overflow-visible print:border-slate-300">
            <table className="w-full text-left border-collapse text-xs table-auto">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 print:bg-slate-100 text-slate-600 dark:text-slate-400 print:text-black font-black uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 print:border-slate-300">
                  <th className="p-3 text-center w-12 border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300 border-b border-slate-200 dark:border-slate-850">No</th>
                  <th className="p-3 w-24 border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300 border-b border-slate-200 dark:border-slate-850">NISN</th>
                  <th className="p-3 w-48 border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300 border-b border-slate-200 dark:border-slate-850">Nama Siswa</th>
                  <th className="p-3 w-16 text-center border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300 border-b border-slate-200 dark:border-slate-850">Kelas</th>
                  
                  {reportData.tasks.length > 0 ? (
                    reportData.tasks.map(task => (
                      <th key={task.id} className="p-3 text-center min-w-[70px] border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300 border-b border-slate-200 dark:border-slate-850">
                        <div className="line-clamp-2 font-bold mb-0.5 max-w-[130px]" title={task.title}>{task.title}</div>
                        {task.isSummative && <span className="inline-block text-[8px] bg-amber-500/10 text-amber-500 dark:text-amber-400 px-1 py-0.5 rounded uppercase tracking-wider font-extrabold font-mono">Sumatif</span>}
                      </th>
                    ))
                  ) : (
                    <th className="p-3 text-center border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300 border-b border-slate-200 dark:border-slate-850">Belum Ada Tugas</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800 print:divide-slate-300">
                {reportData.students.length > 0 ? (
                  reportData.students.map((item, idx) => (
                    <tr 
                      key={item.student.id} 
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-850/20 transition-colors print:hover:bg-transparent text-slate-800 dark:text-slate-200"
                    >
                      <td className="p-3 text-center font-bold text-slate-500 print:text-black border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300">{idx + 1}</td>
                      <td className="p-3 font-mono text-slate-600 dark:text-slate-400 print:text-black border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300">{item.student.nisn || item.student.nis || '-'}</td>
                      <td className="p-3 font-black text-slate-850 dark:text-white print:text-black border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300">{item.student.name}</td>
                      <td className="p-3 text-center font-bold text-slate-600 dark:text-slate-300 print:text-black border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300">{item.student.class}</td>
                      
                      {reportData.tasks.length > 0 ? (
                        reportData.tasks.map(task => {
                          const score = item.scores[task.id];
                          return (
                             <td 
                              key={task.id} 
                              className="p-3 text-center font-black border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300"
                            >
                              {score !== null ? (
                                <span className={score >= 75 ? 'text-emerald-600 dark:text-emerald-400 print:text-black' : 'text-rose-500 dark:text-rose-400 print:text-black'}>
                                  {score}
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-755 print:text-slate-400">--</span>
                              )}
                            </td>
                          );
                        })
                      ) : (
                        <td className="p-3 text-center text-slate-400 border-r border-slate-200 dark:border-slate-800/50 print:border-slate-300">-</td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4 + (reportData.tasks.length || 1)} className="p-12 text-center text-slate-400 font-bold">
                      Tidak ada siswa yang cocok atau belum ada data tugas yang dibuat.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Report Statistics Recap under table */}
          {reportData.students.length > 0 && (
            <div className="grid grid-cols-3 gap-6 mt-8 p-6 bg-slate-50 dark:bg-slate-800/40 rounded-3xl border border-slate-100 dark:border-slate-800/50 print:border-slate-300 print:bg-transparent print:p-0 print:border-none">
              <div className="text-center sm:text-left print:border print:border-slate-300 print:p-3 print:rounded-xl">
                <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 print:text-black uppercase tracking-widest block mb-1">Rata-rata Kelas</span>
                <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400 print:text-black">
                  {reportData.students.length > 0 ? Math.round(reportData.students.reduce((acc, s) => acc + s.average, 0) / reportData.students.length) : 0}
                </span>
              </div>
              <div className="text-center sm:text-left border-x border-slate-200 dark:border-slate-800 px-6 print:border print:border-slate-300 print:p-3 print:rounded-xl">
                <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 print:text-black uppercase tracking-widest block mb-1">Ketuntasan Klasikal</span>
                <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400 print:text-black">
                  {reportData.students.length > 0 ? Math.round((reportData.students.filter(s => s.average >= 75).length / reportData.students.length) * 100) : 0}%
                </span>
              </div>
              <div className="text-center sm:text-left print:border print:border-slate-300 print:p-3 print:rounded-xl">
                <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 print:text-black uppercase tracking-widest block mb-1">Total Partisipasi Tugas</span>
                <span className="text-2xl font-black text-amber-500 dark:text-amber-400 print:text-black">
                  {reportData.tasks.length} / {reportData.tasks.length} Modul
                </span>
              </div>
            </div>
          )}

          {/* Signature Block (print only) */}
          <div className="hidden print:block mt-16 text-right text-sm">
            <div className="inline-block text-center mr-12">
              <p>Bandung, {new Date().toLocaleDateString('id-ID', { dateStyle: 'long' })}</p>
              <p className="font-bold">Guru Mata Pelajaran</p>
              <div className="h-20"></div>
              <p className="font-bold underline">{activeUser?.name || '_________________________'}</p>
              {teacherNip ? <p className="text-xs text-slate-500">NIP. {teacherNip}</p> : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8 pb-32">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white tracking-tight">
              Tugas
            </h2>
            <p className="text-sm text-slate-500 font-bold">Kelola dan kerjakan tugas untuk perbaikan Nilai</p>
          </div>

          <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-2xl w-full sm:w-auto">
            <button
              onClick={() => setActiveTab('beranda')}
              className={`flex-1 sm:flex-none px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                activeTab === 'beranda'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              Beranda
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`flex-1 sm:flex-none px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                activeTab === 'list'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              Daftar Tugas
            </button>
            {role === UserRole.Guru && (
              <button
                onClick={() => setActiveTab('course')}
                className={`flex-1 sm:flex-none px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                  activeTab === 'course'
                    ? 'bg-white dark:bg-slate-900 text-indigo-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                Course
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {role === UserRole.Guru && activeTab === 'list' && (
            <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
              <button
                onClick={() => setViewingAllReports(true)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 rounded-2xl font-black shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-all text-xs uppercase tracking-widest border border-slate-200 dark:border-slate-800"
              >
                <Printer size={16} />
                <span>Cetak Semua Laporan</span>
              </button>
              <button
                onClick={handleCreateNew}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 transition-all hover:scale-105"
              >
                <Plus size={20} />
                Buat Tugas
              </button>
            </div>
          )}
          {role === UserRole.Guru && activeTab === 'course' && (
            <button
              onClick={handleOpenCreateModule}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-2xl font-bold shadow-lg shadow-emerald-500/30 hover:bg-emerald-700 transition-all hover:scale-105"
            >
              <Plus size={20} />
              Tambah Modul
            </button>
          )}
        </div>
      </div>

      {activeTab === 'beranda' && (
        <div className="space-y-6">
          {role === UserRole.Guru && (
            <div className="grid grid-cols-1 gap-6">
              <RealtimeActivityBoard 
                tugasSessions={tugasSessions} 
                onStudentClick={(studentId) => {
                  if (!studentId) return;
                  const student = (students || []).find(s => s.id === studentId || s.nis === studentId);
                  if (student) {
                    setViewingStudentGrades(student);
                  } else {
                    // Fallback to searching by session data if we can find the session
                    const session = (tugasSessions || []).find(s => s.studentId === studentId);
                    if (session) {
                      const studentByName = (students || []).find(s => s.name === session.studentName);
                      if (studentByName) setViewingStudentGrades(studentByName);
                    }
                  }
                }}
              />

              {/* Stats Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Tugas</p>
                   <p className="text-2xl font-black text-slate-800 dark:text-white">{teacherTugas.length}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Submission</p>
                   <p className="text-2xl font-black text-slate-800 dark:text-white">{tugasSubmissions.filter(s => teacherTugas.some(t => t.id === s.tugasId)).length}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tugas Aktif</p>
                   <p className="text-2xl font-black text-emerald-600">{teacherTugas.filter(t => t.isActive).length}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tugas Selesai</p>
                   <p className="text-2xl font-black text-slate-400">{teacherTugas.filter(t => t.endTime && new Date(t.endTime) < new Date()).length}</p>
                </div>
              </div>
            </div>
          )}

          {role === UserRole.Siswa && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
                <h4 className="text-lg font-black text-slate-800 dark:text-white mb-6 flex items-center gap-2">
                   <Star size={20} className="text-amber-500" />
                   Ringkasan Tugas Anda
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl">
                    <span className="text-sm font-bold text-slate-600 dark:text-slate-400">Tugas Selesai</span>
                    <span className="text-xl font-black text-indigo-600">{tugasSubmissions.filter(s => s.studentId === activeUser.id).length}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl">
                    <span className="text-sm font-bold text-slate-600 dark:text-slate-400">Tugas Tersedia</span>
                    <span className="text-xl font-black text-emerald-600">{studentTugas.filter(t => !tugasSubmissions.some(s => s.tugasId === t.id && s.studentId === activeUser.id)).length}</span>
                  </div>
                  <button 
                    onClick={() => setActiveTab('list')}
                    className="w-full py-3 bg-indigo-600 text-white font-black rounded-xl shadow-lg shadow-indigo-500/20 hover:scale-105 transition-transform"
                  >
                    Lihat Semua Tugas
                  </button>
                </div>
              </div>
              
              <div className="bg-indigo-600 p-8 rounded-[2.5rem] shadow-xl text-white relative overflow-hidden flex flex-col justify-center">
                 <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
                 <h4 className="text-xl font-black mb-2 relative z-10">Siap Belajar?</h4>
                 <p className="text-indigo-100 font-medium mb-6 relative z-10 leading-relaxed">Kerjakan tugas dengan jujur dan teliti untuk hasil yang maksimal.</p>
                 <div className="flex items-center gap-2 text-indigo-200 text-sm font-bold relative z-10">
                   <Activity size={16} />
                   <span>Periksa tugas baru secara berkala</span>
                 </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'course' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(courseModules || []).filter(m => role === UserRole.Guru ? (m.teacherId === activeUser.id || m.teacherId === 'system') : m.targetClasses.includes(activeUser.class)).length === 0 ? (
            <div className="col-span-full py-20 text-center bg-white dark:bg-slate-900 rounded-[2.5rem] border border-dashed border-slate-200 dark:border-slate-800">
              <ClipboardList size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Belum ada modul kursus</p>
            </div>
          ) : (
            (courseModules || []).filter(m => role === UserRole.Guru ? (m.teacherId === activeUser.id || m.teacherId === 'system') : m.targetClasses.includes(activeUser.class)).map((module, idx) => (
              <div key={module.id} className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all group flex flex-col">
                <div className="flex justify-between items-start mb-4">
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 rounded-2xl">
                    <BookOpen size={24} />
                  </div>
                  {role === UserRole.Guru && (
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEditModule(module)} className="p-2 text-slate-400 hover:text-indigo-600 transition-colors">
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={() => {
                          setConfirmModal({
                            isOpen: true,
                            title: 'Hapus Modul',
                            message: `Apakah Anda yakin ingin menghapus modul "${module.title}"?`,
                            onConfirm: async () => {
                              setIsDeleting(true);
                              await onDeleteCourseModule?.(module.id);
                              setIsDeleting(false);
                              setConfirmModal(prev => ({ ...prev, isOpen: false }));
                            }
                          });
                        }} 
                        className="p-2 text-slate-400 hover:text-rose-600 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <h4 className="text-lg font-black text-slate-800 dark:text-white mb-2">{module.title}</h4>
                <p className="text-sm text-slate-500 font-medium mb-4 line-clamp-2">{module.description}</p>
                
                <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <div className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-[10px] font-black text-slate-600 dark:text-slate-400 rounded-lg uppercase tracking-widest">
                        {module.chapters.length} Bab
                      </div>
                    </div>
                    {role === UserRole.Siswa && (
                      <div className="flex flex-col gap-2 mt-2">
                        {(() => {
                          const submissions = courseSubmissions.filter(s => s.moduleId === module.id && s.studentId === activeUser.id);
                          const totalChaptersWithQuiz = module.chapters.filter(ch => ch.quiz?.length > 0).length;
                          const completedChapters = submissions.length;
                          const averageScore = submissions.length > 0 
                            ? Math.round(submissions.reduce((acc, s) => acc + s.score, 0) / submissions.length)
                            : 0;
                          
                          return (
                            <div className="space-y-3">
                              {submissions.length > 0 && (
                                <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-500/5 rounded-2xl border border-emerald-100 dark:border-emerald-500/10">
                                  <div className="flex flex-col">
                                    <span className="text-[8px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest leading-none mb-1">Skor Rata-rata</span>
                                    <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400 tabular-nums">{averageScore}</span>
                                  </div>
                                  <div className="h-8 w-[1px] bg-emerald-100 dark:bg-emerald-500/20" />
                                  <div className="flex flex-col">
                                    <span className="text-[8px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest leading-none mb-1">Progress</span>
                                    <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">{completedChapters}/{totalChaptersWithQuiz}</span>
                                  </div>
                                </div>
                              )}
                              {submissions.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 px-1">
                                  {submissions.map(s => {
                                    const chIdx = module.chapters.findIndex(c => c.id === s.chapterId);
                                    return (
                                      <div key={s.id} className="flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 shadow-sm">
                                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-tighter">B{chIdx + 1}</span>
                                        <span className="text-xs font-black text-emerald-500 tabular-nums">{s.score}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {submissions.length === 0 && (
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                                  <p className="text-[10px] font-bold text-slate-400 italic">Belum ada kuis yang dikerjakan.</p>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button 
                      onClick={() => handleOpenModule(module)}
                      className="flex items-center gap-2 text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest hover:gap-3 transition-all"
                    >
                      Buka Modul
                      <ChevronRight size={14} />
                    </button>
                    <button 
                      onClick={() => setViewingCourseResults(module)}
                      className="flex items-center gap-1.5 text-[10px] font-black text-slate-400 hover:text-emerald-500 uppercase tracking-widest transition-all"
                    >
                      Lihat Hasil
                      <Eye size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'list' && (
        <div className="space-y-6">
          {role === UserRole.Siswa && (
            <div className="bg-white dark:bg-slate-900 px-5 py-4 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 mb-6">
              <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
                <Award size={16} className="text-amber-500" />
                Nilai Sumatif & Praktikum
              </h3>
              
              {(() => {
                // 1. Only get objectives defined in the teacher's Tujuan Pembelajaran (objectives) that have Sumatif or Praktikum enabled
                const tpList = (objectives || [])
                  .filter(tp => {
                    // Must have either Sumatif or Praktikum checked
                    const hasSummativeOrPracticum = Boolean(tp.isSummative) || Boolean(tp.isPracticum);
                    if (!hasSummativeOrPracticum) return false;

                    if (selectedSubjectForStudent && tp.subject) {
                      return tp.subject.trim().toLowerCase() === selectedSubjectForStudent.trim().toLowerCase();
                    }
                    return true;
                  })
                  .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));

                if (tpList.length === 0) {
                  return (
                    <div className="py-8 flex flex-col items-center justify-center text-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                      <ClipboardList className="mx-auto text-slate-300 mb-2" size={32} />
                      <p className="text-xs font-bold text-slate-400 italic">
                        {selectedSubjectForStudent 
                          ? `Belum ada TP Sumatif atau Praktikum untuk ${selectedSubjectForStudent}`
                          : "Belum ada TP Sumatif atau Praktikum yang ditentukan"}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tpList.map((tp, idx) => {
                      // Lookup Sumatif grade
                      let sumatifScore: number | null = null;
                      let sumatifDate: string | null = null;

                      // Lookup Praktikum grade
                      let praktikumScore: number | null = null;
                      let praktikumDate: string | null = null;

                      // 1. From assessmentGrades
                      const relevantGrades = (assessmentGrades || []).filter(
                        g => g.class === activeUser.class && g.tpId === tp.id
                      );

                      relevantGrades.forEach(g => {
                        const rawScore = (g.scores || {})[activeUser.id];
                        const hasScore = rawScore !== undefined && rawScore !== null && !isNaN(Number(rawScore));
                        const numScore = hasScore ? Number(rawScore) : null;

                        const isSumatifType = g.activityType === ActivityType.SUMATIF || 
                                              g.assessmentType === 'Sumatif per TP' || 
                                              g.assessmentType?.toLowerCase().includes('sumatif');
                        const isPraktikumType = g.activityType === ActivityType.PRAKTIKUM || 
                                                g.assessmentType === 'Praktikum TP' || 
                                                g.assessmentType?.toLowerCase().includes('praktik');

                        if (isSumatifType) {
                          if (numScore !== null && (sumatifScore === null || new Date(g.date) >= new Date(sumatifDate || 0))) {
                            sumatifScore = numScore;
                            sumatifDate = g.date;
                          }
                        } else if (isPraktikumType) {
                          if (numScore !== null && (praktikumScore === null || new Date(g.date) >= new Date(praktikumDate || 0))) {
                            praktikumScore = numScore;
                            praktikumDate = g.date;
                          }
                        }
                      });

                      // 2. From formativeGradesTP (recap)
                      const fg = (formativeGradesTP || []).find(
                        f => f.class === activeUser.class && f.tpId === tp.id
                      );
                      if (fg) {
                        const fSum = fg.summativeScores?.[activeUser.id];
                        if (fSum !== undefined && fSum !== null && !isNaN(Number(fSum)) && sumatifScore === null) {
                          sumatifScore = Number(fSum);
                          sumatifDate = 'Rekap';
                        }
                        const fPrak = fg.praktikumScores?.[activeUser.id];
                        if (fPrak !== undefined && fPrak !== null && !isNaN(Number(fPrak)) && praktikumScore === null) {
                          praktikumScore = Number(fPrak);
                          praktikumDate = 'Rekap';
                        }
                      }

                      // Determine which blocks to show based strictly on teacher's TP configuration:
                      const showPraktikum = Boolean(tp.isPracticum);
                      const showSumatif = Boolean(tp.isSummative);

                      return (
                        <div key={tp.id || idx} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col group hover:border-amber-500/30 transition-all">
                          <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200 dark:border-slate-700/50">
                            <div className="flex items-center gap-2">
                              <h4 className="text-[11px] font-black text-slate-800 dark:text-white uppercase tracking-wider">
                                {tp.code || 'TP'}
                              </h4>
                              {tp.element && (
                                <span className="text-[9px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded-md border border-indigo-100 dark:border-indigo-900/40 truncate max-w-[100px]" title={tp.element}>
                                  {tp.element}
                                </span>
                              )}
                            </div>
                            <span className="text-[9px] font-bold text-slate-400 truncate max-w-[150px]" title={tp.scope || tp.description}>
                              {tp.scope || tp.description || 'Tujuan Pembelajaran'}
                            </span>
                          </div>

                          <div className="flex items-center gap-6">
                            {showSumatif && (
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-[9px] font-black text-slate-400 tracking-wider uppercase">Sumatif</span>
                                  <span className="text-[8px] font-bold text-slate-400">
                                    {sumatifScore !== null ? (sumatifDate === 'Rekap' ? 'REKAP' : sumatifDate) : '-'}
                                  </span>
                                </div>
                                <div className={`text-2xl font-black ${sumatifScore !== null ? (sumatifScore >= 75 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400') : 'text-slate-400 dark:text-slate-500 font-bold'}`}>
                                  {sumatifScore !== null ? sumatifScore : '-'}
                                </div>
                              </div>
                            )}
                            
                            {showSumatif && showPraktikum && (
                              <div className="w-px h-10 bg-slate-200 dark:bg-slate-700/50"></div>
                            )}
                            
                            {showPraktikum && (
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-[9px] font-black text-slate-400 tracking-wider uppercase">Praktikum</span>
                                  <span className="text-[8px] font-bold text-slate-400">
                                    {praktikumScore !== null ? (praktikumDate === 'Rekap' ? 'REKAP' : praktikumDate) : '-'}
                                  </span>
                                </div>
                                <div className={`text-2xl font-black ${praktikumScore !== null ? (praktikumScore >= 75 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400') : 'text-slate-400 dark:text-slate-500 font-bold'}`}>
                                  {praktikumScore !== null ? praktikumScore : '-'}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {role === UserRole.Guru ? (
            teacherTugas.length > 0 ? (
              teacherTugas.map((tugasItem, index) => {
                const gradients = [
                  'from-indigo-600 via-indigo-700 to-slate-900',
                  'from-blue-600 via-blue-700 to-slate-900',
                  'from-emerald-600 via-emerald-700 to-slate-900',
                  'from-purple-600 via-purple-700 to-slate-900',
                  'from-rose-600 via-rose-700 to-slate-900',
                  'from-amber-600 via-amber-700 to-slate-900'
                ];
                const gradient = gradients[index % gradients.length];
                return (
                  <div key={tugasItem.id} className={`bg-gradient-to-br ${gradient} p-7 rounded-[2rem] flex flex-col h-full shadow-xl shadow-indigo-500/5 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border border-white/10 relative overflow-hidden group`}>
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-all pointer-events-none"></div>
                    <div className="flex justify-between items-start mb-5 relative z-10">
                      <div className="p-3.5 bg-white/15 backdrop-blur-md rounded-2xl text-white shadow-inner flex items-center justify-center">
                        <ClipboardList size={26} />
                      </div>
                      <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-md p-1.5 rounded-2xl border border-white/10">
                        <button
                          onClick={() => onSaveTugas({ ...tugasItem, isActive: !tugasItem.isActive })}
                          className={`p-2 rounded-xl transition-all ${tugasItem.isActive ? 'text-emerald-300 bg-emerald-500/20 shadow-sm' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
                          title={tugasItem.isActive ? `Nonaktifkan Tugas` : `Aktifkan Tugas`}
                        >
                          {tugasItem.isActive ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        </button>
                        <button 
                          onClick={() => handleEditTugas(tugasItem)}
                          className="p-2 text-white/70 hover:text-white hover:bg-white/15 rounded-xl transition-all"
                          title="Edit Tugas"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button 
                          onClick={() => handleDuplicateTugas(tugasItem)}
                          className="p-2 text-white/70 hover:text-white hover:bg-white/15 rounded-xl transition-all"
                          title="Duplikasi Tugas"
                        >
                          <Copy size={18} />
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmModal({
                              isOpen: true,
                              title: `Hapus Tugas`,
                              message: `Apakah Anda yakin ingin menghapus tugas "${tugasItem.title}"? Semua data hasil pengerjaan siswa untuk tugas ini juga akan terhapus.`,
                              onConfirm: async () => {
                                setIsDeleting(true);
                                await onDeleteTugas(tugasItem.id);
                                setIsDeleting(false);
                                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                              }
                            });
                          }}
                          className="p-2 text-white/70 hover:text-rose-300 hover:bg-rose-500/20 rounded-xl transition-all"
                          title="Hapus Tugas"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-3 relative z-10">
                      {tugasItem.isSummative && (
                        <span className="px-3 py-1 bg-amber-500/30 backdrop-blur-md text-amber-200 text-[10px] font-black uppercase tracking-widest rounded-full border border-amber-400/30">
                          Sumatif
                        </span>
                      )}
                      <span className="px-3 py-1 bg-white/10 backdrop-blur-md text-white/90 text-[10px] font-black uppercase tracking-widest rounded-full border border-white/10">
                        {tugasItem.type}
                      </span>
                    </div>

                    <h3 className="text-xl font-black text-white mb-2 tracking-tight leading-snug relative z-10">{tugasItem.title}</h3>
                    
                    <div className="text-xs text-white/80 font-medium mb-4 relative z-10 flex items-center gap-2">
                      <span className="bg-white/15 px-2.5 py-1 rounded-lg font-bold">
                        {tugasItem.targetStudents && tugasItem.targetStudents.length > 0 
                          ? `${tugasItem.targetStudents.length} Siswa Tertentu` 
                          : tugasItem.targetClasses && tugasItem.targetClasses.length > 0 
                            ? `Kelas ${tugasItem.targetClasses.join(', ')}` 
                            : `Kelas ${tugasItem.class}`}
                      </span>
                      <span>•</span>
                      <span className="bg-white/15 px-2.5 py-1 rounded-lg font-bold">
                        {tugasItem.questions.length} Soal
                      </span>
                    </div>

                    {tugasItem.targetStudents && tugasItem.targetStudents.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4 relative z-10">
                        {tugasItem.targetStudents.slice(0, 3).map(sid => {
                          const student = students.find(s => s.id === sid);
                          return student ? (
                            <span key={sid} className="px-2.5 py-1 bg-white/20 rounded-lg text-[10px] font-bold text-white shadow-sm">
                              {student.name.split(' ')[0]}
                            </span>
                          ) : null;
                        })}
                        {tugasItem.targetStudents.length > 3 && (
                          <button 
                            onClick={() => setViewingAllStudents(tugasItem)}
                            className="px-2.5 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-[10px] font-bold text-white transition-all shadow-sm"
                          >
                            +{tugasItem.targetStudents.length - 3} lainnya
                          </button>
                        )}
                      </div>
                    )}

                    {tugasItem.type === TugasType.TUGAS_NILAI_TAMBAH && (
                      <div className="mb-4 bg-amber-500/20 backdrop-blur-md border border-amber-400/30 p-3 rounded-2xl relative z-10">
                        <p className="text-xs text-amber-200 font-bold leading-relaxed">
                          Tugas Nilai Tambah: 1 soal benar = {tugasItem.questions[0]?.points || 10} poin (jumlah benar semua = {tugasItem.questions.reduce((acc, q) => acc + (q.points || 10), 0)})
                        </p>
                      </div>
                    )}

                    <div className="space-y-1.5 mb-6 text-xs text-white/90 relative z-10 bg-black/10 backdrop-blur-sm p-3 rounded-2xl border border-white/5">
                      {tugasItem.startTime && <div className="flex justify-between items-center"><span className="text-white/60 font-medium">Mulai:</span> <span className="font-bold">{new Date(tugasItem.startTime).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>}
                      {tugasItem.endTime && <div className="flex justify-between items-center"><span className="text-white/60 font-medium">Selesai:</span> <span className="font-bold">{new Date(tugasItem.endTime).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>}
                    </div>
                    
                    <div className="mt-auto pt-4 border-t border-white/15 flex items-center gap-3 relative z-10">
                      <button
                        onClick={() => setViewingResults(tugasItem)}
                        className="flex-1 py-3 bg-white text-slate-900 font-black rounded-2xl hover:bg-slate-100 transition-all text-xs flex items-center justify-center gap-2 shadow-lg shadow-black/10"
                      >
                        <Eye size={16} className="text-indigo-600" />
                        <span>Lihat Hasil ({tugasSubmissions.filter(s => s.tugasId === tugasItem.id).length})</span>
                      </button>
                      <button
                        onClick={() => {
                          handlePrintResults(tugasItem);
                        }}
                        className="p-3 bg-white/20 hover:bg-white/30 text-white font-black rounded-2xl transition-all flex items-center justify-center shadow-md backdrop-blur-md"
                        title="Cetak Laporan Lengkap"
                      >
                        <Printer size={16} />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full p-16 text-center bg-white dark:bg-slate-900 rounded-[2.5rem] border border-dashed border-slate-200 dark:border-slate-800 shadow-sm">
                <ClipboardList size={56} className="mx-auto text-indigo-400 dark:text-indigo-500 mb-4 animate-bounce" />
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2">Belum Ada Tugas</h3>
                <p className="text-sm text-slate-500 font-medium">Anda belum membuat tugas apapun untuk kelas ini.</p>
              </div>
            )
          ) : (
            studentTugas.length > 0 ? (
              studentTugas.map((tugas, index) => {
                const gradients = [
                  'from-indigo-600 via-indigo-700 to-slate-900',
                  'from-blue-600 via-blue-700 to-slate-900',
                  'from-emerald-600 via-emerald-700 to-slate-900',
                  'from-purple-600 via-purple-700 to-slate-900',
                  'from-rose-600 via-rose-700 to-slate-900',
                  'from-amber-600 via-amber-700 to-slate-900'
                ];
                const gradient = gradients[index % gradients.length];
                const submission = tugasSubmissions.find(s => s.tugasId === tugas.id && s.studentId === activeUser.id);
                return (
                  <div key={tugas.id} className={`bg-gradient-to-br ${gradient} p-7 rounded-[2rem] flex flex-col h-full relative overflow-hidden shadow-xl shadow-indigo-500/5 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border border-white/10 group`}>
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-all pointer-events-none"></div>
                    {submission && (
                      <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-bl-2xl shadow-lg flex items-center gap-1.5">
                        <CheckCircle size={12} /> Selesai
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-4 relative z-10">
                      <div className="p-3.5 bg-white/15 backdrop-blur-md rounded-2xl text-white shadow-inner">
                        <ClipboardList size={24} />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mb-3 relative z-10">
                      {tugas.isSummative && (
                        <span className="px-3 py-1 bg-amber-500/30 backdrop-blur-md text-amber-200 text-[10px] font-black uppercase tracking-widest rounded-full border border-amber-400/30">
                          Sumatif
                        </span>
                      )}
                      <span className="px-3 py-1 bg-white/10 backdrop-blur-md text-white/90 text-[10px] font-black uppercase tracking-widest rounded-full border border-white/10">
                        {tugas.type}
                      </span>
                    </div>
                    <h3 className="text-xl font-black text-white mb-2 tracking-tight leading-snug relative z-10">{tugas.title}</h3>
                    <div className="text-xs text-white/80 font-medium mb-4 relative z-10 flex items-center gap-2">
                      <span className="bg-white/15 px-2.5 py-1 rounded-lg font-bold">{tugas.subject}</span>
                      <span>•</span>
                      <span className="bg-white/15 px-2.5 py-1 rounded-lg font-bold">{tugas.questions.length} Soal</span>
                    </div>
                    {tugas.type === TugasType.TUGAS_NILAI_TAMBAH && (
                      <div className="mb-4 bg-amber-500/20 backdrop-blur-md border border-amber-400/30 p-3 rounded-2xl relative z-10">
                        <p className="text-xs text-amber-200 font-bold leading-relaxed">
                          Tugas Nilai Tambah: 1 soal benar = {tugas.questions[0]?.points || 10} poin (jumlah benar semua = {tugas.questions.reduce((acc, q) => acc + (q.points || 10), 0)})
                        </p>
                      </div>
                    )}
                    <div className="space-y-1.5 mb-6 text-xs text-white/90 relative z-10 bg-black/10 backdrop-blur-sm p-3 rounded-2xl border border-white/5">
                      {tugas.startTime && <div className="flex justify-between items-center"><span className="text-white/60 font-medium">Mulai:</span> <span className="font-bold">{new Date(tugas.startTime).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>}
                      {tugas.endTime && <div className="flex justify-between items-center"><span className="text-white/60 font-medium">Selesai:</span> <span className="font-bold">{new Date(tugas.endTime).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>}
                    </div>
                    
                    <div className="mt-auto pt-4 border-t border-white/15 relative z-10">
                      {submission ? (
                        <div className="flex flex-col gap-3">
                          {(() => {
                            let pgEarned = 0;
                            let pgTotal = 0;
                            let essayEarned = 0;
                            let essayTotal = 0;
                            let hasEssay = false;
                            const essayCount = tugas.questions.filter(q => q.type === 'essay').length;
                            const gradedCount = tugas.questions.filter(q => q.type === 'essay' && submission.essayScores?.[q.id] !== undefined).length;
  
                            tugas.questions.forEach(q => {
                              const pts = q.points || 10;
                              if (!q.type || q.type === 'multiple-choice') {
                                pgTotal += pts;
                                if (submission.answers[q.id] === q.correctAnswerIndex) {
                                  pgEarned += pts;
                                }
                              } else if (q.type === 'essay') {
                                hasEssay = true;
                                essayTotal += pts;
                                essayEarned += (submission.essayScores?.[q.id] || 0);
                              }
                            });
  
                            return (
                              <div className="bg-black/20 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex flex-col">
                                    <span className="text-[10px] font-black text-white/60 uppercase tracking-widest">Skor PG</span>
                                    <span className="text-3xl font-black text-white tracking-tight">{pgEarned}</span>
                                  </div>
                                  {hasEssay && (
                                    <div className="flex flex-col items-end">
                                      <span className="text-[10px] font-black text-white/60 uppercase tracking-widest">Skor Essai</span>
                                      <span className="text-3xl font-black text-white tracking-tight">
                                        {gradedCount < essayCount ? (
                                          <span className="text-sm font-bold text-amber-300 bg-amber-500/20 px-2.5 py-1 rounded-xl">Pending</span>
                                        ) : essayEarned}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex justify-between items-center pt-2.5 border-t border-white/10 text-[10px] font-bold text-white/60">
                                  <span>Maks PG: {pgTotal}</span>
                                  {hasEssay && (
                                    <span>Maks Essai: {essayTotal}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleStartTugas(tugas)}
                          disabled={(tugas.startTime && new Date(tugas.startTime) > new Date()) || (tugas.endTime && new Date(tugas.endTime) < new Date())}
                          className={`w-full py-3.5 bg-white text-slate-900 text-xs font-black uppercase tracking-wider rounded-2xl hover:bg-slate-100 transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <Play size={16} className="text-indigo-600 fill-indigo-600" />
                          {tugas.startTime && new Date(tugas.startTime) > new Date() ? 'Belum Dimulai' : (tugas.endTime && new Date(tugas.endTime) < new Date() ? 'Sudah Berakhir' : `Kerjakan Tugas`)}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full p-16 text-center bg-white dark:bg-slate-900 rounded-[2.5rem] border border-dashed border-slate-200 dark:border-slate-800 shadow-sm">
                <ClipboardList size={56} className="mx-auto text-indigo-400 dark:text-indigo-500 mb-4 animate-bounce" />
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2">Belum Ada Tugas</h3>
                <p className="text-sm text-slate-500 font-medium">Tidak ada tugas yang tersedia untuk Anda saat ini.</p>
              </div>
            )
          )}
        </div>
        </div>
      )}
    </div>
    );
  };

  return (
    <>
      {renderContent()}

      {/* Modal Lihat Jawaban Siswa */}
      {(() => {
        const modalTask = viewingResults || (viewingAnswers ? tugas.find(t => t.id === viewingAnswers.tugasId) : null);
        if (!viewingAnswers || !modalTask) return null;

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-3xl sm:rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
              <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0">
                <div>
                  <h3 className="text-lg sm:text-xl font-black text-slate-800 dark:text-white">Jawaban Siswa</h3>
                  <p className="text-xs text-slate-500 font-bold">{students.find(s => s.id === viewingAnswers.studentId)?.name || 'Unknown'}</p>
                </div>
                <button 
                  onClick={() => {
                    setViewingAnswers(null);
                    setExpandedEssayQuestions({});
                  }}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 sm:p-6 overflow-y-auto no-scrollbar flex-1 space-y-4 sm:space-y-6">
                {modalTask.questions
                  .filter(q => q.type === 'essay')
                  .map((q, index) => {
                  const studentAnswer = viewingAnswers.answers[q.id];
                  
                  return (
                    <div key={q.id} className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-3xl border border-slate-100 dark:border-slate-800">
                      <h4 className="font-bold text-slate-800 dark:text-white mb-4">{index + 1}. Jawaban Essai</h4>
                      
                      <div className="space-y-4">
                        <button
                          onClick={() => {
                            setExpandedEssayQuestions(prev => ({
                              ...prev,
                              [q.id]: !prev[q.id]
                            }));
                          }}
                          className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                        >
                          {expandedEssayQuestions[q.id] ? 'Sembunyikan Soal' : 'Lihat Soal'}
                        </button>
                        
                        {expandedEssayQuestions[q.id] && (
                          <div className="bg-indigo-50/50 dark:bg-indigo-900/10 p-4 rounded-2xl border border-indigo-100 dark:border-indigo-800/30">
                            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                              {q.text}
                            </p>
                          </div>
                        )}

                        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-700">
                          <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                            {studentAnswer || <span className="text-slate-400 italic">Tidak dijawab</span>}
                          </p>
                        </div>
                          {viewingAnswers.essayScores?.[q.id] !== undefined && (
                            <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-800">
                              <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                                Skor Essai: {viewingAnswers.essayScores[q.id]} / {q.points || 10}
                              </p>
                            </div>
                          )}
                          {role === UserRole.Guru && onUpdateSubmission && (
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                              <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300">Nilai Essai (Maks: {q.points || 10})</span>
                              <div className="flex items-center gap-2 w-full sm:w-auto">
                                <input
                                  type="number"
                                  min="0"
                                  max={q.points || 10}
                                  value={viewingAnswers.essayScores?.[q.id] || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    const newEssayScores = { ...viewingAnswers.essayScores };
                                    if (!isNaN(val)) {
                                      newEssayScores[q.id] = val;
                                    } else {
                                      delete newEssayScores[q.id];
                                    }
                                    
                                    // Recalculate total score
                                    let earnedPoints = 0;
                                    modalTask.questions.forEach(question => {
                                      if (!question.type || question.type === 'multiple-choice') {
                                        if (viewingAnswers.answers[question.id] === question.correctAnswerIndex) {
                                          earnedPoints += (question.points || 10);
                                        }
                                      } else if (question.type === 'essay') {
                                        earnedPoints += (newEssayScores[question.id] || 0);
                                      }
                                    });
                                    
                                    const newScore = earnedPoints;
                                    
                                    const updatedSubmission = {
                                      ...viewingAnswers,
                                      essayScores: newEssayScores,
                                      score: newScore
                                    };
                                    setViewingAnswers(updatedSubmission);
                                    onUpdateSubmission(updatedSubmission);
                                  }}
                                  className="w-20 p-2 text-center bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-700 rounded-xl font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-4 sm:p-6 border-t border-slate-100 dark:border-slate-800 shrink-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4 sm:gap-6 w-full sm:w-auto">
                  {(() => {
                    let pgEarned = 0;
                    let pgTotal = 0;
                    let essayEarned = 0;
                    let essayTotal = 0;

                    modalTask.questions.forEach(q => {
                      const pts = q.points || 10;
                      if (!q.type || q.type === 'multiple-choice') {
                        pgTotal += pts;
                        if (viewingAnswers.answers[q.id] === q.correctAnswerIndex) {
                          pgEarned += pts;
                        }
                      } else if (q.type === 'essay') {
                        essayTotal += pts;
                        essayEarned += (viewingAnswers.essayScores?.[q.id] || 0);
                      }
                    });

                    let pgScoreDisplay = '-';
                    if (pgTotal > 0) {
                      pgScoreDisplay = pgEarned.toString();
                    }

                    let essayScoreDisplay = '-';
                    if (essayTotal > 0) {
                      essayScoreDisplay = essayEarned.toString() + ' / ' + essayTotal.toString();
                    }

                    return (
                      <>
                        {pgTotal > 0 && (
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-slate-500 uppercase">Skor PG</span>
                            <span className="text-lg font-black text-slate-700 dark:text-slate-300">{pgScoreDisplay}</span>
                          </div>
                        )}
                        {essayTotal > 0 && (
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-slate-500 uppercase">Skor Essai</span>
                            <span className="text-lg font-black text-slate-700 dark:text-slate-300">{essayScoreDisplay}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div className="flex flex-col border-l border-slate-200 dark:border-slate-700 pl-4 sm:pl-6">
                    <span className="text-xs font-bold text-slate-500 uppercase">Nilai Akhir</span>
                    <span className="text-2xl font-black text-slate-700 dark:text-slate-300">
                      {viewingAnswers.score}
                    </span>
                  </div>
                </div>
                {role === UserRole.Guru && onUpdateSubmission && (
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                      onClick={() => {
                        onUpdateSubmission(viewingAnswers);
                        setViewingAnswers(null);
                      }}
                      className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all"
                    >
                      Simpan
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Lihat Semua Siswa */}
      {viewingAllStudents && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black text-slate-800 dark:text-white">Daftar Siswa</h3>
                <p className="text-xs text-slate-500 font-bold">{viewingAllStudents.title}</p>
              </div>
              <button 
                onClick={() => setViewingAllStudents(null)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto no-scrollbar">
              <div className="grid grid-cols-1 gap-2">
                {viewingAllStudents.targetStudents?.map(sid => {
                  const student = students.find(s => s.id === sid);
                  return student ? (
                    <div key={sid} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                      <div>
                        <p className="font-bold text-slate-700 dark:text-slate-300">{student.name}</p>
                        <p className="text-[10px] text-slate-500">{student.class} • {student.nis} {student.nisn ? `| ${student.nisn}` : ''}</p>
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
            <div className="p-6 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800">
              <button 
                onClick={() => setViewingAllStudents(null)}
                className="w-full py-3 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-500/20"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Course Module Viewer Modal */}
      <AnimatePresence>
        {learningModule && (
          <div className="fixed inset-0 z-[120] bg-white dark:bg-slate-950 flex flex-col overflow-hidden">
            {/* Viewer Header */}
            <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0 bg-white dark:bg-slate-900 shadow-sm z-10">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setLearningModule(null)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-500 transition-all"
                >
                  <ArrowLeft size={24} />
                </button>
                <div>
                  <div className="flex items-center gap-3 mb-0.5">
                    <h3 className="text-lg font-black text-slate-800 dark:text-white line-clamp-1">{learningModule.title}</h3>
                    {showQuiz && !quizSubmitted && (
                      <motion.div 
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        key={quizMistakes} // Flash on change
                        className="flex items-center gap-2 px-3 py-1 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-xl border border-amber-100 dark:border-amber-500/20 shadow-sm"
                      >
                        <Star size={14} className="fill-current" />
                        <div className="flex flex-col leading-none">
                          <span className="text-[8px] font-black uppercase tracking-tighter opacity-70">Skor Sementara</span>
                          <span className="text-sm font-black tabular-nums">{Math.max(0, 100 - quizMistakes)}</span>
                        </div>
                      </motion.div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bab {currentChapterIndex + 1} dari {learningModule.chapters.length}</span>
                  </div>
                </div>
              </div>
              <div className="w-1/3 hidden sm:block">
                 <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentChapterIndex + 1) / learningModule.chapters.length) * 100}%` }}
                      className="h-full bg-emerald-500"
                    />
                 </div>
              </div>
              <button 
                onClick={() => setLearningModule(null)}
                className="p-2 text-slate-400 hover:text-rose-500 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Sidebar Navigation */}
              <div className="w-64 border-r border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 hidden lg:flex flex-col p-6 overflow-y-auto no-scrollbar">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Daftar Materi</h4>
                <div className="space-y-2">
                  {learningModule.chapters.map((ch, idx) => {
                    const submission = courseSubmissions.find(s => s.moduleId === learningModule.id && s.studentId === activeUser.id && s.chapterId === ch.id);
                    const prevChapter = idx > 0 ? learningModule.chapters[idx - 1] : null;
                    const prevSubmission = prevChapter ? courseSubmissions.find(s => s.moduleId === learningModule.id && s.studentId === activeUser.id && s.chapterId === prevChapter.id) : null;
                    
                    const isLocked = idx > 0 && prevChapter && (prevChapter.quiz?.length > 0 && !prevSubmission);

                    return (
                      <button
                        key={ch.id}
                        disabled={isLocked}
                        onClick={() => {
                          setCurrentChapterIndex(idx);
                          setShowQuiz(false);
                        }}
                        className={`w-full p-4 rounded-2xl flex flex-col gap-1 transition-all text-left relative ${
                          currentChapterIndex === idx
                            ? 'bg-white dark:bg-slate-800 border border-emerald-500/30 shadow-md shadow-emerald-500/5'
                            : isLocked 
                              ? 'opacity-40 cursor-not-allowed bg-slate-100/50 dark:bg-slate-900/50 border border-transparent'
                              : 'hover:bg-white dark:hover:bg-slate-800 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] font-black uppercase tracking-widest ${currentChapterIndex === idx ? 'text-emerald-500' : 'text-slate-400'}`}>Bab {idx + 1}</span>
                          {submission && <CheckCircle size={12} className="text-emerald-500" />}
                          {isLocked && <XCircle size={12} className="text-slate-400" />}
                        </div>
                        <span className={`text-sm font-bold ${currentChapterIndex === idx ? 'text-slate-900 dark:text-white' : 'text-slate-500'}`}>{ch.title}</span>
                        {submission && (
                          <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400">Skor: {submission.score}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-12 no-scrollbar bg-slate-50/30 dark:bg-slate-950/30">
                <div className="max-w-3xl mx-auto space-y-12 pb-24">
                  {!showQuiz ? (
                    <motion.div
                      key={`content-${currentChapterIndex}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-8"
                    >
                      <div className="space-y-4">
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-800 dark:text-white tracking-tight leading-tight">
                          {learningModule.chapters[currentChapterIndex].title}
                        </h2>
                      </div>
                      
                      {learningModule.chapters[currentChapterIndex].imageUrl && (
                        <div className="w-full rounded-[2rem] overflow-hidden border border-slate-100 dark:border-slate-800 shadow-xl">
                          <img 
                            src={learningModule.chapters[currentChapterIndex].imageUrl} 
                            alt={learningModule.chapters[currentChapterIndex].title}
                            className="w-full h-auto object-cover"
                            referrerPolicy="no-referrer"
                          />
                        </div>
                      )}
                      
                      <div className="prose prose-slate dark:prose-invert max-w-none prose-img:rounded-3xl prose-img:shadow-lg prose-p:text-lg prose-p:leading-relaxed prose-p:text-slate-600 dark:prose-p:text-slate-300">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm, remarkBreaks]} 
                          rehypePlugins={[rehypeRaw, rehypeSanitize]}
                          components={{
                            img: ({ node, ...props }) => {
                              const src = props.src || '';
                              // Parse width from fragment #w300
                              const widthMatch = src.match(/#w(\d+)/);
                              const width = widthMatch ? `${widthMatch[1]}px` : '100%';
                              const isCentered = src.includes('#center');
                              
                              return (
                                <span className={`block my-8 ${isCentered ? 'flex justify-center' : ''}`}>
                                  <img 
                                    {...props} 
                                    style={{ width, maxWidth: '100%', height: 'auto' }} 
                                    className="rounded-3xl shadow-xl"
                                  />
                                </span>
                              );
                            }
                          }}
                        >
                          {learningModule.chapters[currentChapterIndex].content}
                        </ReactMarkdown>
                      </div>

                      <div className="pt-12 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-6">
                         <div className="flex items-center gap-4 w-full sm:w-auto">
                            <button
                              disabled={currentChapterIndex === 0}
                              onClick={() => setCurrentChapterIndex(prev => prev - 1)}
                              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-black uppercase tracking-widest text-xs disabled:opacity-50 transition-all hover:bg-slate-200"
                            >
                              <ChevronLeft size={18} />
                              Sebelumnya
                            </button>
                         </div>

                         {learningModule.chapters[currentChapterIndex].quiz?.length > 0 && (
                            (() => {
                              const existingSub = courseSubmissions.find(s => s.moduleId === learningModule.id && s.studentId === activeUser.id && s.chapterId === learningModule.chapters[currentChapterIndex].id);
                              
                              if (existingSub) {
                                return (
                                  <div className="flex flex-col items-center gap-2">
                                    <div className="px-6 py-3 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-2xl font-black uppercase tracking-widest text-sm flex items-center gap-2">
                                      <CheckCircle size={18} />
                                      Kuis Selesai: {existingSub.score}
                                    </div>
                                    {currentChapterIndex < learningModule.chapters.length - 1 && (
                                      <button
                                        onClick={() => setCurrentChapterIndex(prev => prev + 1)}
                                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-indigo-500/20 hover:scale-105 transition-all"
                                      >
                                        Lanjut Materi Berikutnya
                                        <ChevronRight size={18} />
                                      </button>
                                    )}
                                  </div>
                                );
                              }

                              return (
                                <div className="flex flex-col items-center gap-2">
                                  {cooldownCountdown ? (
                                     <div className="flex flex-col items-center gap-2">
                                       <button
                                         disabled
                                         className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-slate-100 dark:bg-slate-800 text-slate-400 rounded-2xl font-black uppercase tracking-widest text-xs cursor-not-allowed opacity-70"
                                       >
                                         Tunggu {Math.ceil(cooldownCountdown / 60000)} Menit Lagi
                                         <RefreshCw size={14} className="animate-spin" />
                                       </button>
                                       <p className="text-[9px] font-black text-rose-500 uppercase tracking-widest bg-rose-50 dark:bg-rose-500/10 px-3 py-1 rounded-full">Selesaikan waktu tunggu</p>
                                     </div>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setShowQuiz(true);
                                        setQuizSubmitted(false);
                                        setQuizAnswers({});
                                        setQuizMistakes(0);
                                        prepareQuizQuestion(0, learningModule, currentChapterIndex);
                                      }}
                                      className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-emerald-500/20 hover:scale-105 transition-all"
                                    >
                                      Kerjakan Kuis
                                      <Play size={18} />
                                    </button>
                                  )}
                                </div>
                              );
                            })()
                         )}

                         {!learningModule.chapters[currentChapterIndex].quiz?.length && currentChapterIndex < learningModule.chapters.length - 1 && (
                            (() => {
                              const nextChapter = learningModule.chapters[currentChapterIndex + 1];
                              // Actually, if current has no quiz, we can always go to next.
                              // The "next" will be locked anyway if it has a quiz. Wait.
                              // Logic should be: if current has quiz, MUST do quiz. 
                              // If current has NO quiz, can go next.
                              return (
                                <button
                                  onClick={() => setCurrentChapterIndex(prev => prev + 1)}
                                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-indigo-500/20 hover:scale-105 transition-all"
                                >
                                  Lanjut Materi
                                  <ChevronRight size={18} />
                                </button>
                              );
                            })()
                         )}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-white dark:bg-slate-900 p-8 sm:p-12 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl space-y-10"
                    >
                      {!quizSubmitted ? (
                        <>
                          <div className="flex items-center justify-between gap-4">
                            <div className="space-y-1">
                              <h3 className="text-xl font-black text-slate-800 dark:text-white">Pertanyaan {currentQuizQuestionIndex + 1} dari {learningModule.chapters[currentChapterIndex].quiz.length}</h3>
                            </div>
                            <div className="p-3 bg-amber-100 dark:bg-amber-500/20 text-amber-600 rounded-2xl">
                              <ClipboardList size={24} />
                            </div>
                          </div>

                          <div className="space-y-8">
                            <div className="space-y-4">
                              <p className="text-xl font-bold text-slate-800 dark:text-white leading-relaxed">
                                {learningModule.chapters[currentChapterIndex].quiz[currentQuizQuestionIndex].question}
                              </p>
                              
                              <div className="grid grid-cols-1 gap-3">
                                {shuffledOptions.map((opt, oIdx) => {
                                  const question = learningModule.chapters[currentChapterIndex].quiz[currentQuizQuestionIndex];
                                  const isCorrectOption = opt.originalIndex === question.correctAnswerIndex;
                                  const isSelected = quizAnswers[currentQuizQuestionIndex] === opt.originalIndex;
                                  
                                  return (
                                    <button
                                      key={oIdx}
                                      disabled={!!quizFeedback}
                                      onClick={() => handleAnswerQuiz(opt.originalIndex)}
                                      className={`w-full p-5 rounded-2xl text-left border-2 transition-all flex items-center justify-between group ${
                                        quizFeedback
                                          ? isCorrectOption
                                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
                                            : isSelected && quizFeedback.isWrong
                                              ? 'border-rose-500 bg-rose-50 dark:bg-rose-500/10'
                                              : 'border-slate-100 dark:border-slate-800 opacity-50'
                                          : 'border-slate-100 dark:border-slate-800 hover:border-indigo-500 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                      }`}
                                    >
                                      <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm transition-colors ${
                                          quizFeedback
                                            ? isCorrectOption
                                              ? 'bg-emerald-500 text-white'
                                              : isSelected && quizFeedback.isWrong
                                                ? 'bg-rose-500 text-white'
                                                : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                                            : 'bg-slate-100 dark:bg-slate-800 text-slate-400 group-hover:bg-indigo-500 group-hover:text-white'
                                        }`}>
                                          {String.fromCharCode(65 + oIdx)}
                                        </div>
                                        <span className={`font-bold text-lg ${
                                          quizFeedback
                                            ? isCorrectOption
                                              ? 'text-emerald-700 dark:text-emerald-400'
                                              : isSelected && quizFeedback.isWrong
                                                ? 'text-rose-700 dark:text-rose-400'
                                                : 'text-slate-400'
                                            : 'text-slate-600 dark:text-slate-300'
                                        }`}>{opt.text}</span>
                                      </div>
                                      {quizFeedback && isCorrectOption && (
                                        <CheckCircle size={24} className="text-emerald-500" />
                                      )}
                                      {quizFeedback && isSelected && quizFeedback.isWrong && (
                                        <XCircle size={24} className="text-rose-500" />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {quizFeedback && (
                              <motion.div 
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className={`p-6 rounded-[2rem] border-2 flex flex-col sm:flex-row items-center justify-between gap-6 ${
                                  quizFeedback.isWrong 
                                    ? 'bg-rose-50 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/20' 
                                    : 'bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/20'
                                }`}
                              >
                                <div className="flex items-center gap-4">
                                  <div className={`p-3 rounded-[1.25rem] ${quizFeedback.isWrong ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-white'}`}>
                                    {quizFeedback.isWrong ? <RefreshCw size={24} /> : <CheckCircle size={24} />}
                                  </div>
                                  <div>
                                    <p className={`font-black text-sm uppercase tracking-tight ${quizFeedback.isWrong ? 'text-rose-600' : 'text-emerald-600'}`}>
                                      {quizFeedback.isWrong ? 'Jawaban Kurang Tepat' : 'Bagus Sekali!'}
                                    </p>
                                    <p className="text-xs font-bold text-slate-500">
                                      {quizFeedback.isWrong ? 'Pelajari jawaban yang benar di atas dan coba lagi.' : 'Klik tombol di samping untuk melanjutkan.'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 w-full sm:w-auto">
                                  <button
                                    onClick={quizFeedback.isWrong ? () => setQuizFeedback(null) : handleNextQuizQuestion}
                                    className={`flex-1 sm:flex-none px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl transition-all active:scale-95 ${
                                      quizFeedback.isWrong
                                        ? 'bg-rose-600 text-white hover:bg-rose-700 shadow-rose-500/30'
                                        : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-500/30'
                                    }`}
                                  >
                                    {quizFeedback.isWrong ? 'Coba Lagi' : (currentQuizQuestionIndex < learningModule.chapters[currentChapterIndex].quiz.length - 1 ? 'Lanjut' : 'Lihat Hasil')}
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="py-12 text-center space-y-10">
                          <div className="relative inline-flex">
                            <div className="absolute inset-0 bg-emerald-500/20 blur-3xl rounded-full"></div>
                            <div className="relative p-8 bg-emerald-500 text-white rounded-[2.5rem] shadow-2xl shadow-emerald-500/30">
                              < Star size={64} className="animate-bounce" />
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <h3 className="text-4xl font-black text-slate-800 dark:text-white">Kuis Selesai!</h3>
                            <p className="text-slate-500 font-bold uppercase tracking-widest text-sm leading-relaxed max-w-sm mx-auto">
                              Kamu telah menyelesaikan semua pertanyaan di bab ini.
                            </p>
                          </div>

                          <div className="max-w-xs mx-auto p-10 bg-slate-50 dark:bg-slate-800/50 rounded-[3rem] border border-slate-200 dark:border-slate-800 space-y-3 shadow-inner">
                            <p className="text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Skor Akhir</p>
                            <p className="text-7xl font-black text-emerald-500 tracking-tighter tabular-nums">{Math.max(0, 100 - quizMistakes)}</p>
                            <p className="text-xs font-bold text-slate-400 italic font-mono tracking-tight">Total Kesalahan: {quizMistakes}</p>
                          </div>

                          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
                            <button
                              onClick={() => {
                                setShowQuiz(false);
                                setQuizSubmitted(false);
                                if (currentChapterIndex < learningModule.chapters.length - 1) {
                                  setCurrentChapterIndex(prev => prev + 1);
                                }
                              }}
                              className="w-full sm:w-auto px-12 py-5 bg-emerald-600 text-white rounded-[1.5rem] font-black uppercase tracking-widest text-sm shadow-2xl shadow-emerald-500/30 hover:scale-105 active:scale-95 transition-all"
                            >
                              {currentChapterIndex < learningModule.chapters.length - 1 ? 'Lanjut ke Bab Berikutnya' : 'Selesai Belajar'}
                            </button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Transfer Score Modal */}
      <AnimatePresence>
        {transferringScore && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-slate-900 w-full max-w-md p-8 rounded-[2rem] shadow-2xl border border-slate-200 dark:border-slate-800 space-y-6"
            >
              <div className="text-center space-y-2">
                <div className="inline-flex p-3 bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 rounded-2xl mb-2">
                  <Star size={32} />
                </div>
                <h3 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Masukkan ke Nilai?</h3>
                <p className="text-sm text-slate-500 font-bold">
                  Skor <span className="text-emerald-500 font-black">{transferringScore.submission.score}</span> milik <span className="text-slate-800 dark:text-white">{transferringScore.student.name}</span> akan ditambahkan ke buku nilai.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleTransferScore('sumatif')}
                  className="p-6 bg-rose-50 dark:bg-rose-500/10 border-2 border-rose-100 dark:border-rose-500/20 rounded-2xl flex flex-col items-center justify-center gap-3 hover:scale-105 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-rose-500 text-white flex items-center justify-center shadow-lg shadow-rose-500/20 group-hover:rotate-12 transition-transform">
                    <Activity size={24} />
                  </div>
                  <span className="text-xs font-black text-rose-600 dark:text-rose-400 uppercase tracking-widest">Sumatif</span>
                </button>
                <button
                  onClick={() => handleTransferScore('praktikum')}
                  className="p-6 bg-emerald-50 dark:bg-emerald-500/10 border-2 border-emerald-100 dark:border-emerald-500/20 rounded-2xl flex flex-col items-center justify-center gap-3 hover:scale-105 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20 group-hover:-rotate-12 transition-transform">
                    <ClipboardList size={24} />
                  </div>
                  <span className="text-xs font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Praktikum</span>
                </button>
              </div>

              <button
                onClick={() => setTransferringScore(null)}
                className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-400 font-black rounded-2xl uppercase tracking-widest text-xs hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
              >
                Batal
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Course Results Modal */}
      <AnimatePresence>
        {viewingCourseResults && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col"
            >
              <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 rounded-2xl">
                    <Star size={24} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-slate-800 dark:text-white">Hasil Kursus: {viewingCourseResults.title}</h3>
                    <p className="text-sm text-slate-500 font-bold uppercase tracking-wider">Laporan perkembangan belajar</p>
                  </div>
                </div>
                <button 
                  onClick={() => setViewingCourseResults(null)}
                  className="p-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl text-slate-400 transition-all hover:rotate-90"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 no-scrollbar">
                {role === UserRole.Siswa ? (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      <div className="p-6 bg-indigo-50 dark:bg-indigo-500/10 rounded-[2rem] border border-indigo-100 dark:border-indigo-500/20 text-center">
                        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Total Bab</p>
                        <p className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{viewingCourseResults.chapters.length}</p>
                      </div>
                      <div className="p-6 bg-emerald-50 dark:bg-emerald-500/10 rounded-[2rem] border border-emerald-100 dark:border-emerald-500/20 text-center">
                        <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">Kuis Selesai</p>
                        <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">
                          {courseSubmissions.filter(s => s.moduleId === viewingCourseResults.id && s.studentId === activeUser.id).length}
                        </p>
                      </div>
                      <div className="p-6 bg-amber-50 dark:bg-amber-500/10 rounded-[2rem] border border-amber-100 dark:border-amber-500/20 text-center">
                        <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-1">Rata-rata Skor</p>
                        <p className="text-3xl font-black text-amber-600 dark:text-amber-400">
                          {(() => {
                            const subs = courseSubmissions.filter(s => s.moduleId === viewingCourseResults.id && s.studentId === activeUser.id);
                            if (subs.length === 0) return '0';
                            const avg = subs.reduce((acc, curr) => acc + curr.score, 0) / subs.length;
                            return Math.round(avg);
                          })()}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] px-2">Detail Per Bab</h4>
                      <div className="space-y-3">
                        {viewingCourseResults.chapters.map((ch, idx) => {
                          const submission = courseSubmissions.find(s => s.moduleId === viewingCourseResults.id && s.chapterId === ch.id && s.studentId === activeUser.id);
                          const hasQuiz = ch.quiz && ch.quiz.length > 0;
                          
                          return (
                            <div key={ch.id} className="p-5 bg-white dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between group hover:border-indigo-500/30 transition-all">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-black text-slate-400 group-hover:bg-indigo-500 group-hover:text-white transition-all">
                                  {idx + 1}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-slate-800 dark:text-white">{ch.title}</p>
                                  {!hasQuiz && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tidak ada kuis</p>}
                                </div>
                              </div>
                              {hasQuiz ? (
                                submission ? (
                                  <div className="flex items-center gap-6">
                                    <div className="text-right">
                                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Skor</p>
                                      <p className={`text-xl font-black ${submission.score >= 80 ? 'text-emerald-500' : 'text-amber-500'}`}>{submission.score}</p>
                                    </div>
                                    <div className="p-2 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 rounded-lg">
                                      <CheckCircle size={16} />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-400 rounded-lg text-[9px] font-black uppercase tracking-widest">
                                    Belum dikerjakan
                                  </div>
                                )
                              ) : (
                                <div className="w-10 h-10 flex items-center justify-center text-slate-200 dark:text-slate-800">
                                  <Star size={16} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                      <div className="flex-1 flex items-center gap-4 w-full">
                        <div className="relative flex-1">
                          <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                          <input 
                            type="text" 
                            placeholder="Cari nama siswa..." 
                            value={searchStudentResults}
                            onChange={(e) => setSearchStudentResults(e.target.value)}
                            className="w-full pl-12 pr-6 py-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl font-bold text-sm text-slate-800 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                          />
                        </div>
                        <select
                          value={selectedResultClass}
                          onChange={(e) => setSelectedResultClass(e.target.value)}
                          className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl font-bold text-sm text-slate-800 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                        >
                          <option value="all">Semua Kelas</option>
                          {viewingCourseResults.targetClasses.map(c => (
                            <option key={c} value={c}>Kelas {c}</option>
                          ))}
                        </select>
                      </div>
                      <button 
                         onClick={async () => {
                           const workbook = new ExcelJS.Workbook();
                           const worksheet = workbook.addWorksheet('Hasil Kursus');
                           
                           // Headers
                           const headers = ['Nama', 'NISN', 'Kelas'];
                           viewingCourseResults.chapters.forEach((ch, idx) => {
                             if (ch.quiz && ch.quiz.length > 0) {
                               headers.push(`Bab ${idx + 1}: ${ch.title}`);
                             }
                           });
                           headers.push('Rata-rata');
                           
                           worksheet.addRow(headers);
                           
                           // Data
                           students.filter(s => viewingCourseResults.targetClasses.includes(s.class)).forEach(student => {
                             const row: (string | number)[] = [student.name, student.nisn || student.nis || '', student.class];
                             let totalScore = 0;
                             let quizCount = 0;
                             
                             viewingCourseResults.chapters.forEach(ch => {
                               if (ch.quiz && ch.quiz.length > 0) {
                                 const sub = courseSubmissions.find(s => s.moduleId === viewingCourseResults.id && s.chapterId === ch.id && s.studentId === student.id);
                                 row.push(sub ? sub.score : '-');
                                 if (sub) {
                                   totalScore += sub.score;
                                   quizCount++;
                                 }
                               }
                             });
                             
                             row.push(quizCount > 0 ? Math.round(totalScore / quizCount) : 0);
                             worksheet.addRow(row);
                           });
                           
                           // Style headers
                           worksheet.getRow(1).font = { bold: true };
                           worksheet.getRow(1).fill = {
                             type: 'pattern',
                             pattern: 'solid',
                             fgColor: { argb: 'FFE2E8F0' }
                           };
                           
                           const buffer = await workbook.xlsx.writeBuffer();
                           saveAs(new Blob([buffer]), `Hasil_Kursus_${viewingCourseResults.title}.xlsx`);
                         }}
                         className="px-6 py-4 bg-emerald-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-2 shadow-lg shadow-emerald-500/20 hover:scale-105 transition-all"
                      >
                        <Download size={16} />
                        Excel
                      </button>
                    </div>

                    <div className="overflow-x-auto rounded-3xl border border-slate-100 dark:border-slate-800">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50 dark:bg-slate-800/50 uppercase tracking-widest text-[10px] font-black text-slate-400">
                            <th className="px-6 py-4">Nama Siswa</th>
                            <th className="px-6 py-4">Kelas</th>
                            {viewingCourseResults.chapters.map((ch, idx) => (
                              ch.quiz && ch.quiz.length > 0 && (
                                <th key={ch.id} className="px-6 py-4 text-center">B{idx + 1}</th>
                              )
                            ))}
                            <th className="px-6 py-4 text-right">Rata-rata</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                          {students
                            .filter(s => viewingCourseResults.targetClasses.includes(s.class))
                            .filter(s => selectedResultClass === 'all' || s.class === selectedResultClass)
                            .filter(s => s.name.toLowerCase().includes(searchStudentResults.toLowerCase()) || s.nis.includes(searchStudentResults))
                            .map(student => {
                              const studentSubs = courseSubmissions.filter(s => s.moduleId === viewingCourseResults.id && s.studentId === student.id);
                              const totalQuizInModule = viewingCourseResults.chapters.filter(ch => ch.quiz && ch.quiz.length > 0).length;
                              const average = studentSubs.length > 0 ? Math.round(studentSubs.reduce((acc, curr) => acc + curr.score, 0) / studentSubs.length) : 0;
                              
                              return (
                                <tr key={student.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors group">
                                  <td className="px-6 py-4">
                                    <p className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-tight">{student.name}</p>
                                    <p className="text-[10px] font-black text-slate-400">{student.nis}</p>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-[10px] font-black text-slate-600 dark:text-slate-400 rounded-md">
                                      {student.class}
                                    </span>
                                  </td>
                                  {viewingCourseResults.chapters.map(ch => (
                                    ch.quiz && ch.quiz.length > 0 && (
                                      <td key={ch.id} className="px-6 py-4 text-center">
                                        {(() => {
                                          const sub = studentSubs.find(s => s.chapterId === ch.id);
                                          return sub ? (
                                            <div className="flex flex-col items-center gap-1 group/score">
                                              <div 
                                                className="cursor-pointer hover:scale-110 transition-transform"
                                                onClick={() => setTransferringScore({ submission: sub, student, chapterTitle: ch.title })}
                                              >
                                                <span className={`text-sm font-black ${sub.score >= 80 ? 'text-emerald-500' : 'text-amber-500'}`}>
                                                  {sub.score}
                                                </span>
                                              </div>
                                              {role === UserRole.Guru && (
                                                <button 
                                                  onClick={() => {
                                                    setConfirmModal({
                                                      isOpen: true,
                                                      title: 'Hapus Skor',
                                                      message: `Apakah Anda yakin ingin menghapus skor ${student.name} untuk bab ini? Siswa akan bisa mengerjakan ulang kuis.`,
                                                      onConfirm: () => {
                                                        onDeleteSubmission?.(sub.id);
                                                        setConfirmModal(prev => ({ ...prev, isOpen: false }));
                                                      }
                                                    });
                                                  }}
                                                  className="p-1 text-slate-300 hover:text-rose-500 opacity-0 group-hover/score:opacity-100 transition-all"
                                                >
                                                  <Trash2 size={10} />
                                                </button>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-[10px] font-bold text-slate-300">-</span>
                                          );
                                        })()}
                                      </td>
                                    )
                                  ))}
                                  <td className="px-6 py-4 text-right">
                                    <div className="flex flex-col items-end">
                                      <span className={`text-base font-black ${average >= 80 ? 'text-emerald-600' : average > 0 ? 'text-amber-600' : 'text-slate-300'}`}>
                                        {average}
                                      </span>
                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">
                                        {studentSubs.length}/{totalQuizInModule} Kuis
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Course Module Modal */}
      <AnimatePresence>
        {isModuleModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col"
            >
              <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 rounded-2xl">
                    <BookOpen size={24} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-slate-800 dark:text-white">
                      {editingModule ? 'Edit Modul' : 'Tambah Modul Kursus'}
                    </h3>
                    <p className="text-sm text-slate-500 font-bold uppercase tracking-wider">Lengkapi detail modul dan materi</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFullCoursePreview(true)}
                    className="px-4 py-2 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-all border border-indigo-100 dark:border-indigo-500/20"
                  >
                    <Eye size={16} />
                    Pratinjau Kursus
                  </button>
                  <button 
                    onClick={() => setIsModuleModalOpen(false)}
                    className="p-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl text-slate-400 transition-all hover:rotate-90"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-8 space-y-8 no-scrollbar">
                {/* Module Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Judul Modul</label>
                    <input
                      type="text"
                      value={newModule.title}
                      onChange={(e) => setNewModule(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl font-bold text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                      placeholder="Contoh: Dasar-dasar Pemrograman"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Pilih Kelas Target</label>
                    <div className="flex flex-wrap gap-2">
                      {classes.map(cls => (
                        <button
                          key={cls}
                          onClick={() => {
                            const current = newModule.targetClasses || [];
                            if (current.includes(cls)) {
                              setNewModule(prev => ({ ...prev, targetClasses: current.filter(c => c !== cls) }));
                            } else {
                              setNewModule(prev => ({ ...prev, targetClasses: [...current, cls] }));
                            }
                          }}
                          className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                            (newModule.targetClasses || []).includes(cls)
                              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          {cls}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Waktu Tunggu Kuis (Menit)</label>
                    <div className="relative group">
                      <input
                        type="number"
                        min="0"
                        value={newModule.quizCooldownMinutes || 0}
                        onChange={(e) => setNewModule(prev => ({ ...prev, quizCooldownMinutes: parseInt(e.target.value) || 0 }))}
                        className="w-full px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl font-bold text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                        placeholder="Contoh: 30"
                      />
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400 uppercase tracking-widest">Menit</div>
                    </div>
                    <p className="text-[10px] text-slate-400 font-bold px-1 italic">Siswa harus menunggu selama durasi ini sebelum dapat mengerjakan ulang kuis.</p>
                  </div>
                  <div className="md:col-span-1 space-y-2">
                    {/* Empty spacer or additional settings can go here */}
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Deskripsi Modul</label>
                    <textarea
                      value={newModule.description}
                      onChange={(e) => setNewModule(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl font-bold text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none min-h-[100px] resize-none"
                      placeholder="Jelaskan secara singkat apa yang akan dipelajari di modul ini..."
                    />
                  </div>
                </div>

                {/* Chapters Section */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
                    <h4 className="text-lg font-black text-slate-800 dark:text-white flex items-center gap-2">
                      Daftar Bab / Materi
                      <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-[10px] rounded-lg">
                        {newModule.chapters?.length || 0}
                      </span>
                    </h4>
                    <button
                      onClick={addChapter}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-100 transition-colors"
                    >
                      <Plus size={16} />
                      Tambah Bab
                    </button>
                  </div>

                  <div className="space-y-8">
                    {(newModule.chapters || []).map((chapter, cIdx) => (
                      <div key={chapter.id} className="p-6 bg-slate-50 dark:bg-slate-800/30 rounded-3xl border border-slate-100 dark:border-slate-800 relative group/chapter">
                        <div className="absolute -left-3 top-6 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-[10px] font-black">
                          {cIdx + 1}
                        </div>
                        
                        <div className="flex items-start justify-between mb-4 pl-4">
                          <input
                            type="text"
                            value={chapter.title}
                            onChange={(e) => updateChapter(cIdx, { title: e.target.value })}
                            className="text-lg font-black text-slate-800 dark:text-white bg-transparent border-b-2 border-transparent focus:border-indigo-500 outline-none w-full"
                            placeholder={`Judul Bab ${cIdx + 1}`}
                          />
                          <button
                            onClick={() => removeChapter(cIdx)}
                            className="p-2 text-slate-400 hover:text-rose-600 opacity-0 group-hover/chapter:opacity-100 transition-all"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        <div className="pl-4 space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="col-span-full space-y-4">
                              <div className="flex flex-col gap-4">
                                <div className="flex items-center justify-between px-1">
                                  <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Editor Materi & Layout</label>
                                    <p className="text-[9px] text-slate-400 font-medium">Gunakan toolbar untuk format teks dan ukuran gambar</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        const prev = (chapter as any)._showPreview;
                                        updateChapter(cIdx, { _showPreview: !prev } as any);
                                      }}
                                      className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
                                        (chapter as any)._showPreview 
                                          ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' 
                                          : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-200'
                                      }`}
                                    >
                                      <Layout size={14} />
                                      {(chapter as any)._showPreview ? 'Stop Preview' : 'Live Preview'}
                                    </button>
                                  </div>
                                </div>

                                {/* Modern Toolbar */}
                                <div className="flex flex-wrap items-center gap-1 p-2 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm overflow-x-auto no-scrollbar">
                                  {(() => {
                                    const formatText = (prefix: string, suffix: string = '', isBlock: boolean = false) => {
                                      const textarea = document.getElementById(`chapter-editor-${cIdx}`) as HTMLTextAreaElement;
                                      if (!textarea) return;

                                      const start = textarea.selectionStart;
                                      const end = textarea.selectionEnd;
                                      const text = chapter.content;
                                      const selection = text.substring(start, end);
                                      
                                      let newContent;
                                      let newStart, newEnd;

                                      if (isBlock) {
                                        const before = text.substring(0, start);
                                        const after = text.substring(end);
                                        const needsNewLine = before.length > 0 && !before.endsWith('\n');
                                        const blockPrefix = (needsNewLine ? '\n' : '') + prefix;
                                        
                                        newContent = before + blockPrefix + (selection || 'Teks') + suffix + after;
                                        newStart = start + blockPrefix.length;
                                        newEnd = newStart + (selection ? selection.length : 4);
                                      } else {
                                        newContent = text.substring(0, start) + prefix + (selection || 'Teks') + suffix + text.substring(end);
                                        newStart = start + prefix.length;
                                        newEnd = newStart + (selection ? selection.length : 4);
                                      }
                                      
                                      updateChapter(cIdx, { content: newContent });
                                      
                                      setTimeout(() => {
                                        textarea.focus();
                                        textarea.setSelectionRange(newStart, newEnd);
                                      }, 0);
                                    };

                                    const resizeNearestImage = (delta: number) => {
                                      const textarea = document.getElementById(`chapter-editor-${cIdx}`) as HTMLTextAreaElement;
                                      if (!textarea) return;
                                      
                                      const pos = textarea.selectionStart;
                                      const text = chapter.content;
                                      
                                      // Find all image tags with #w and their positions
                                      const regex = /!\[.*?\]\(.*?#w(\d+).*?\)/g;
                                      let match;
                                      let nearestMatch = null;
                                      let minDistance = Infinity;
                                      
                                      while ((match = regex.exec(text)) !== null) {
                                        const start = match.index;
                                        const end = regex.lastIndex;
                                        // Calculate distance to current cursor
                                        const dist = Math.min(Math.abs(pos - start), Math.abs(pos - end));
                                        if (dist < minDistance) {
                                          minDistance = dist;
                                          nearestMatch = {
                                            full: match[0],
                                            size: parseInt(match[1]),
                                            index: start
                                          };
                                        }
                                      }
                                      
                                      if (nearestMatch) {
                                        const newSize = Math.max(50, nearestMatch.size + delta);
                                        const newImgTag = nearestMatch.full.replace(`#w${nearestMatch.size}`, `#w${newSize}`);
                                        const newContent = text.substring(0, nearestMatch.index) + newImgTag + text.substring(nearestMatch.index + nearestMatch.full.length);
                                        updateChapter(cIdx, { content: newContent });
                                      }
                                    };

                                    return (
                                      <>
                                        <div className="flex items-center gap-1 pr-2 border-r border-slate-100 dark:border-slate-800">
                                          <button onClick={() => formatText('# ', '', true)} className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" title="H1"><span className="font-black text-xs">H1</span></button>
                                          <button onClick={() => formatText('## ', '', true)} className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" title="H2"><span className="font-black text-xs">H2</span></button>
                                          <button onClick={() => formatText('### ', '', true)} className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" title="H3"><span className="font-black text-xs">H3</span></button>
                                        </div>
                                        
                                        <div className="flex items-center gap-1 px-2 border-r border-slate-100 dark:border-slate-800">
                                          <button onClick={() => formatText('**', '**')} className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" title="Bold"><Bold size={16} /></button>
                                          <button onClick={() => formatText('*', '*')} className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" title="Italic"><Italic size={16} /></button>
                                        </div>

                                        <div className="flex items-center gap-1 px-2 border-r border-slate-100 dark:border-slate-800">
                                          <button onClick={() => formatText('- ', '', true)} className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" title="List"><List size={16} /></button>
                                          <button onClick={() => formatText('1. ', '', true)} className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" title="Ordered List"><ListOrdered size={16} /></button>
                                        </div>
                                        
                                        <div className="flex items-center gap-1 px-2 border-r border-slate-100 dark:border-slate-800">
                                          <button 
                                            onClick={() => {
                                              const input = document.createElement('input');
                                              input.type = 'file';
                                              input.accept = 'image/*';
                                              input.onchange = async (e) => {
                                                const file = (e.target as HTMLInputElement).files?.[0];
                                                if (file) {
                                                  try {
                                                    setUploadingImageIndex(cIdx);
                                                    const options = { maxSizeMB: 0.3, maxWidthOrHeight: 1920, useWebWorker: true };
                                                    const compressedFile = await imageCompression(file, options);
                                                    if (!supabase) throw new Error('Supabase configuration missing');
                                                    const fileName = `course/${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
                                                    const { error } = await supabase.storage.from('PictureClass').upload(fileName, compressedFile);
                                                    if (error) throw error;
                                                    const { data: { publicUrl } } = supabase.storage.from('PictureClass').getPublicUrl(fileName);
                                                    
                                                    const textarea = document.getElementById(`chapter-editor-${cIdx}`) as HTMLTextAreaElement;
                                                    const start = textarea?.selectionStart || chapter.content.length;
                                                    const before = chapter.content.substring(0, start);
                                                    const after = chapter.content.substring(start);
                                                    const imgMd = `\n![deskripsi](${publicUrl}#w400#center)\n`;
                                                    
                                                    updateChapter(cIdx, { content: before + imgMd + after });
                                                  } catch (err) {
                                                    alert('Gagal mengunggah gambar.');
                                                  } finally {
                                                    setUploadingImageIndex(null);
                                                  }
                                                }
                                              };
                                              input.click();
                                            }} 
                                            className={`p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors flex items-center gap-1.5 ${uploadingImageIndex === cIdx ? 'animate-pulse' : ''}`}
                                            title="Upload Gambar"
                                          >
                                            {uploadingImageIndex === cIdx ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
                                          </button>
                                        </div>

                                        <div className="flex items-center gap-1 px-2">
                                          <button 
                                            onClick={() => resizeNearestImage(50)} 
                                            className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" 
                                            title="Perbesar Gambar Terdekat"
                                          ><Maximize2 size={16} /></button>
                                          <button 
                                            onClick={() => resizeNearestImage(-50)}
                                            className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" 
                                            title="Perkecil Gambar Terdekat"
                                          ><Minimize2 size={16} /></button>
                                          <button 
                                            onClick={() => {
                                              const textarea = document.getElementById(`chapter-editor-${cIdx}`) as HTMLTextAreaElement;
                                              const start = textarea?.selectionStart || 0;
                                              const end = textarea?.selectionEnd || chapter.content.length;
                                              const before = chapter.content.substring(0, start);
                                              const selection = chapter.content.substring(start, end) || 'Teks Besar';
                                              const after = chapter.content.substring(end);
                                              const newContent = before + `\n<span style="font-size: 32px; font-weight: bold; line-height: 1.2; display: block; margin: 20px 0;">${selection}</span>\n` + after;
                                              updateChapter(cIdx, { content: newContent });
                                            }}
                                            className="p-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg text-slate-500 hover:text-indigo-600 transition-colors" 
                                            title="Teks Sangat Besar (Judul)"
                                          ><Type size={20} /></button>
                                          <button 
                                            onClick={() => {
                                              const textarea = document.getElementById(`chapter-editor-${cIdx}`) as HTMLTextAreaElement;
                                              const start = textarea?.selectionStart || 0;
                                              const end = textarea?.selectionEnd || chapter.content.length;
                                              const before = chapter.content.substring(0, start);
                                              const selection = chapter.content.substring(start, end) || 'Teks Sedang';
                                              const after = chapter.content.substring(end);
                                              const newContent = before + `<span style="font-size: 20px">${selection}</span>` + after;
                                              updateChapter(cIdx, { content: newContent });
                                            }}
                                            className="p-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg text-slate-500 hover:text-indigo-600 transition-colors" 
                                            title="Teks Ukuran Sedang"
                                          ><Type size={16} /></button>
                                          <button 
                                            onClick={() => {
                                              const textarea = document.getElementById(`chapter-editor-${cIdx}`) as HTMLTextAreaElement;
                                              if (!textarea) return;
                                              const start = textarea.selectionStart;
                                              const end = textarea.selectionEnd;
                                              const text = chapter.content;
                                              const selection = text.substring(start, end) || 'Teks Warna';
                                              const newContent = text.substring(0, start) + `<span style="color: #4f46e5; font-weight: bold">${selection}</span>` + text.substring(end);
                                              updateChapter(cIdx, { content: newContent });
                                            }}
                                            className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" 
                                            title="Teks Warna Indigo"
                                          ><Sparkles size={16} /></button>
                                          
                                          <div className="w-px h-6 bg-slate-100 dark:bg-slate-800 mx-1"></div>
                                          
                                          <button 
                                            onClick={() => {
                                              const textarea = document.getElementById(`chapter-editor-${cIdx}`) as HTMLTextAreaElement;
                                              if (!textarea) return;
                                              const pos = textarea.selectionStart;
                                              const text = chapter.content;
                                              const regex = /!\[.*?\]\((.*?)#w(\d+).*?\)/g;
                                              let match;
                                              let found = false;
                                              while ((match = regex.exec(text)) !== null) {
                                                const start = match.index;
                                                const end = regex.lastIndex;
                                                if (pos >= start && pos <= end) {
                                                  const fullMatch = match[0];
                                                  const url = match[1];
                                                  const size = match[2];
                                                  const newTag = fullMatch.includes('#center') 
                                                    ? fullMatch.replace('#center', '') 
                                                    : fullMatch.replace(url, url + '#center');
                                                  const newContent = text.substring(0, start) + newTag + text.substring(end);
                                                  updateChapter(cIdx, { content: newContent });
                                                  found = true;
                                                  break;
                                                }
                                              }
                                              if (!found) alert('Letakkan kursor pada baris gambar untuk mengatur perataan.');
                                            }}
                                            className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors" 
                                            title="Tengahkan Gambar (Klik pada baris gambar)"
                                          ><AlignCenter size={16} /></button>
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                  <div className="space-y-2">
                                    <textarea
                                      id={`chapter-editor-${cIdx}`}
                                      value={chapter.content}
                                      onChange={(e) => updateChapter(cIdx, { content: e.target.value })}
                                      className="w-full px-6 py-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl font-medium text-slate-800 dark:text-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none h-[450px] resize-none text-sm leading-relaxed"
                                      placeholder="Tuliskan isi materi bab ini..."
                                    />
                                    <div className="flex items-center gap-4 px-2">
                                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Tips:</p>
                                      <p className="text-[9px] text-slate-500 font-medium italic">Gunakan #w[angka] pada link gambar untuk atur lebar (misal: #w300)</p>
                                    </div>
                                  </div>

                                  <div className={`rounded-3xl border border-slate-100 dark:border-slate-800 overflow-hidden bg-slate-50 dark:bg-slate-900/50 flex flex-col h-[450px] transition-all ${(chapter as any)._showPreview ? 'opacity-100 scale-100' : 'opacity-40 scale-[0.98]'}`}>
                                    <div className="px-4 py-2 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tampilan Siswa</span>
                                      <Eye size={14} className="text-slate-400" />
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-8 prose prose-slate dark:prose-invert max-w-none prose-img:rounded-2xl prose-img:shadow-md h-full whitespace-pre-wrap">
                                      <ReactMarkdown 
                                        remarkPlugins={[remarkGfm, remarkBreaks]} 
                                        rehypePlugins={[rehypeRaw]} // Trust teacher content, allow styles for font size
                                        components={{
                                          img: ({ node, ...props }) => {
                                            const src = props.src || '';
                                            const widthMatch = src.match(/#w(\d+)/);
                                            const width = widthMatch ? `${widthMatch[1]}px` : '100%';
                                            const isCentered = src.includes('#center');
                                            return (
                                              <span className={`block my-4 ${isCentered ? 'flex justify-center' : ''}`}>
                                                <img {...props} style={{ width, maxWidth: '100%', height: 'auto' }} className="rounded-2xl shadow-md border-4 border-white" />
                                              </span>
                                            );
                                          }
                                        }}
                                      >
                                        {chapter.content || '*Pratinjau materi akan muncul di sini...*'}
                                      </ReactMarkdown>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-4">
                              <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Gambar Ilustrasi</label>
                                <ImageDropzone 
                                  currentImage={chapter.imageUrl}
                                  onImageSelect={(base64) => updateChapter(cIdx, { imageUrl: base64 })}
                                />
                                <div className="mt-2">
                                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-1 opacity-60 transition-opacity hover:opacity-100 cursor-pointer block mb-1">Link Gambar Kustom (Opsional)</label>
                                  <input
                                    type="text"
                                    value={chapter.imageUrl || ''}
                                    onChange={(e) => updateChapter(cIdx, { imageUrl: e.target.value })}
                                    className="w-full px-4 py-2 bg-transparent border border-slate-100 dark:border-slate-800 rounded-xl font-medium text-slate-600 dark:text-slate-400 focus:ring-1 focus:ring-indigo-500 transition-all outline-none text-[10px]"
                                    placeholder="Tempel URL gambar di sini jika ada..."
                                  />
                                </div>
                              </div>
                              {chapter.imageUrl && (
                                <div className="relative group/img-preview aspect-video rounded-2xl overflow-hidden border border-slate-100 dark:border-slate-800 bg-slate-100 dark:bg-slate-800">
                                  <img 
                                    src={chapter.imageUrl} 
                                    alt="Preview" 
                                    className="w-full h-full object-cover"
                                    referrerPolicy="no-referrer"
                                  />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img-preview:opacity-100 transition-opacity flex items-center justify-center">
                                    <button 
                                      onClick={() => updateChapter(cIdx, { imageUrl: '' })}
                                      className="p-2 bg-rose-600 text-white rounded-xl shadow-lg hover:bg-rose-700 transition-all active:scale-95"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </div>
                              )}
                              <p className="text-[10px] text-slate-400 font-medium px-1">Klik atau seret gambar. Gambar akan disimpan dalam format data (maks 1MB).</p>
                            </div>
                          </div>

                          {/* Quiz per Bab */}
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h5 className="text-xs font-black text-slate-500 uppercase tracking-widest">Kuis Akhir Bab</h5>
                              <button
                                onClick={() => addQuizQuestion(cIdx)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-colors"
                              >
                                <Plus size={14} />
                                Tambah Soal
                              </button>
                            </div>

                            <div className="space-y-4">
                              {chapter.quiz.map((q, qIdx) => (
                                <div key={qIdx} className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-4 relative group/quiz">
                                  <button
                                    onClick={() => removeQuizQuestion(cIdx, qIdx)}
                                    className="absolute right-2 top-2 p-1.5 text-slate-300 hover:text-rose-500 opacity-0 group-hover/quiz:opacity-100 transition-all"
                                  >
                                    <X size={14} />
                                  </button>

                                  <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pertanyaan {qIdx + 1}</label>
                                    <input
                                      type="text"
                                      value={q.question}
                                      onChange={(e) => updateQuizQuestion(cIdx, qIdx, { question: e.target.value })}
                                      className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 rounded-xl font-bold text-sm outline-none focus:ring-1 focus:ring-emerald-500"
                                      placeholder="Ketik pertanyaan kuis..."
                                    />
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {q.options.map((opt, oIdx) => (
                                      <div key={oIdx} className="flex items-center gap-2">
                                        <input
                                          type="radio"
                                          name={`q-${cIdx}-${qIdx}`}
                                          checked={q.correctAnswerIndex === oIdx}
                                          onChange={() => updateQuizQuestion(cIdx, qIdx, { correctAnswerIndex: oIdx })}
                                          className="text-emerald-600"
                                        />
                                        <input
                                          type="text"
                                          value={opt}
                                          onChange={(e) => {
                                            const newOpts = [...q.options];
                                            newOpts[oIdx] = e.target.value;
                                            updateQuizQuestion(cIdx, qIdx, { options: newOpts });
                                          }}
                                          className={`flex-1 px-3 py-1.5 bg-slate-50 dark:bg-slate-800/50 border rounded-lg text-xs font-medium outline-none transition-all ${
                                            q.correctAnswerIndex === oIdx 
                                              ? 'border-emerald-500/50 ring-1 ring-emerald-500/20 bg-emerald-50/10' 
                                              : 'border-slate-100 dark:border-slate-700'
                                          }`}
                                          placeholder={`Opsi ${String.fromCharCode(65 + oIdx)}`}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-8 border-t border-slate-100 dark:border-slate-800 shrink-0 flex items-center justify-end gap-3 bg-slate-50/50 dark:bg-slate-900/50">
                <button
                  onClick={() => setIsModuleModalOpen(false)}
                  className="px-6 py-2.5 text-slate-500 font-black rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all uppercase tracking-widest text-xs"
                >
                  Batal
                </button>
                <button
                  onClick={handleSaveModule}
                  className="px-8 py-2.5 bg-emerald-600 text-white font-black rounded-2xl shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 hover:scale-105 transition-all uppercase tracking-widest text-xs flex items-center gap-2"
                >
                  <Check size={18} />
                  Simpan Modul
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        isLoading={isDeleting}
      />

      {/* Warning Modal for Sequential Tugas */}
      {showStartWarning && pendingTugas && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-amber-100 dark:bg-amber-500/20 rounded-3xl flex items-center justify-center mx-auto mb-6 text-amber-600 dark:text-amber-400">
                <ClipboardList size={40} />
              </div>
              <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-4">Peringatan Tugas</h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-8">
                Tugas ini diatur dengan mode <strong>Wajib Jawab Berurutan</strong>. 
                Anda tidak dapat melewati soal tanpa menjawab, dan tidak dapat kembali ke soal sebelumnya. 
                Pastikan Anda siap sebelum memulai.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => startTugasExecution(pendingTugas)}
                  className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 transition-all active:scale-95"
                >
                  Saya Mengerti, Mulai Sekarang
                </button>
                <button
                  onClick={() => {
                    setShowStartWarning(false);
                    setPendingTugas(null);
                  }}
                  className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-2xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                >
                  Batal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Full Course Preview Modal for Teacher */}
      <AnimatePresence>
        {showFullCoursePreview && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/40 backdrop-blur-md p-4 md:p-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-slate-900 w-full max-w-6xl h-full max-h-[90vh] rounded-[3rem] shadow-2xl border border-white/20 overflow-hidden flex flex-col"
            >
              <div className="p-6 md:p-10 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-900/50 backdrop-blur-md">
                <div className="flex items-center gap-4 md:gap-6">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-lg shadow-indigo-200 dark:shadow-none">
                    <BookOpen size={24} className="text-white md:hidden" />
                    <BookOpen size={32} className="text-white hidden md:block" />
                  </div>
                  <div>
                    <h2 className="text-xl md:text-3xl font-black text-slate-800 dark:text-white leading-tight">
                      Pratinjau Kursus
                    </h2>
                    <p className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-[0.2em] mt-1">
                      {newModule.title || 'Judul Belum Diisi'} • Mode Pratinjau Guru
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowFullCoursePreview(false);
                    setPreviewChapterIndex(0);
                  }}
                  className="p-3 md:p-4 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-3xl hover:bg-rose-50 hover:text-rose-600 transition-all active:scale-90"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-slate-50 dark:bg-slate-950/20">
                {/* Sidebar Navigation */}
                <div className="w-full md:w-80 bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 overflow-y-auto p-6 space-y-4 no-scrollbar border-b md:border-b-0 shrink-0">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2 mb-4">Daftar Bab</h3>
                  <div className="space-y-2">
                    {newModule.chapters.map((chapter, idx) => (
                      <button
                        key={idx}
                        onClick={() => setPreviewChapterIndex(idx)}
                        className={`w-full p-4 rounded-2xl flex items-start gap-4 transition-all group ${
                          previewChapterIndex === idx 
                            ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-200' 
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black shrink-0 ${
                          previewChapterIndex === idx ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-800'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="text-left">
                          <div className="text-xs font-black leading-tight mb-1">{chapter.title || `Bab ${idx + 1}`}</div>
                          <div className={`text-[9px] font-medium opacity-60 line-clamp-1`}>
                            {chapter.content.substring(0, 40)}...
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6 md:p-12 no-scrollbar bg-white dark:bg-slate-900/50">
                  {newModule.chapters[previewChapterIndex] ? (
                    <motion.div
                      key={previewChapterIndex}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="max-w-4xl mx-auto space-y-10 pb-20"
                    >
                      <div className="space-y-4">
                        <div className="px-4 py-2 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-xl text-[10px] font-black uppercase tracking-widest inline-block">
                          Bab {previewChapterIndex + 1}
                        </div>
                        <h1 className="text-3xl md:text-5xl font-black text-slate-800 dark:text-white leading-[1.15]">
                          {newModule.chapters[previewChapterIndex].title}
                        </h1>
                      </div>

                      {newModule.chapters[previewChapterIndex].imageUrl && (
                        <div className="relative aspect-video rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white dark:border-slate-800">
                          <img 
                            src={newModule.chapters[previewChapterIndex].imageUrl} 
                            className="w-full h-full object-cover" 
                            alt={newModule.chapters[previewChapterIndex].title}
                            referrerPolicy="no-referrer"
                          />
                        </div>
                      )}
                      
                      <div className="prose prose-slate dark:prose-invert max-w-none prose-img:rounded-3xl prose-img:shadow-lg prose-p:text-lg prose-p:leading-relaxed prose-p:text-slate-600 dark:prose-p:text-slate-300">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm, remarkBreaks]} 
                          rehypePlugins={[rehypeRaw, rehypeSanitize]}
                          components={{
                            img: ({ node, ...props }) => {
                              const src = props.src || '';
                              const widthMatch = src.match(/#w(\d+)/);
                              const width = widthMatch ? `${widthMatch[1]}px` : '100%';
                              const isCentered = src.includes('#center');
                              return (
                                <span className={`block my-8 ${isCentered ? 'flex justify-center' : ''}`}>
                                  <img 
                                    {...props} 
                                    style={{ width, maxWidth: '100%', height: 'auto' }} 
                                    className="rounded-3xl shadow-xl border-4 border-white dark:border-slate-800 bg-white"
                                  />
                                </span>
                              );
                            }
                          }}
                        >
                          {newModule.chapters[previewChapterIndex].content}
                        </ReactMarkdown>
                      </div>

                      {/* Navigation Controls */}
                      <div className="mt-20 pt-10 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                        <button
                          disabled={previewChapterIndex === 0}
                          onClick={() => setPreviewChapterIndex(prev => prev - 1)}
                          className="flex items-center gap-3 px-6 py-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl text-slate-600 dark:text-slate-400 font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all disabled:opacity-30"
                        >
                          <ChevronLeft size={20} />
                          Bab Sebelumnya
                        </button>
                        <button
                          onClick={() => {
                            if (previewChapterIndex < newModule.chapters.length - 1) {
                              setPreviewChapterIndex(prev => prev + 1);
                            } else {
                              setShowFullCoursePreview(false);
                            }
                          }}
                          className="flex items-center gap-3 px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-200 hover:bg-indigo-700 hover:scale-105 transition-all"
                        >
                          {previewChapterIndex < newModule.chapters.length - 1 ? 'Bab Berikutnya' : 'Selesai Pratinjau'}
                          <ChevronRight size={20} />
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="h-full flex items-center justify-center p-12 text-center">
                      <div className="space-y-4 max-w-sm">
                        <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-[2rem] flex items-center justify-center mx-auto text-slate-300">
                          <BookOpen size={40} />
                        </div>
                        <h4 className="text-xl font-black text-slate-800 dark:text-white">Pilih Bab</h4>
                        <p className="text-slate-400 font-medium">Klik bab di sebelah kiri untuk melihat isi materi.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Student Grades Popup */}
      <AnimatePresence>
        {viewingStudentGrades && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewingStudentGrades(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl shadow-xl overflow-hidden"
            >
              <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-800/20">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="w-10 h-10 shrink-0 bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center text-lg font-black">
                    {viewingStudentGrades.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-black text-slate-800 dark:text-white truncate">
                      {viewingStudentGrades.name}
                    </h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate mt-0.5">
                      Kelas {viewingStudentGrades.class} • {viewingStudentGrades.nis}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setViewingStudentGrades(null)}
                  className="p-1.5 shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-3 sm:p-4 max-h-[50vh] overflow-y-auto">
                {studentGradesSummary ? (
                  <div className="space-y-1">
                    <div className="grid grid-cols-12 gap-2 px-2 py-1 mb-1 text-[8px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 dark:border-slate-800">
                      <div className="col-span-4">Kode TP</div>
                      <div className="col-span-4 text-center">Sumatif</div>
                      <div className="col-span-4 text-center">Praktik</div>
                    </div>
                    {(studentGradesSummary as any[]).length > 0 ? (
                      (studentGradesSummary as any[]).map((tp) => (
                        <div key={tp.tpId} className="grid grid-cols-12 gap-2 p-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors">
                          <div className="col-span-4 flex items-center">
                            <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase">
                              {tp.tpCode}
                            </span>
                          </div>
                          <div className="col-span-4 flex items-center justify-center">
                            <span className={`text-xs font-black ${tp.sumatif !== undefined ? 'text-slate-800 dark:text-white' : 'text-slate-300 dark:text-slate-700'}`}>
                              {tp.sumatif ?? '-'}
                            </span>
                          </div>
                          <div className="col-span-4 flex items-center justify-center">
                            <span className={`text-xs font-black ${tp.praktikum !== undefined ? 'text-slate-800 dark:text-teal-400' : 'text-slate-300 dark:text-slate-700'}`}>
                              {tp.praktikum ?? '-'}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-8 flex flex-col items-center justify-center text-center opacity-40">
                        <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Belum Ada Data</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-8 flex flex-col items-center justify-center text-center">
                    <Loader2 className="animate-spin text-indigo-500 mb-2" size={24} />
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Memuat Data...</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Tugas Result Modal */}
      {tugasResult && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2rem] shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400">
                <CheckCircle size={32} />
              </div>
              
              <h3 className="text-xl font-black text-slate-800 dark:text-white mb-1">Tugas Selesai!</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">{tugasResult.tugasTitle}</p>
              
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 mb-6 space-y-6">
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Skor PG Anda</div>
                  <div className="text-5xl font-black text-indigo-600 dark:text-indigo-400">
                    {tugasResult.score}
                  </div>
                  <div className="text-xs font-bold text-slate-400 mt-1">
                    Dari {tugasResult.total} Poin
                  </div>
                </div>

                {tugasResult.hasEssay && (
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Skor Essai</div>
                    <div className="text-3xl font-black text-amber-500">
                      Pending
                    </div>
                    <div className="text-xs font-bold text-slate-400 mt-1">
                      Total Poin Essai: {tugasResult.essayTotal}
                    </div>
                    <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800">
                      <p className="text-[10px] font-bold text-amber-700 dark:text-amber-300 leading-relaxed">
                        Soal essai masih dalam proses penilaian. Skor akhir Anda akan diperbarui setelah dinilai.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => setTugasResult(null)}
                className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 transition-all active:scale-95 text-sm"
              >
                Tutup & Kembali
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TugasView;
